import { getSpriteGrid, dampedSpringRecoil, clearSpriteBitmapCache, clearAuraBitmapCache, clearStatueBitmapCache } from './render/unitSprites.js';
import { unitMixin } from './render/units.js';
import { backgroundMixin } from './render/background.js';
import { sulBackgroundMixin } from './render/backgroundSul.js';
import { swampBackgroundMixin } from './render/backgroundSwamp.js';
import { voidglassBackgroundMixin, releaseVoidglassCaches } from './render/backgroundVoidglass.js';
import { stratosBackgroundMixin, releaseStratosCaches } from './render/backgroundStratos.js';
import { postProcessMixin } from './render/postProcessEffects.js';
import { buildingsMixin } from './render/buildings.js';
import { weatherMixin } from './render/weather.js';
import { modulesMixin, clearModuleBitmapCache } from './render/modules.js';
import { MODULE_W, MODULE_H, MODULE_SKEW, MODULE_TOP_D, MODULE_ANCHOR_DX, FOUNDATION_ROOF_DY } from './modules.js';


function compareUnitDepth(a, b) {
  if (a.isAir !== b.isAir) return a.isAir ? 1 : -1;
  return a.y - b.y;
}

const _casingSparkPool = [];
const _casingSmokePool = [];

let _metaFogStripCanvas = null;
function getMetaFogStripCanvas() {
  if (!_metaFogStripCanvas && typeof document !== 'undefined') {
    const canvas = document.createElement('canvas');
    canvas.width = 4;
    canvas.height = 128;
    const gctx = canvas.getContext('2d');
    const grad = gctx.createLinearGradient(0, 0, 0, 128);
    grad.addColorStop(0, 'rgba(59, 7, 100, 0)');
    grad.addColorStop(0.2, 'rgba(88, 28, 135, 0.75)');
    grad.addColorStop(0.5, 'rgba(192, 38, 211, 0.55)');
    grad.addColorStop(0.8, 'rgba(59, 7, 100, 0.85)');
    grad.addColorStop(1, 'rgba(15, 23, 42, 0)');
    gctx.fillStyle = grad;
    gctx.fillRect(0, 0, 4, 128);
    _metaFogStripCanvas = canvas;
  }
  return _metaFogStripCanvas;
}

const TERRITORY_WEST_PHRASES = Object.freeze([
  'DOMINANCE',
  'FRONTLINE PUSH',
  'SECTOR OVERDRIVE',
  'ADVANCING FORCE',
  'PRESSURE SURGE',
  'OFFENSIVE RUSH',
  'HIGH GROUND'
]);

const TERRITORY_EAST_PHRASES = Object.freeze([
  'ENEMY PUSH',
  'SECTOR BREACH',
  'DEFENSIVE LINE',
  'ENEMY INVASION',
  'DEFEND THE LINE',
  'RED ALERT',
  'HOSTILE ADVANCE'
]);

const TERRITORY_NEUTRAL_PHRASES = Object.freeze([
  'CONTESTED MIDLINE',
  '50 / 50 STALEMATE',
  'FRONTLINE ENGAGED',
  'BATTLE FOR LINE',
  'BALANCED FORCES'
]);

function drawFilledTriangleRight(ctx, tx, ty, size = 6.5) {
  ctx.beginPath();
  ctx.moveTo(tx - size * 0.4, ty - size * 0.45);
  ctx.lineTo(tx + size * 0.55, ty);
  ctx.lineTo(tx - size * 0.4, ty + size * 0.45);
  ctx.closePath();
  ctx.fill();
}

function drawFilledTriangleLeft(ctx, tx, ty, size = 6.5) {
  ctx.beginPath();
  ctx.moveTo(tx + size * 0.4, ty - size * 0.45);
  ctx.lineTo(tx - size * 0.55, ty);
  ctx.lineTo(tx + size * 0.4, ty + size * 0.45);
  ctx.closePath();
  ctx.fill();
}

const _phraseWidthCache = new Map();
function getTerritoryPhraseWidth(ctx, phrase) {
  let w = _phraseWidthCache.get(phrase);
  if (w === undefined) {
    w = ctx.measureText(phrase).width;
    _phraseWidthCache.set(phrase, w);
  }
  return w;
}

let _metaBillowCanvas = null;
function getMetaBillowCanvas() {
  if (!_metaBillowCanvas && typeof document !== 'undefined') {
    const canvas = document.createElement('canvas');
    const size = 128;
    const refR = 64;
    canvas.width = size;
    canvas.height = size;
    const gctx = canvas.getContext('2d');
    const grad = gctx.createRadialGradient(refR, refR, 0, refR, refR, refR);
    grad.addColorStop(0, 'rgba(192, 38, 211, 0.4)');
    grad.addColorStop(0.5, 'rgba(132, 204, 22, 0.25)');
    grad.addColorStop(1, 'rgba(59, 7, 100, 0)');
    gctx.fillStyle = grad;
    gctx.fillRect(0, 0, size, size);
    _metaBillowCanvas = canvas;
  }
  return _metaBillowCanvas;
}

let _precisionStrikeSunDimCanvas = null;
function getPrecisionStrikeSunDimCanvas() {
  if (_precisionStrikeSunDimCanvas) return _precisionStrikeSunDimCanvas;
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = 520;
  c.height = 520;
  const hctx = c.getContext('2d');
  const g = hctx.createRadialGradient(260, 260, 40, 260, 260, 260);
  g.addColorStop(0, 'rgba(12, 6, 22, 0.36)');
  g.addColorStop(0.5, 'rgba(10, 5, 20, 0.18)');
  g.addColorStop(1, 'rgba(0, 0, 0, 0)');
  hctx.fillStyle = g;
  hctx.beginPath();
  hctx.arc(260, 260, 260, 0, Math.PI * 2);
  hctx.fill();
  _precisionStrikeSunDimCanvas = c;
  return _precisionStrikeSunDimCanvas;
}

let _precisionStrikeSporeBombCanvas = null;
function getPrecisionStrikeSporeBombCanvas() {
  if (_precisionStrikeSporeBombCanvas) return _precisionStrikeSporeBombCanvas;
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = 32;
  c.height = 32;
  const hctx = c.getContext('2d');
  const g = hctx.createRadialGradient(13, 13, 2.5, 16, 16, 16);
  g.addColorStop(0, '#ffffff');
  g.addColorStop(0.35, '#bef264');
  g.addColorStop(0.75, '#84cc16');
  g.addColorStop(1, '#4d7c0f');
  hctx.fillStyle = g;
  hctx.beginPath();
  hctx.arc(16, 16, 16, 0, Math.PI * 2);
  hctx.fill();
  _precisionStrikeSporeBombCanvas = c;
  return _precisionStrikeSporeBombCanvas;
}

export class Renderer {
  // worldW/worldH are the FIXED simulation dimensions (Battlefield's own
  // 1600x540 - see js/engine/battlefield.js) and are never resized: every
  // draw call in this file below works in that same coordinate space,
  // regardless of the device's actual screen size. The canvas element's
  // backing-store size (canvas.width/height, device pixels) is instead
  // owned entirely by js/ui/viewport.js, which maps the fixed world onto
  // whatever screen this happens to render on - see setViewport().
  constructor(canvas, worldW = 1600, worldH = 540) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.width = worldW;
    this.height = worldH;
    this.viewport = null;
    // Auto-tuned by js/engine/performance.js (or manually via the setup
    // sheet's quality selector) - 'high' | 'medium' | 'low'. Gates the
    // purely-cosmetic passes below (bloom, film grain/scanlines, weather,
    // per-draw shadowBlur glows); nothing gameplay-relevant ever reads
    // this, so two peers running different tiers in an online match still
    // simulate identically - see MOBILE_PLAN.md section 9.
    this.quality = 'high';
    this.isMobile = false;

    // World-Space Static Layer Caching:
    // Heavy static background and foreground geometry are baked ONCE into
    // dedicated world-space offscreen canvases (_staticBgCanvas, _staticFgCanvas).
    // The main render loop simply blits them via ctx.drawImage with near-zero CPU cost.
    this._staticBgCanvas = document.createElement('canvas');
    this._staticBgCtx = this._staticBgCanvas.getContext('2d');
    this._staticFgCanvas = document.createElement('canvas');
    this._staticFgCtx = this._staticFgCanvas.getContext('2d');
    this._staticBgPrimed = false;

    // Static Base & Bunker Foundation Caching:
    // The complex planetary fortress bunker platform is baked ONCE into a dedicated
    // world-space offscreen canvas (_staticBaseCanvas). Render loop blits it via drawImage.
    this._staticBaseCanvas = document.createElement('canvas');
    this._staticBaseCtx = this._staticBaseCanvas.getContext('2d');
    this._staticBasePrimed = false;
    this._staticBaseKey = null;

    // Shared per-frame lightmap (buildLightmap()) - every reactive point
    // light (explosions, muzzle flashes) composited once into a single
    // world-space canvas, instead of every consumer (ground illumination,
    // each unit's own floor-glow) re-deriving the same gradients from
    // particles.lights independently.
    this._lightmapCanvas = document.createElement('canvas');
    this._lightmapCanvas.width = this.width;
    this._lightmapCanvas.height = this.height;
    this._lightmapCtx = this._lightmapCanvas.getContext('2d');
    this._sortedUnits = [];
    this._allyAntiShades = [];

    this.targetingMode = false;
    this.targetX = 0;
    this.targetY = 0;

    // Ambient Rare Wildlife in Foreground
    this.foregroundCritters = [];
    this.critterTimer = 16;

    // Stage Timings (v3 §3 P0)
    this.lastWorldMs = 0;
    this.lastPostMs = 0;

    // Context restoration recovery: re-prime static world canvases & wipe unit sprite caches
    if (this.canvas && typeof this.canvas.addEventListener === 'function') {
      this.canvas.addEventListener('contextrestored', () => {
        this.releaseStaticCaches();
      });
    }
  }

  getSpriteGrid(unitType, race) {
    return getSpriteGrid(unitType, race);
  }

  setViewport(viewport) {
    this.viewport = viewport;
  }

  // Memoized CanvasGradient objects for the Human bunker platform (see
  // _drawBunkerPlatform() - docs/260906_performanceimplementation_plan.md
  // Pillar 2): most of that geometry's gradients depend only on `team`
  // (fixed for the whole match) with no time-varying stop colors, so the
  // gradient object itself - not just the color values - is safe to build
  // once and reuse every frame instead of calling createLinearGradient()/
  // createRadialGradient() again each time. `this.ctx` never changes after
  // construction (see the constructor's note above), so a gradient created
  // against it stays valid for the renderer's lifetime.
  _cachedGradient(key, createFn) {
    if (!this._bunkerGradCache) this._bunkerGradCache = new Map();
    let grad = this._bunkerGradCache.get(key);
    if (!grad) {
      grad = createFn();
      this._bunkerGradCache.set(key, grad);
    }
    return grad;
  }

  setTargeting(active, x = 0, y = 0, color = null) {
    this.targetingMode = active;
    this.targetX = x;
    this.targetY = y;
    if (active) {
      if (color !== null) {
        this.reticleColor = color;
      } else if (!this.reticleColor) {
        this.reticleColor = '#ff0033';
      }
    } else {
      this.reticleColor = null;
    }
  }

  // Applies the world -> device-pixel camera transform (viewport fit, screen
  // shake, punch-zoom) to the main 2D context.
  _applyCameraTransform(c, particles, nowSec) {
    if (this.viewport) {
      this.viewport.applyTransform(c);
    }
    if (particles.screenShake && (particles.screenShake.x !== 0 || particles.screenShake.y !== 0)) {
      c.translate(particles.screenShake.x, particles.screenShake.y);
      // Kinetic camera lens jitter: subtle physical rotational oscillation during heavy impacts
      if (particles.screenShake.intensity >= 1.5) {
        const cx = this.viewport ? this.viewport.camera.x : this.width / 2;
        const cy = this.viewport ? this.viewport.camera.y : this.height / 2;
        const maxAngle = Math.min(0.006, particles.screenShake.intensity * 0.00045);
        const jitterAngle = Math.sin(nowSec * 54.0) * maxAngle;
        c.translate(cx, cy);
        c.rotate(jitterAngle);
        c.translate(-cx, -cy);
      }
    }
    if (this._punchStartTime !== undefined) {
      const punchDuration = 0.5;
      const elapsed = nowSec - this._punchStartTime;
      if (elapsed < punchDuration) {
        const ease = Math.sin(Math.min(1, elapsed / punchDuration) * Math.PI);
        const punch = 1 + ease * 0.05;
        // Around the current camera center, not the world's absolute
        // center - matters once portrait mode can pan away from it.
        const cx = this.viewport ? this.viewport.camera.x : this.width / 2;
        const cy = this.viewport ? this.viewport.camera.y : this.height / 2;
        c.translate(cx, cy);
        c.scale(punch, punch);
        c.translate(-cx, -cy);
      }
    }
    // Bio-Plasma Zoom-Sog (docs/BIO_PLASMA_PLAN.md §7.3):
    // Inward zoom pull (~0.96 scale at peak) accumulated via dt on particles.zoomPull
    const zp = particles?.zoomPull;
    if (zp && zp.elapsed < zp.duration) {
      const t = zp.elapsed / zp.duration;
      const ease = Math.sin(t * Math.PI); // Ease-in/Ease-out
      const factor = 1 - ease * 0.04;    // ~0.96 at peak
      const cx = this.viewport ? this.viewport.camera.x : this.width / 2;
      const cy = this.viewport ? this.viewport.camera.y : this.height / 2;
      c.translate(cx, cy);
      c.scale(factor, factor);
      c.translate(-cx, -cy);
    }
  }

  render(battlefield, particles) {
    const ctx = this.ctx;
    // Device-pixel clear (NOT this.width/height, which are fixed world
    // dims - the actual backing store is whatever js/ui/viewport.js sized
    // it to for this screen).
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Ambient escalation (critical-base smoke/sparks) is time-throttled state,
    // not a draw call - update it once per frame before drawing anything.
    this.updateCriticalBaseAmbience(battlefield, particles);

    this.particles = particles;
    this.buildLightmap(particles);

    const t0World = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
    ctx.save();
    const nowSec = performance.now() / 1000;
    if (particles.screenShake && particles.screenShake.intensity >= 12) {
      if (!this._punchArmed) {
        this._punchArmed = true;
        this._punchStartTime = nowSec;
      }
    } else {
      this._punchArmed = false;
    }
    this._applyCameraTransform(ctx, particles, nowSec);

    // 1. Render Sci-Fi Metallic Background & Lane (Skipped if ?nobg=1)
    if (!this.game?.debugFlags?.noBg) {
      this.renderBackgroundCached(ctx, battlefield, particles, nowSec);
    }

    // 🌫️ Atmospheric Depth Veil – smog/air-mass that pushes the background
    // visually further away. Denser toward the sky, thinner at lane level so
    // foreground elements (units, bases) on the ground stay readable.
    {
      const laneY = battlefield.laneY;
      if (!this._veilGradient || this._veilLaneY !== laneY) {
        this._veilLaneY = laneY;
        this._veilGradient = ctx.createLinearGradient(0, 0, 0, laneY);
        this._veilGradient.addColorStop(0,   'rgba(3, 5, 12, 0.48)');  // deep smog at top
        this._veilGradient.addColorStop(0.55,'rgba(5, 8, 16, 0.28)');  // mid haze
        this._veilGradient.addColorStop(0.82,'rgba(4, 7, 14, 0.14)');  // thin near horizon
        this._veilGradient.addColorStop(1,   'rgba(0, 0, 0, 0.04)');   // barely-there at lane
      }
      // Cover the full visible area above the lane, not just world y >= 0:
      // the 'contain' camera fit leaves a letterbox margin above the world
      // on wide screens (filled by the padded static background), which
      // otherwise stayed unveiled and showed up as a brighter band with a
      // hard edge at world y = 0. The gradient's end colours extend
      // automatically beyond its 0..laneY range.
      const veil = this.getVisibleWorldRect();
      ctx.save();
      ctx.fillStyle = this._veilGradient;
      ctx.fillRect(veil.left, veil.top, veil.right - veil.left, laneY + 20 - veil.top);
      ctx.restore();
    }

    // 1A-2. Shared lightmap laid over the ground (skipped on low quality)
    // Skipped while the lightmap holds nothing (see buildLightmap()): an
    // additive full-lane blit of a transparent canvas changes no pixel.
    if (this.quality !== 'low' && this._lightmapHasContent) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = 0.5;
      ctx.drawImage(this._lightmapCanvas, 0, 0);
      ctx.restore();
    }

    // 1B. Ground scorch marks from past explosions
    particles.renderDecals(ctx);

    const westIsSul = battlefield.bases.western?.race === 'Sul';
    const eastIsSul = battlefield.bases.eastern?.race === 'Sul';
    const westGlow = westIsSul ? '#d946ef' : '#38bdf8';
    const eastGlow = eastIsSul ? '#d946ef' : '#ff4466';
    const westMain = westIsSul ? '#a855f7' : '#00f0ff';
    const eastMain = eastIsSul ? '#a855f7' : '#ff003c';

    // 1C. Building connector cables
    this.renderBuildingCables(ctx, battlefield, battlefield.bases.western, westGlow);
    this.renderBuildingCables(ctx, battlefield, battlefield.bases.eastern, eastGlow);

    // 1D. Background Weather Depth Layer (far rain streaks & volumetric mist sheets behind units)
    if (this.quality !== 'low') {
      if (battlefield?.mapId === 'sul') {
        this.renderSulWeatherBackground(ctx, battlefield);
      } else if (battlefield?.mapId === 'swamp') {
        this.renderSwampWeatherBackground(ctx, battlefield);
      } else if (battlefield?.mapId === 'voidglass') {
        this.renderVoidglassWeatherBackground(ctx, battlefield);
      } else if (battlefield?.mapId === 'stratos') {
        this.renderStratosWeatherBackground(ctx, battlefield);
      } else {
        this.renderWeatherBackground(ctx, battlefield);
      }
    }

    // 2. Sort Units by Y-depth for correct isometric perspective & base occlusion
    const bounds = this.viewport ? this.viewport.getVisibleBounds() : null;
    const CULL_MARGIN_X = 140; // Safe margin exceeding widest unit sprite (120px) + recoil + HP bar
    const localPlayerTeam = (battlefield && battlefield.playerTeam) || 'western';
    if (!this._allyAntiShades) this._allyAntiShades = [];
    this._allyAntiShades.length = 0;
    const units = battlefield.units;
    this._sortedUnits.length = units.length;
    for (let i = 0; i < units.length; i++) {
      const u = units[i];
      this._sortedUnits[i] = u;
      if (u.team === localPlayerTeam && !u.isDead && (u.isAntiShade || u.cardData?.unitType === 'AntiShade' || u.name === 'Anti Shade')) {
        this._allyAntiShades.push(u);
      }
    }
    this._sortedUnits.sort(compareUnitDepth);

    const westBaseAnchorY = battlefield.bases.western.y + 28;
    const eastBaseAnchorY = battlefield.bases.eastern.y + 28;

    // 2A. Render ground units standing BEHIND bases (Y < baseAnchorY)
    let unitIdx = 0;
    while (unitIdx < this._sortedUnits.length) {
      const u = this._sortedUnits[unitIdx];
      if (u.isAir) break; // Air units render above ground and bases
      const baseAnchor = u.team === 'western' ? westBaseAnchorY : eastBaseAnchorY;
      if (u.y >= baseAnchor) break;
      const isHackerUnit = u.isHacker || u.name === 'Hacker' || u.cardData?.unitType === 'Hacker';
      const hasScreenSpanningTether = (isHackerUnit && (u.isHacking || u.isTranscendent || u.hackerTargetId)) ||
        (u.hasTether && u.tetherTargetId) ||
        (u.hasDualTether && (u.dualTetherTarget1Id || u.dualTetherTarget2Id));
      if (!bounds || hasScreenSpanningTether || (u.x >= bounds.left - CULL_MARGIN_X && u.x <= bounds.right + CULL_MARGIN_X &&
                      u.y >= bounds.top - 160 && u.y <= bounds.bottom + 100)) {
        this.renderUnit(ctx, u, battlefield);
      }
      unitIdx++;
    }

    // 2B. Render 8-Bit Planetary Fortress Bases & Base Attachments
    // hideBases (docs/game-design-singleplayer-kampagne.md): campaign
    // encounters read as an isolated squad fight, not a two-fortress lane
    // battle - the base data objects still exist and hold full hp (see
    // Game.startCampaignMission() in js/main.js; removing them outright
    // would crash unit.js's marchForward() bounds and battlefield.js's
    // game-over check, both of which read battlefield.bases.* unguarded),
    // only the visuals are suppressed here.
    if (!battlefield.hideBases) {
      this.ensureStaticBases(battlefield, westMain, westGlow, eastMain, eastGlow);
      if (this._staticBaseCanvas) {
        // 2-Zone Fortress Blit: Only blit the Western (0..360) and Eastern (width-360..width)
        // fortress zones, skipping the 100% transparent 880x540 center lane corridor.
        const baseZoneW = Math.min(360, Math.floor(this.width * 0.5));
        const eastZoneX = Math.max(baseZoneW, this.width - baseZoneW);
        const eastZoneW = this.width - eastZoneX;
        ctx.drawImage(this._staticBaseCanvas, 0, 0, baseZoneW, this.height, 0, 0, baseZoneW, this.height);
        if (eastZoneW > 0) {
          ctx.drawImage(this._staticBaseCanvas, eastZoneX, 0, eastZoneW, this.height, eastZoneX, 0, eastZoneW, this.height);
        }
      }
      this.renderBase(ctx, battlefield.bases.western, westMain, westGlow);
      this.renderBase(ctx, battlefield.bases.eastern, eastMain, eastGlow);
      // Stacked base modules sit on the fortress roof, so they draw after it
      this.renderModuleTowers(ctx, battlefield);
      // Ground artillery emplacements - drawn after the fortresses so the guns
      // sit visually in front of them, matching their position on the lane floor
      this.renderBaseTurret(ctx, battlefield, battlefield.bases.western, westMain, westGlow);
      this.renderBaseTurret(ctx, battlefield, battlefield.bases.eastern, eastMain, eastGlow);
      this.renderBuildingAttachmentSprites(ctx, battlefield, battlefield.bases.western, westMain, westGlow);
      this.renderBuildingAttachmentSprites(ctx, battlefield, battlefield.bases.eastern, eastMain, eastGlow);
    }
    this.renderToxinClouds(ctx, battlefield);

    // 2C. Render ground units standing IN FRONT of bases (Y >= baseAnchorY) and all Air units
    while (unitIdx < this._sortedUnits.length) {
      const u = this._sortedUnits[unitIdx];
      const isHackerUnit = u.isHacker || u.name === 'Hacker' || u.cardData?.unitType === 'Hacker';
      const hasScreenSpanningTether = (isHackerUnit && (u.isHacking || u.isTranscendent || u.hackerTargetId)) ||
        (u.hasTether && u.tetherTargetId) ||
        (u.hasDualTether && (u.dualTetherTarget1Id || u.dualTetherTarget2Id));
      if (!bounds || hasScreenSpanningTether || (u.x >= bounds.left - CULL_MARGIN_X && u.x <= bounds.right + CULL_MARGIN_X &&
                      u.y >= bounds.top - 160 && u.y <= bounds.bottom + 100)) {
        this.renderUnit(ctx, u, battlefield);
      }
      unitIdx++;
    }

    // 3. Render tactical warp-in beacons on lane floor during deploy windows
    this.renderDeployBeacons(ctx, battlefield);

    // 3B. Sul Precision Strike Volumetric Overflight Shadow & Falling Spore Bomb
    this.renderPrecisionStrikes(ctx, battlefield, nowSec);

    // 3C. Sul Retreat Protocol Bio-Acoustic Wavefront
    this.renderRetreatSignals(ctx, battlefield, nowSec);

    // 4. Render Particles, Lasers, Explosions
    particles.render(ctx, this.quality, bounds);

    // 4B. Interactive Dynamic Territory Neon Guide Rail & Frontline Dominance Balance Indicator
    this.renderTerritoryIndicator(ctx, battlefield);

    // 4C. Sculptural 3D Relief Foreground Wasteland, Rocks & Ancient Tech Props (rendered in front of floor & rail)
    this.renderForegroundCached(ctx, battlefield, nowSec);

    // 4D. Occasional full-lane weather event
    if (this.quality !== 'low') {
      if (battlefield?.mapId === 'sul') {
        this.renderSulWeather(ctx, battlefield);
      } else if (battlefield?.mapId === 'swamp') {
        this.renderSwampWeather(ctx, battlefield);
      } else if (battlefield?.mapId === 'voidglass') {
        this.renderVoidglassWeather(ctx, battlefield);
      } else if (battlefield?.mapId === 'stratos') {
        this.renderStratosWeather(ctx, battlefield);
      } else {
        this.renderWeather(ctx, battlefield);
      }
    }

    // 4E. Experimental Metamorphosis: Atmosphärischer Sul-Bioplasma-Nebel
    if (battlefield?.metamorphosisFogTimer > 0) {
      this.renderMetamorphosisFogEvent(ctx, battlefield, nowSec);
    }

    // 5. Render Targeting Crosshair if active
    if (this.targetingMode) {
      this.renderTargetingCrosshair(ctx);
    }

    ctx.restore();
    const tWorldEnd = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
    this.lastWorldMs = tWorldEnd - t0World;

    // 6. Post-Process: CRT bloom, subtle scanlines, film grain, base danger pulse
    const t0Post = tWorldEnd;
    this.renderPostProcess(ctx, battlefield);
    const tPostEnd = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
    this.lastPostMs = tPostEnd - t0Post;
  }


  // Ensures the static background and foreground layers are baked into world-space
  // offscreen canvases (_staticBgCanvas, _staticFgCanvas). Runs once on startup
  // or on explicit terrain/world size change.
  // Frees the baked world canvases' backing stores (ensureStaticBackground/ensureStaticBases run every
  // render and re-bake when they are not primed, so the next frame simply rebuilds them).
  releaseStaticCaches() {
    for (const c of [this._staticBgCanvas, this._staticFgCanvas, this._staticBaseCanvas, this._lightmapCanvas, this._territoryBadgeCanvas]) {
      if (c) { c.width = 0; c.height = 0; }
    }
    this._staticBgPrimed = false;
    this._staticBasePrimed = false;
    this._lightmapHasContent = false;
    this._territoryBadgeKey = null;
    if (this._lightStamps instanceof Map) {
      for (const c of this._lightStamps.values()) {
        if (c) { c.width = 0; c.height = 0; }
      }
      this._lightStamps.clear();
    }
    if (this._deckStreakStamps instanceof Map) {
      for (const c of this._deckStreakStamps.values()) {
        if (c) { c.width = 0; c.height = 0; }
      }
      this._deckStreakStamps.clear();
    }
    this._bunkerGradCache?.clear?.();
    clearSpriteBitmapCache();
    clearModuleBitmapCache?.();
    clearAuraBitmapCache?.();
    clearStatueBitmapCache?.();
    this.releasePostProcessCaches?.();
    releaseStratosCaches?.();
    releaseVoidglassCaches?.(this);
  }

  // World-space rect covering at least the world itself plus whatever the
  // camera currently shows beyond it (letterbox margins on wide/tall
  // screens, panned edges in portrait). Used by full-field world-space
  // overlays (atmospheric veil, rain wash) so they never end in a hard edge
  // at the world border.
  getVisibleWorldRect() {
    const bounds = this.viewport ? this.viewport.getVisibleBounds() : null;
    if (bounds) {
      // Small safety margin so screen shake / punch offsets never expose the rect's edge.
      const m = 48;
      return {
        left: Math.min(0, Math.floor(bounds.left - m)),
        top: Math.min(0, Math.floor(bounds.top - m)),
        right: Math.max(this.width, Math.ceil(bounds.right + m)),
        bottom: Math.max(this.height, Math.ceil(bounds.bottom + m))
      };
    }
    const padLeft = this._staticBgPadLeft || 0;
    const padTop = this._staticBgPadTop || 0;
    const padRight = this._staticBgPadRight || 0;
    const padBottom = this._staticBgPadBottom || 0;
    return {
      left: -padLeft,
      top: -padTop,
      right: this.width + padRight,
      bottom: this.height + padBottom
    };
  }

  ensureStaticBackground(battlefield) {
    const bounds = this.viewport ? this.viewport.getVisibleBounds() : null;
    let boundTop = bounds ? bounds.top : 0;
    let boundBottom = bounds ? bounds.bottom : this.height;
    // In Mobile Portrait, pre-pad vertically for the 50% wide-zoom state (plus rubber-band margin)
    // right from match start so pinching out never triggers a synchronous mid-gesture re-bake.
    if (this.viewport && this.viewport.mode === 'cover-height' && this.viewport.zoomWide > 0) {
      const minZoom = this.viewport.zoomWide * 0.90;
      const maxHalfH = (this.viewport.cssH / 2) / minZoom;
      const baseCamY = this.viewport.baseCameraY || (this.height / 2 + 16);
      boundTop = Math.min(boundTop, baseCamY - maxHalfH);
      boundBottom = Math.max(boundBottom, baseCamY + maxHalfH);
    }
    const minLeft = bounds ? Math.min(-240, bounds.left - 60) : -240;
    const maxRight = bounds ? Math.max(this.width + 240, bounds.right + 60) : this.width + 240;
    const minTop = Math.min(-140, boundTop - 60);
    const maxBottom = Math.max(this.height + 140, boundBottom + 60);

    // Quantize padding to generous 200px / 100px buckets so camera motion never invalidates static cache
    const padLeft = Math.ceil(-minLeft / 200) * 200;
    const padRight = Math.ceil((maxRight - this.width) / 200) * 200;
    const padTop = Math.ceil(-minTop / 100) * 100;
    const padBottom = Math.ceil((maxBottom - this.height) / 100) * 100;

    const totalW = this.width + padLeft + padRight;
    const totalH = this.height + padTop + padBottom;

    // The human map's bake never varies between matches (same skyline every
    // time), which is why this cache historically never checked anything
    // match-specific - only camera padding. The Sul map does vary per match
    // (mapId itself, and its mapSeed-driven pyramid/rock layout), and the
    // Renderer/Battlefield persist across matches (see js/main.js's
    // start*Match() - only battlefield.reset() runs, never `new Battlefield`),
    // so a map switch between two matches must invalidate this cache too.
    const mapKey = `${battlefield?.mapId || 'human'}:${battlefield?.mapSeed || 0}`;
    if (this._staticBgPrimed && this._staticBgPadTop === padTop && this._staticBgPadLeft === padLeft &&
        this._staticBgMapKey === mapKey && this._staticBgQuality === this.quality &&
        this._staticBgCanvas && this._staticBgCanvas.width === totalW && this._staticBgCanvas.height === totalH) {
      return;
    }
    if (this._staticBgMapKey !== undefined && this._staticBgMapKey !== mapKey) {
      // Map switched since the last bake (a new match started) - drop any
      // in-flight ambient critters from the previous map rather than
      // leaving e.g. a Sul grub mid-walk on the human skyline.
      this.foregroundCritters.length = 0;
      if (this._sulBottomCritters) this._sulBottomCritters.length = 0;
    }
    this._staticBgMapKey = mapKey;
    this._staticBgQuality = this.quality;

    this._staticBgPadLeft = padLeft;
    this._staticBgPadTop = padTop;
    this._staticBgPadRight = padRight;
    this._staticBgPadBottom = padBottom;

    if (!this._staticBgCanvas) {
      this._staticBgCanvas = document.createElement('canvas');
    }
    if (this._staticBgCanvas.width !== totalW || this._staticBgCanvas.height !== totalH) {
      this._staticBgCanvas.width = totalW;
      this._staticBgCanvas.height = totalH;
    }
    this._staticBgCtx = this._staticBgCanvas.getContext('2d');
    this._staticBgCtx.setTransform(1, 0, 0, 1, 0, 0);
    this._staticBgCtx.clearRect(0, 0, totalW, totalH);
    this._staticBgCtx.save();
    this._staticBgCtx.translate(padLeft, padTop);
    const staticBgBounds = {
      left: -padLeft,
      top: -padTop,
      right: this.width + padRight,
      bottom: this.height + padBottom,
      visibleLeft: -padLeft,
      visibleTop: -padTop,
      visibleRight: this.width + padRight,
      visibleBottom: this.height + padBottom
    };
    if (battlefield?.mapId === 'sul') {
      this.renderSulStaticBackground(this._staticBgCtx, battlefield, staticBgBounds);
    } else if (battlefield?.mapId === 'swamp') {
      this.renderSwampStaticBackground(this._staticBgCtx, battlefield, staticBgBounds);
    } else if (battlefield?.mapId === 'voidglass') {
      this.renderVoidglassStaticBackground(this._staticBgCtx, battlefield, staticBgBounds);
    } else if (battlefield?.mapId === 'stratos') {
      this.renderStratosStaticBackground(this._staticBgCtx, battlefield, staticBgBounds);
    } else {
      this.renderStaticBackground(this._staticBgCtx, battlefield, staticBgBounds);
    }
    this._staticBgCtx.restore();

    if (!this._staticFgCanvas) {
      this._staticFgCanvas = document.createElement('canvas');
    }
    if (this._staticFgCanvas.width !== totalW || this._staticFgCanvas.height !== totalH) {
      this._staticFgCanvas.width = totalW;
      this._staticFgCanvas.height = totalH;
    }
    this._staticFgCtx = this._staticFgCanvas.getContext('2d');
    this._staticFgCtx.setTransform(1, 0, 0, 1, 0, 0);
    this._staticFgCtx.clearRect(0, 0, totalW, totalH);
    this._staticFgCtx.save();
    this._staticFgCtx.translate(padLeft, padTop);
    const staticFgBounds = {
      left: -padLeft,
      top: -padTop,
      right: this.width + padRight,
      bottom: this.height + padBottom,
      visibleLeft: -padLeft,
      visibleTop: -padTop,
      visibleRight: this.width + padRight,
      visibleBottom: this.height + padBottom
    };
    if (battlefield?.mapId === 'sul') {
      this.renderSulStaticForeground(this._staticFgCtx, battlefield, staticFgBounds);
    } else if (battlefield?.mapId === 'swamp') {
      this.renderSwampStaticForeground(this._staticFgCtx, battlefield, staticFgBounds);
    } else if (battlefield?.mapId === 'voidglass') {
      this.renderVoidglassStaticForeground(this._staticFgCtx, battlefield, staticFgBounds);
    } else if (battlefield?.mapId === 'stratos') {
      this.renderStratosStaticForeground(this._staticFgCtx, battlefield, staticFgBounds);
    } else {
      this.renderStaticForeground(this._staticFgCtx, battlefield, staticFgBounds);
    }
    this._staticFgCtx.restore();

    this._staticBgPrimed = true;
  }

  // Pre-bakes the complex planetary fortress bunker platform and ground shadows
  // into an offscreen canvas (_staticBaseCanvas). Render loop blits it via a single
  // ctx.drawImage call, eliminating ~300-400 Canvas 2D path instructions per frame.
  ensureStaticBases(battlefield, westMain, westGlow, eastMain, eastGlow) {
    if (!battlefield?.bases) return;
    const westRace = battlefield.bases.western?.race || 'Human';
    const eastRace = battlefield.bases.eastern?.race || 'Human';
    const key = `${westRace}:${eastRace}:${this.quality}`;
    if (this._staticBasePrimed && this._staticBaseKey === key && this._staticBaseCanvas &&
        this._staticBaseCanvas.width === this.width && this._staticBaseCanvas.height === this.height) {
      return;
    }
    if (!this._staticBaseCanvas) {
      this._staticBaseCanvas = document.createElement('canvas');
    }
    if (this._staticBaseCanvas.width !== this.width || this._staticBaseCanvas.height !== this.height) {
      this._staticBaseCanvas.width = this.width;
      this._staticBaseCanvas.height = this.height;
    }
    const bCtx = this._staticBaseCanvas.getContext('2d');
    bCtx.setTransform(1, 0, 0, 1, 0, 0);
    bCtx.clearRect(0, 0, this.width, this.height);

    const isLow = this.quality === 'low';
    const topDepth = MODULE_TOP_D;
    const skewX = MODULE_SKEW;
    const DOCK_BLOCK_W = 88;
    const BASE_SCALE = 0.864;
    const BLDG_SCALE = 1.06;

    // 1. Bake Western Base
    if (battlefield.bases.western) {
      const base = battlefield.bases.western;
      if (westRace === 'Sul') {
        this.renderSulBaseStatic(bCtx, base, westMain, westGlow);
      } else {
        const x = base.x;
        const y = base.y;
        const dir = 1;
        const groundY = y + 26;
        const dockX = x - 40;
        const bAnchorX = dockX + DOCK_BLOCK_W / 2;
        const bAnchorY = groundY;

        bCtx.save();
        bCtx.translate(x, y);
        bCtx.scale(BASE_SCALE, BASE_SCALE);
        bCtx.translate(-x, -y);

        bCtx.save();
        bCtx.translate(bAnchorX, bAnchorY);
        bCtx.scale(BLDG_SCALE, BLDG_SCALE);
        bCtx.translate(-bAnchorX, -bAnchorY);
        this._drawBuildingGroundShadow(bCtx, x, y, dir, topDepth, skewX);
        bCtx.restore();

        bCtx.save();
        bCtx.translate(bAnchorX, bAnchorY);
        bCtx.scale(BLDG_SCALE, BLDG_SCALE);
        bCtx.translate(-bAnchorX, -bAnchorY);
        this._drawBunkerPlatform(bCtx, base, x, y, dir, topDepth, skewX, westMain, westGlow, 0, isLow);
        bCtx.restore();

        bCtx.restore();
      }
    }

    // 2. Bake Eastern Base
    if (battlefield.bases.eastern) {
      const base = battlefield.bases.eastern;
      if (eastRace === 'Sul') {
        this.renderSulBaseStatic(bCtx, base, eastMain, eastGlow);
      } else {
        const x = base.x;
        const y = base.y;
        const dir = -1;
        const groundY = y + 26;
        const dockX = x + 40 - DOCK_BLOCK_W;
        const bAnchorX = dockX + DOCK_BLOCK_W / 2;
        const bAnchorY = groundY;

        bCtx.save();
        bCtx.translate(x, y);
        bCtx.scale(BASE_SCALE, BASE_SCALE);
        bCtx.translate(-x, -y);

        bCtx.save();
        bCtx.translate(bAnchorX, bAnchorY);
        bCtx.scale(BLDG_SCALE, BLDG_SCALE);
        bCtx.translate(-bAnchorX, -bAnchorY);
        this._drawBuildingGroundShadow(bCtx, x, y, dir, topDepth, skewX);
        bCtx.restore();

        bCtx.save();
        bCtx.translate(bAnchorX, bAnchorY);
        bCtx.scale(BLDG_SCALE, BLDG_SCALE);
        bCtx.translate(-bAnchorX, -bAnchorY);
        this._drawBunkerPlatform(bCtx, base, x, y, dir, topDepth, skewX, eastMain, eastGlow, 0, isLow);
        bCtx.restore();

        bCtx.restore();
      }
    }

    this._staticBaseKey = key;
    this._staticBasePrimed = true;
  }

  // Fast GPU Blit of the pre-baked world-space static background layer,
  // followed by ultra-lightweight dynamic ambient overlays (stars, traffic, beacons).
  renderBackgroundCached(ctx, battlefield, particles, nowSec) {
    this.ensureStaticBackground(battlefield);
    const padLeft = this._staticBgPadLeft || 80;
    const padTop = this._staticBgPadTop || 40;
    const padRight = this._staticBgPadRight || 80;
    const padBottom = this._staticBgPadBottom || 40;

    // 1. Fast GPU Blit of static world background (Culled to visible slice in portrait cover-height)
    if (this.viewport && this.viewport.mode === 'cover-height') {
      const bounds = this.viewport.getVisibleBounds();
      const margin = 40;
      const cLeft = Math.max(0, Math.floor(bounds.left + padLeft - margin));
      const cTop = Math.max(0, Math.floor(bounds.top + padTop - margin));
      const cRight = Math.min(this._staticBgCanvas.width, Math.ceil(bounds.right + padLeft + margin));
      const cBottom = Math.min(this._staticBgCanvas.height, Math.ceil(bounds.bottom + padTop + margin));
      const sW = cRight - cLeft;
      const sH = cBottom - cTop;
      if (sW > 0 && sH > 0) {
        ctx.drawImage(this._staticBgCanvas, cLeft, cTop, sW, sH, cLeft - padLeft, cTop - padTop, sW, sH);
      }
    } else {
      ctx.drawImage(this._staticBgCanvas, -padLeft, -padTop);
    }

    // 2. Lightweight dynamic overlays (stars, traffic, beacons, flora sway, nuke fallout)
    // Skipped entirely on low tier per Paradigma 10 (0 additional CPU load, pure static blit)
    if (this.quality !== 'low') {
      const dynamicBgBounds = {
        left: -padLeft,
        top: -padTop,
        right: this.width + padRight,
        bottom: this.height + padBottom,
        visibleLeft: -padLeft,
        visibleTop: -padTop,
        visibleRight: this.width + padRight,
        visibleBottom: this.height + padBottom
      };
      if (battlefield?.mapId === 'sul') {
        this.renderSulDynamicBackground(ctx, battlefield, nowSec, dynamicBgBounds);
      } else if (battlefield?.mapId === 'swamp') {
        this.renderSwampDynamicBackground(ctx, battlefield, nowSec, dynamicBgBounds);
      } else if (battlefield?.mapId === 'voidglass') {
        this.renderVoidglassDynamicBackground(ctx, battlefield, nowSec, dynamicBgBounds);
      } else if (battlefield?.mapId === 'stratos') {
        this.renderStratosDynamicBackground(ctx, battlefield, nowSec, dynamicBgBounds);
      } else {
        this.renderDynamicBackground(ctx, battlefield, nowSec, dynamicBgBounds);
      }
    }
  }

  renderForegroundCached(ctx, battlefield, nowSec = null) {
    this.ensureStaticBackground(battlefield);
    const padLeft = this._staticBgPadLeft || 80;
    const padTop = this._staticBgPadTop || 40;
    // Fast GPU Blit of static foreground (Culled to visible slice in portrait cover-height)
    if (this.viewport && this.viewport.mode === 'cover-height') {
      const bounds = this.viewport.getVisibleBounds();
      const margin = 40;
      const cLeft = Math.max(0, Math.floor(bounds.left + padLeft - margin));
      const cTop = Math.max(0, Math.floor(bounds.top + padTop - margin));
      const cRight = Math.min(this._staticFgCanvas.width, Math.ceil(bounds.right + padLeft + margin));
      const cBottom = Math.min(this._staticFgCanvas.height, Math.ceil(bounds.bottom + padTop + margin));
      const sW = cRight - cLeft;
      const sH = cBottom - cTop;
      if (sW > 0 && sH > 0) {
        ctx.drawImage(this._staticFgCanvas, cLeft, cTop, sW, sH, cLeft - padLeft, cTop - padTop, sW, sH);
      }
    } else {
      ctx.drawImage(this._staticFgCanvas, -padLeft, -padTop);
    }
    if (this.quality !== 'low') {
      if (battlefield?.mapId === 'sul') {
        this.renderSulDynamicForeground(ctx, battlefield, nowSec);
        this.renderSulForegroundCritters(ctx, battlefield, nowSec);
      } else if (battlefield?.mapId === 'swamp') {
        this.renderSwampDynamicForeground(ctx, battlefield, nowSec);
        this.renderSwampForegroundCritters(ctx, battlefield, nowSec);
      } else if (battlefield?.mapId === 'voidglass') {
        this.renderVoidglassDynamicForeground(ctx, battlefield, nowSec);
        this.renderVoidglassForegroundCritters(ctx, battlefield, nowSec);
      } else if (battlefield?.mapId === 'stratos') {
        this.renderStratosDynamicForeground(ctx, battlefield, nowSec);
        this.renderStratosForegroundCritters(ctx, battlefield, nowSec);
      } else {
        this.renderDynamicForeground(ctx, battlefield, nowSec);
        this.renderForegroundCritters(ctx, battlefield, nowSec);
      }
    }
  }

  renderForeground(ctx, battlefield, nowSec = null) {
    this.renderForegroundCached(ctx, battlefield, nowSec);
  }

  // One shared additive lightmap per frame (world-space, not device pixels -
  // battlefield coordinates map 1:1 to its pixels regardless of camera zoom/
  // DPR) built from every currently-active reactive point light. Replaces
  // what used to be each consumer (ground illumination, every unit's own
  // floor-glow in renderUnit()) independently re-deriving the same radial
  // gradients from particles.lights - same visual source, computed once.
  _getLightStamp(color) {
    if (!this._lightStamps) this._lightStamps = new Map();
    let stamp = this._lightStamps.get(color);
    if (!stamp) {
      stamp = document.createElement('canvas');
      stamp.width = 64;
      stamp.height = 64;
      const sctx = stamp.getContext('2d');
      const grad = sctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      grad.addColorStop(0, color);
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      sctx.fillStyle = grad;
      sctx.fillRect(0, 0, 64, 64);
      this._lightStamps.set(color, stamp);
    }
    return stamp;
  }

  // Specular reflection streak stamp for wet metallic deck floor illumination
  _getDeckStreakStamp(color) {
    if (!this._deckStreakStamps) this._deckStreakStamps = new Map();
    let stamp = this._deckStreakStamps.get(color);
    if (!stamp) {
      stamp = document.createElement('canvas');
      stamp.width = 32;
      stamp.height = 96;
      const sctx = stamp.getContext('2d');
      // 1. Soft colored ambient reflection column with lateral elliptical falloff
      const grad = sctx.createLinearGradient(0, 0, 0, 96);
      grad.addColorStop(0, color);
      grad.addColorStop(0.35, color);
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      sctx.fillStyle = grad;
      sctx.beginPath();
      sctx.ellipse(16, 48, 14, 48, 0, 0, Math.PI * 2);
      sctx.fill();

      // 2. Soft translucent specular glint at the reflection contact point (subtle sheen, not opaque white)
      const coreGrad = sctx.createLinearGradient(0, 0, 0, 48);
      coreGrad.addColorStop(0, 'rgba(255, 255, 255, 0.45)');
      coreGrad.addColorStop(0.35, 'rgba(255, 255, 255, 0.18)');
      coreGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
      sctx.fillStyle = coreGrad;
      sctx.beginPath();
      sctx.ellipse(16, 20, 5, 22, 0, 0, Math.PI * 2);
      sctx.fill();

      this._deckStreakStamps.set(color, stamp);
    }
    return stamp;
  }

  buildLightmap(particles) {
    if (this.quality === 'low') { this._lightmapHasContent = false; return; }
    const lm = this._lightmapCanvas;
    const lctx = this._lightmapCtx;
    if (!lm || !lctx) return;
    const hasLights = !!particles.lights && particles.lights.length > 0;
    const hasBeams = this.quality !== 'low' && !!particles.beams && particles.beams.length > 0;
    // Nothing to draw and the canvas is already blank: skip the full-size
    // clearRect too (it was running every frame on every non-low tier).
    if (!hasLights && !hasBeams && !this._lightmapHasContent) return;
    if (lm.width !== this.width || lm.height !== this.height) {
      lm.width = this.width;
      lm.height = this.height;
    }
    lctx.setTransform(1, 0, 0, 1, 0, 0);
    lctx.clearRect(0, 0, lm.width, lm.height);
    this._lightmapHasContent = hasLights || hasBeams;
    if (!this._lightmapHasContent) return;

    lctx.globalCompositeOperation = 'lighter';
    const maxLights = this.isMobile ? (this.quality === 'medium' ? 4 : 6) : 14;
    const lightsCount = Math.min(particles.lights ? particles.lights.length : 0, maxLights);
    for (let i = 0; i < lightsCount; i++) {
      const l = particles.lights[i];
      const stamp = this._getLightStamp(l.color);
      lctx.globalAlpha = l.alpha * 0.55;
      lctx.drawImage(stamp, l.x - l.radius, l.y - l.radius, l.radius * 2, l.radius * 2);

      // Specular wet deck reflection streak: translucent sheen on the wet metallic floor
      if (this.quality !== 'low' && l.y >= 160 && l.radius >= 10) {
        const streak = this._getDeckStreakStamp(l.color);
        lctx.globalAlpha = l.alpha * 0.32;
        const streakW = Math.max(14, l.radius * 0.7);
        const streakH = Math.min(96, Math.max(36, l.radius * 2.0));
        lctx.drawImage(streak, l.x - streakW * 0.5, l.y, streakW, streakH);
      }
    }

    // Specular beam sheen: lasers & Gauss tracers cast a soft, transparent reflection on the wet deck
    if (hasBeams) {
      const maxBeams = Math.min(particles.beams.length, 6);
      for (let i = 0; i < maxBeams; i++) {
        const b = particles.beams[i];
        if (b.y1 >= 160 || b.y2 >= 160) {
          const alpha = Math.max(0, b.life / b.maxLife);
          if (alpha > 0.08) {
            lctx.globalAlpha = alpha * 0.20;
            lctx.strokeStyle = b.color;
            lctx.lineWidth = Math.max(2, (b.width || 2) * 1.5);
            lctx.beginPath();
            lctx.moveTo(b.x1, b.y1 + 10);
            lctx.lineTo(b.x2, b.y2 + 10);
            lctx.stroke();
          }
        }
      }
    }

    lctx.globalAlpha = 1;
    lctx.globalCompositeOperation = 'source-over';
  }


  // 💡 DYNAMIC TERRITORY NEON GUIDE RAIL & GAMIFIED FRONTLINE DOMINANCE HUD / BALANCE INDICATOR
  // Rendered outside cached background so it stays razor-sharp and immune to depth blur and smog veil
  renderTerritoryIndicator(ctx, battlefield) {
    if (!battlefield) return;
    // hideBases (docs/game-design-singleplayer-kampagne.md): a campaign
    // encounter with no bases has no lane frontline to contest either -
    // the whole "Frontline Dominance Balance Indicator" (rails, needle,
    // beacon, status badge) doesn't apply. Zeroes the click hit-box too,
    // so a stale rect from a previous match can't still catch a tap here.
    if (battlefield.hideBases) {
      if (this.territoryBadgeRect) this.territoryBadgeRect.w = 0;
      return;
    }
    const t = performance.now() * 0.001;
    const laneY = battlefield.laneY;
    const laneH = battlefield.laneHeight;
    const lowerY = laneY + laneH;

    const bounds = this.viewport ? this.viewport.getVisibleBounds() : null;
    const visibleLeft = bounds ? Math.min(0, bounds.left - 20) : -40;
    const visibleRight = bounds ? Math.max(this.width, bounds.right + 20) : this.width + 40;

    const splitX = battlefield.territoryDivider !== undefined ? battlefield.territoryDivider : (this.width / 2);
    const control = battlefield.territoryControl || 'neutral';
    const isWestBonus = control === 'western';
    const isEastBonus = control === 'eastern';
    const bonusFrac = battlefield.territoryBonus || 0;
    if (this._cachedBonusFrac === undefined || Math.abs(bonusFrac - this._cachedBonusFrac) >= 0.001) {
      this._cachedBonusFrac = bonusFrac;
      const pct = (bonusFrac * 100).toFixed(1);
      this._cachedBonusGasStr = `+${pct}%`;
    }
    const bonusGasStr = this._cachedBonusGasStr;

    ctx.save();

    // 1. Western Rail (Left of splitX)
    const westRailH = isWestBonus ? 4 : 2.5;
    ctx.fillStyle = isWestBonus ? '#00f0ff' : 'rgba(0, 240, 255, 0.45)';
    ctx.fillRect(visibleLeft, lowerY + 13 - (isWestBonus ? 1 : 0), splitX - visibleLeft, westRailH);

    if (isWestBonus) {
      // Radiant Cyan Neon Glow
      ctx.fillStyle = 'rgba(0, 240, 255, 0.32)';
      ctx.fillRect(visibleLeft, lowerY + 10, splitX - visibleLeft, 8);

      // Fast energy packets racing eastward along the rail into enemy territory
      for (let p = 0; p < 4; p++) {
        const pulseProg = (t * 2.2 + p * 0.25) % 1.0;
        const pulsePx = visibleLeft + pulseProg * (splitX - visibleLeft);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(pulsePx, lowerY + 12, 16, 4);
      }
    }

    // 2. Eastern Rail (Right of splitX)
    const eastRailH = isEastBonus ? 4 : 2.5;
    ctx.fillStyle = isEastBonus ? '#ff003c' : 'rgba(255, 0, 60, 0.45)';
    ctx.fillRect(splitX, lowerY + 13 - (isEastBonus ? 1 : 0), visibleRight - splitX, eastRailH);

    if (isEastBonus) {
      // Radiant Crimson Neon Glow
      ctx.fillStyle = 'rgba(255, 0, 60, 0.32)';
      ctx.fillRect(splitX, lowerY + 10, visibleRight - splitX, 8);

      // Fast energy packets racing westward along the rail
      for (let p = 0; p < 4; p++) {
        const pulseProg = (t * 2.2 + p * 0.25) % 1.0;
        const pulsePx = visibleRight - (pulseProg * (visibleRight - splitX));
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(pulsePx - 16, lowerY + 12, 16, 4);
      }
    }

    // 3. Dynamic Territory Frontline Diamond Beacon & Orbital Halo
    const nodeCenterY = lowerY + 8; // Centered vertically across hazard barrier
    const nodeHalf = (isWestBonus || isEastBonus) ? 9 : 7;
    const nodeColor = isWestBonus ? '#00f0ff' : (isEastBonus ? '#ff003c' : '#facc15');
    const nodeGlowColor = isWestBonus ? 'rgba(0, 240, 255, 0.55)' : (isEastBonus ? 'rgba(255, 0, 60, 0.55)' : 'rgba(250, 204, 21, 0.45)');

    // Vertical Holographic Laser Needle projecting into lower battlefield lane
    if (this.quality !== 'low') {
      const needlePulse = 0.55 + 0.45 * Math.sin(t * 4);
      ctx.fillStyle = nodeColor;
      ctx.globalAlpha = 0.25 * needlePulse;
      ctx.fillRect(splitX - 1.5, lowerY - 32, 3, 34);
      ctx.fillStyle = '#ffffff';
      ctx.globalAlpha = 0.6 * needlePulse;
      ctx.fillRect(splitX - 0.5, lowerY - 28, 1, 30);
      ctx.globalAlpha = 1.0;
    }

    // Expanding Orbital Sonar Rings around Frontline Node (desktop high & ultra)
    if (!this.isMobile && (this.quality === 'high' || this.quality === 'ultra')) {
      for (let ring = 0; ring < 2; ring++) {
        const rProg = (t * 1.4 + ring * 0.5) % 1.0;
        const rRad = 8 + rProg * 22;
        const rAlpha = (1 - rProg) * 0.45;
        ctx.strokeStyle = nodeColor;
        ctx.globalAlpha = rAlpha;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.ellipse(splitX, nodeCenterY, rRad, rRad * 0.6, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.globalAlpha = 1.0;
    }

    // Outer Radial Halo (skipped on low and mobile to save fillrate)
    if (this.quality !== 'low' && !this.isMobile) {
      if (!this._haloLUTs) {
        this._haloLUTs = new Map();
      }
      let haloCanvas = this._haloLUTs.get(nodeGlowColor);
      if (!haloCanvas) {
        haloCanvas = document.createElement('canvas');
        haloCanvas.width = 48;
        haloCanvas.height = 48;
        const hCtx = haloCanvas.getContext('2d');
        const hGrad = hCtx.createRadialGradient(24, 24, 2, 24, 24, 24);
        hGrad.addColorStop(0, nodeGlowColor);
        const midColor = nodeGlowColor.indexOf('rgba') === 0
          ? nodeGlowColor.replace(/[\d\.]+\)$/, '0.12)')
          : 'rgba(56, 189, 248, 0.12)';
        hGrad.addColorStop(0.6, midColor);
        hGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
        hCtx.fillStyle = hGrad;
        hCtx.beginPath();
        hCtx.arc(24, 24, 24, 0, Math.PI * 2);
        hCtx.fill();
        this._haloLUTs.set(nodeGlowColor, haloCanvas);
      }
      ctx.drawImage(haloCanvas, splitX - 24, nodeCenterY - 24);
    }

    // Diamond Outer Black Stroke
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(splitX, nodeCenterY - nodeHalf);
    ctx.lineTo(splitX + nodeHalf, nodeCenterY);
    ctx.lineTo(splitX, nodeCenterY + nodeHalf);
    ctx.lineTo(splitX - nodeHalf, nodeCenterY);
    ctx.closePath();
    ctx.stroke();

    // Diamond Colored Fill & Facets
    ctx.fillStyle = nodeColor;
    ctx.fill();

    // Diamond Left Shadow Facet
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.beginPath();
    ctx.moveTo(splitX, nodeCenterY - nodeHalf);
    ctx.lineTo(splitX - nodeHalf, nodeCenterY);
    ctx.lineTo(splitX, nodeCenterY + nodeHalf);
    ctx.closePath();
    ctx.fill();

    // Diamond Right Specular Highlight Facet
    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.beginPath();
    ctx.moveTo(splitX, nodeCenterY - nodeHalf);
    ctx.lineTo(splitX + nodeHalf, nodeCenterY);
    ctx.lineTo(splitX, nodeCenterY);
    ctx.closePath();
    ctx.fill();

    // Pure White Core Glint
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(splitX - 2, nodeCenterY - 2, 4, 4);

    // 🔒 Sci-Fi Anchor / Clamping Lock Brackets when Territory is Locked by Claiming Pillar (No emojis!)
    if (battlefield.isTerritoryLocked) {
      ctx.save();
      const lockPulse = 0.85 + 0.15 * Math.sin(t * 5.0);
      const isOwnerWest = battlefield.territoryLockedTeam === 'western';
      const isOwnerEast = battlefield.territoryLockedTeam === 'eastern';
      const clampColor = isOwnerWest ? '#00f0ff' : (isOwnerEast ? '#ff003c' : '#ef4444');

      ctx.strokeStyle = clampColor;
      ctx.lineWidth = 2.0;

      // Clamping jaws (left & right brackets gripping the rail)
      // Left clamp
      ctx.beginPath();
      ctx.moveTo(splitX - 14, nodeCenterY - 7);
      ctx.lineTo(splitX - 9, nodeCenterY - 7);
      ctx.lineTo(splitX - 9, nodeCenterY + 7);
      ctx.lineTo(splitX - 14, nodeCenterY + 7);
      ctx.stroke();

      // Right clamp
      ctx.beginPath();
      ctx.moveTo(splitX + 14, nodeCenterY - 7);
      ctx.lineTo(splitX + 9, nodeCenterY - 7);
      ctx.lineTo(splitX + 9, nodeCenterY + 7);
      ctx.lineTo(splitX + 14, nodeCenterY + 7);
      ctx.stroke();

      // Padlock Shackle
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.8;
      ctx.beginPath();
      ctx.arc(splitX, nodeCenterY - 4.5, 4, Math.PI, 0);
      ctx.lineTo(splitX + 4, nodeCenterY);
      ctx.moveTo(splitX - 4, nodeCenterY - 4.5);
      ctx.lineTo(splitX - 4, nodeCenterY);
      ctx.stroke();

      // Padlock Body
      ctx.fillStyle = '#b91c1c';
      ctx.strokeStyle = '#fca5a5';
      ctx.lineWidth = 1.0;
      if (typeof ctx.roundRect === 'function') {
        ctx.roundRect(splitX - 6, nodeCenterY - 0.5, 12, 8.5, 2);
      } else if (typeof ctx.rect === 'function') {
        ctx.rect(splitX - 6, nodeCenterY - 0.5, 12, 8.5);
      } else {
        ctx.fillRect(splitX - 6, nodeCenterY - 0.5, 12, 8.5);
      }
      ctx.fill();
      ctx.stroke();

      // Keyhole / sensor slit
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(splitX - 1, nodeCenterY + 2, 2, 3.5);
      ctx.restore();
    }

    // 🏆 4. GAMIFIED HOLOGRAPHIC TERRITORY DOMINANCE BADGE (Floating Cyberpunk Status Capsule)
    ctx.save();
    const badgeW = 264;
    const badgeH = 26;
    const badgeX = Math.min(this.width - badgeW / 2 - 16, Math.max(badgeW / 2 + 16, splitX));
    const badgeY = lowerY - 32;

    if (!this.territoryBadgeRect) {
      this.territoryBadgeRect = { x: 0, y: 0, w: 0, h: 0 };
    }
    this.territoryBadgeRect.x = badgeX - badgeW / 2;
    this.territoryBadgeRect.y = badgeY;
    this.territoryBadgeRect.w = badgeW;
    this.territoryBadgeRect.h = badgeH;

    const textCyclePeriod = 4.5; // Rotates text every 4.5 seconds
    const phraseIdx = Math.floor(t / textCyclePeriod);
    const pulsePhase = Math.sin(t * 6);
    const pulseBit = pulsePhase > 0 ? 1 : 0;
    const westFraction = Math.max(0.05, Math.min(0.95, splitX / this.width));
    const gaugeStep = Math.round(westFraction * 244);

    const badgePadX = 15;
    const badgePadY = 8;
    const cacheLogicalW = badgeW + badgePadX * 2;
    const cacheLogicalH = badgeH + badgePadY * 2;
    const cacheScale = 2;
    const badgeStateKey = `${this.quality}:${control}:${battlefield.isTerritoryLocked ? (battlefield.territoryLockedTeam || '1') : '0'}:${bonusGasStr}:${phraseIdx}:${pulseBit}:${gaugeStep}`;

    let useBadgeCache = false;
    if (typeof document !== 'undefined' && document.createElement) {
      if (!this._territoryBadgeCanvas || this._territoryBadgeCanvas.width !== cacheLogicalW * cacheScale) {
        const c = document.createElement('canvas');
        const cCtx = c ? c.getContext('2d') : null;
        if (c && cCtx && typeof cCtx.roundRect === 'function' && typeof cCtx.fillText === 'function') {
          c.width = cacheLogicalW * cacheScale;
          c.height = cacheLogicalH * cacheScale;
          this._territoryBadgeCanvas = c;
          this._territoryBadgeCtx = cCtx;
          this._territoryBadgeKey = null;
        }
      }
      if (this._territoryBadgeCanvas && this._territoryBadgeCtx) {
        useBadgeCache = true;
      }
    }

    const drawBadgeContents = (targetCtx, cx, topY) => {
      // A. Drop Shadow & Radial Glow behind Badge (skipped on low)
      if (this.quality !== 'low') {
        this._badgeGlowLUTs = this._badgeGlowLUTs || new Map();
        let bCanvas = this._badgeGlowLUTs.get(nodeGlowColor);
        if (!bCanvas) {
          bCanvas = document.createElement('canvas');
          bCanvas.width = badgeW + 30;
          bCanvas.height = badgeH + 16;
          const bCtx = bCanvas.getContext('2d');
          const bGrad = bCtx.createRadialGradient(bCanvas.width / 2, bCanvas.height / 2, 10, bCanvas.width / 2, bCanvas.height / 2, badgeW * 0.55);
          bGrad.addColorStop(0, nodeGlowColor.replace(/[\d\.]+\)$/, '0.22)'));
          bGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
          bCtx.fillStyle = bGrad;
          bCtx.fillRect(0, 0, bCanvas.width, bCanvas.height);
          this._badgeGlowLUTs.set(nodeGlowColor, bCanvas);
        }
        targetCtx.drawImage(bCanvas, cx - badgeW / 2 - 15, topY - 8);
      }

      // B. Beveled Capsule Body (Dark Obsidian Glass)
      targetCtx.fillStyle = 'rgba(3, 7, 18, 0.94)';
      targetCtx.beginPath();
      targetCtx.roundRect(cx - badgeW / 2, topY, badgeW, badgeH, 6);
      targetCtx.fill();

      // C. Glowing Cyberpunk Border & Neon Corner Brackets
      targetCtx.strokeStyle = isWestBonus ? 'rgba(0, 240, 255, 0.7)' : (isEastBonus ? 'rgba(255, 0, 60, 0.7)' : 'rgba(250, 204, 21, 0.5)');
      targetCtx.lineWidth = 1.5;
      targetCtx.stroke();

      // Top Rim Specular Glint
      targetCtx.fillStyle = 'rgba(255, 255, 255, 0.18)';
      targetCtx.fillRect(cx - badgeW / 2 + 6, topY + 1.5, badgeW - 12, 1);

      // Corner HUD Bracket Accents
      targetCtx.fillStyle = nodeColor;
      // Top-Left & Bottom-Left
      targetCtx.fillRect(cx - badgeW / 2, topY, 4, 2);
      targetCtx.fillRect(cx - badgeW / 2, topY, 2, 4);
      targetCtx.fillRect(cx - badgeW / 2, topY + badgeH - 2, 4, 2);
      targetCtx.fillRect(cx - badgeW / 2, topY + badgeH - 4, 2, 4);
      // Top-Right & Bottom-Right
      targetCtx.fillRect(cx + badgeW / 2 - 4, topY, 4, 2);
      targetCtx.fillRect(cx + badgeW / 2 - 2, topY, 2, 4);
      targetCtx.fillRect(cx + badgeW / 2 - 4, topY + badgeH - 2, 4, 2);
      targetCtx.fillRect(cx + badgeW / 2 - 2, topY + badgeH - 4, 2, 4);

      // D. Left Status Title with Dynamic Rotating Sci-Fi Phrases & Animated Pulsing Chevrons
      targetCtx.textBaseline = 'middle';
      targetCtx.font = 'bold 9.5px "Orbitron", "Rajdhani", sans-serif';
      targetCtx.textAlign = 'left';

      const startX = cx - badgeW / 2 + 10;
      const centerY = topY + 11;
      const triAlpha1 = pulseBit ? 1.0 : 0.4;
      const triAlpha2 = pulseBit ? 0.4 : 1.0;

      if (battlefield.isTerritoryLocked) {
        const lockTeam = battlefield.territoryLockedTeam;
        const isOwnerWest = lockTeam === 'western';
        const isOwnerEast = lockTeam === 'eastern';
        targetCtx.fillStyle = isOwnerWest ? '#00f0ff' : (isOwnerEast ? '#ff003c' : '#ef4444');
        targetCtx.globalAlpha = 1.0;
        const phrase = 'LOCKED (CLAIMING PILLAR)';
        targetCtx.fillText(phrase, startX, centerY);
        const phraseW = getTerritoryPhraseWidth(ctx, phrase);

        // Crisp vector padlock icon next to text (no emojis)
        const iconX = startX + phraseW + 8;
        const iconY = centerY;
        targetCtx.save();
        targetCtx.strokeStyle = targetCtx.fillStyle;
        targetCtx.lineWidth = 1.2;
        targetCtx.beginPath();
        targetCtx.arc(iconX, iconY - 3, 2.5, Math.PI, 0);
        targetCtx.lineTo(iconX + 2.5, iconY);
        targetCtx.moveTo(iconX - 2.5, iconY - 3);
        targetCtx.lineTo(iconX - 2.5, iconY);
        targetCtx.stroke();
        targetCtx.fillRect(iconX - 3.5, iconY, 7, 5);
        targetCtx.restore();
      } else if (isWestBonus) {
        targetCtx.fillStyle = '#00f0ff';
        const phrase = TERRITORY_WEST_PHRASES[phraseIdx % TERRITORY_WEST_PHRASES.length];
        targetCtx.globalAlpha = 1.0;
        targetCtx.fillText(phrase, startX, centerY);
        const phraseW = getTerritoryPhraseWidth(ctx, phrase);
        targetCtx.globalAlpha = triAlpha1;
        drawFilledTriangleRight(targetCtx, startX + phraseW + 7, centerY);
        targetCtx.globalAlpha = triAlpha2;
        drawFilledTriangleRight(targetCtx, startX + phraseW + 15, centerY);
        targetCtx.globalAlpha = 1.0;
      } else if (isEastBonus) {
        targetCtx.fillStyle = '#ff003c';
        const phrase = TERRITORY_EAST_PHRASES[phraseIdx % TERRITORY_EAST_PHRASES.length];
        targetCtx.globalAlpha = triAlpha2;
        drawFilledTriangleLeft(targetCtx, startX + 4, centerY);
        targetCtx.globalAlpha = triAlpha1;
        drawFilledTriangleLeft(targetCtx, startX + 12, centerY);
        targetCtx.globalAlpha = 1.0;
        targetCtx.fillText(phrase, startX + 19, centerY);
      } else {
        targetCtx.fillStyle = '#facc15';
        const phrase = TERRITORY_NEUTRAL_PHRASES[phraseIdx % TERRITORY_NEUTRAL_PHRASES.length];
        targetCtx.globalAlpha = 1.0;
        targetCtx.fillText(phrase, startX, centerY);
      }

      // E. Right Highlighted Gamified Percentage Pill
      const tagW = 78;
      const tagH = 17;
      const tagX = cx + badgeW / 2 - tagW - 6;
      const tagY = topY + 4.5;

      // Tag Container Pill
      targetCtx.fillStyle = isWestBonus ? 'rgba(0, 240, 255, 0.18)' : (isEastBonus ? 'rgba(255, 0, 60, 0.18)' : 'rgba(250, 204, 21, 0.14)');
      targetCtx.beginPath();
      targetCtx.roundRect(tagX, tagY, tagW, tagH, 4);
      targetCtx.fill();
      targetCtx.strokeStyle = isWestBonus ? '#00f0ff' : (isEastBonus ? '#ff003c' : '#facc15');
      targetCtx.lineWidth = 1;
      targetCtx.stroke();

      // Tag Text: Bold Percentage
      targetCtx.textAlign = 'center';
      targetCtx.font = '900 11px "Orbitron", "Rajdhani", sans-serif';
      if (isWestBonus) {
        targetCtx.fillStyle = '#38bdf8';
        targetCtx.fillText(bonusGasStr, tagX + tagW / 2, tagY + tagH / 2 + 0.5);
      } else if (isEastBonus) {
        targetCtx.fillStyle = '#fb7185';
        targetCtx.fillText(bonusGasStr, tagX + tagW / 2, tagY + tagH / 2 + 0.5);
      } else {
        targetCtx.fillStyle = '#94a3b8';
        targetCtx.fillText('0.0%', tagX + tagW / 2, tagY + tagH / 2 + 0.5);
      }

      // F. Integrated Micro Tug-of-War Gauge (Bottom Edge of Badge)
      const gaugeW = badgeW - 20;
      const gaugeH = 2.5;
      const gaugeX = cx - gaugeW / 2;
      const gaugeY = topY + badgeH - 4;

      targetCtx.fillStyle = '#0f172a';
      targetCtx.fillRect(gaugeX, gaugeY, gaugeW, gaugeH);

      // Blue West Bar
      targetCtx.fillStyle = '#00f0ff';
      targetCtx.fillRect(gaugeX, gaugeY, gaugeW * westFraction, gaugeH);

      // Red East Bar
      targetCtx.fillStyle = '#ff003c';
      targetCtx.fillRect(gaugeX + gaugeW * westFraction, gaugeY, gaugeW * (1 - westFraction), gaugeH);

      // White Center Divider Notch
      targetCtx.fillStyle = '#ffffff';
      targetCtx.fillRect(gaugeX + gaugeW * westFraction - 1, gaugeY - 1, 2, gaugeH + 2);
    };

    if (useBadgeCache) {
      if (this._territoryBadgeKey !== badgeStateKey) {
        this._territoryBadgeKey = badgeStateKey;
        const bCtx = this._territoryBadgeCtx;
        bCtx.save();
        bCtx.setTransform(cacheScale, 0, 0, cacheScale, 0, 0);
        bCtx.clearRect(0, 0, cacheLogicalW, cacheLogicalH);
        drawBadgeContents(bCtx, cacheLogicalW / 2, badgePadY);
        bCtx.restore();
      }
      ctx.drawImage(
        this._territoryBadgeCanvas,
        badgeX - badgeW / 2 - badgePadX,
        badgeY - badgePadY,
        cacheLogicalW,
        cacheLogicalH
      );
    } else {
      drawBadgeContents(ctx, badgeX, badgeY);
    }

    ctx.restore();
    ctx.restore();
  }


  // ===========================================================================
  // THE FOUNDATION MODULE - one ground floor with the vehicle garage, nothing
  // above it.
  // ===========================================================================
  // The command citadel and the separate roof deck that used to sit on top of
  // this level are gone: everything above the ground floor is a PLAYED module
  // now (js/engine/render/modules.js), not painted-on architecture.
  //
  // Its geometry IS the module cube's geometry, and that is the whole point of
  // this function: the front face is exactly MODULE_W wide, centred on
  // MODULE_ANCHOR_DX, and the roof recedes by (dir * MODULE_SKEW,
  // -MODULE_TOP_D) - the identical vanishing direction every module bakes into
  // its steel frame (buildModuleBitmap()). Without that shared projection a
  // landed module reads as a second building at a different angle balanced on
  // the first; with it, slot 0's front face and this roof line are literally
  // the same line. Anything that changes the module cube's projection has to
  // change here too.
  _drawBuildingGroundShadow(ctx, x, y, dir, topDepth = MODULE_TOP_D, skewX = MODULE_SKEW) {
    const DOCK_BLOCK_W = 88;
      const groundY = y + 26;
      const dockX = dir === 1 ? x - 40 : x + 40 - DOCK_BLOCK_W;
      const dockFlankX = dir === 1 ? dockX + DOCK_BLOCK_W : dockX;

      // 1. SCHLAGSCHATTEN FUNDAMENT-DOCK (Direkte Grundfläche in Fluchtperspektive)
      const rearY = groundY - topDepth;
      const fX1 = dockX;
      const fX2 = dockFlankX;
      const rX1 = dockX + dir * skewX;
      const rX2 = dockFlankX + dir * skewX;

      // A. Sanfter Umgebungs-Schatten Fundament (Penumbra)
      ctx.fillStyle = 'rgba(2, 6, 23, 0.35)';
      ctx.beginPath();
      ctx.moveTo(fX1 - dir * 6, groundY + 4);
      ctx.lineTo(fX2 + dir * 6, groundY + 4);
      ctx.lineTo(rX2 + dir * 10, rearY - 4);
      ctx.lineTo(rX1 - dir * 8, rearY - 4);
      ctx.closePath();
      ctx.fill();

      // B. Baukörper-Schlagschatten Fundament
      ctx.fillStyle = 'rgba(2, 6, 23, 0.70)';
      ctx.beginPath();
      ctx.moveTo(fX1 - dir * 2, groundY + 1.5);
      ctx.lineTo(fX2 + dir * 2, groundY + 1.5);
      ctx.lineTo(rX2 + dir * 5, rearY - 1);
      ctx.lineTo(rX1 - dir * 3, rearY - 1);
      ctx.closePath();
      ctx.fill();

      // C. Direkte AO-Kontaktkante am Fundament-Boden
      ctx.fillStyle = 'rgba(1, 3, 10, 0.98)';
      ctx.beginPath();
      ctx.moveTo(fX1, groundY);
      ctx.lineTo(fX2, groundY);
      ctx.lineTo(rX2, rearY);
      ctx.lineTo(rX1, rearY);
      ctx.closePath();
      ctx.fill();
  }

  _drawCannonTurntableShadow(ctx, mountX, mountY, dir) {
      // 2. SCHLAGSCHATTEN RUNDE KANONEN-DREHSCHEIBE (12% kleiner, unverändert von Gebäude-Skalierung)
      ctx.save();
      ctx.translate(mountX, mountY);
      ctx.scale(0.88, 0.88);
      ctx.translate(-mountX, -mountY);

      // A. Weicher Bodenschatten
      ctx.fillStyle = 'rgba(2, 6, 23, 0.55)';
      ctx.beginPath();
      ctx.ellipse(mountX + dir * 3, mountY + 3, 24, 10.5, 0, 0, Math.PI * 2);
      ctx.fill();

      // B. Tiefer Kontaktschatten
      ctx.fillStyle = 'rgba(1, 3, 10, 0.95)';
      ctx.beginPath();
      ctx.ellipse(mountX, mountY + 1.5, 21.5, 9.2, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
  }

  _drawBunkerPlatform(ctx, base, x, y, dir, topDepth = MODULE_TOP_D, skewX = MODULE_SKEW, mainColor, glowColor, t, isLow) {
    const team = base.team;
    const recoilTimer = base.recoilTimer || 0;
    const enableGlow = !isLow;
    const MOD_W = MODULE_W;
    const MOD_H = MODULE_H;
    const DOCK_BLOCK_W = 88;
    const DOCK_BLOCK_H = 65;
      const topLight = team === 'western' ? '#8a9ab0' : '#c2410c';
      const frontLight = team === 'western' ? '#334155' : '#6b250e';
      const frontMid = team === 'western' ? '#728294' : '#a05238'; // Basisfarbe des Fundaments: Jetzt HELLGRAU (#728294)
      const frontDark = team === 'western' ? '#1e293b' : '#3d1206';
      const sideDark = team === 'western' ? '#263342' : '#3a1005';
      const sideShade = team === 'western' ? '#4a586a' : '#5c2414'; // Seitenwand im Schatten der hellgrauen Wand
      const highlight = team === 'western' ? '#cbd5e1' : '#fdba74';
      const specLine = '#f8fafc';
      const teamGlow = team === 'western' ? '#00f0ff' : '#ff0055';
      const outline = '#0a0f18';

      // Vertauschte Metallfarben: Panzertor & Dachbettung nun dunkel, Wand nun hellgrau
      const doorSlatBase = team === 'western' ? '#334155' : '#541c0b';
      const doorSlatLight = team === 'western' ? '#475569' : '#782a12';
      const doorSlatDark = team === 'western' ? '#141a24' : '#2d0e05';
      const roofBedColor = team === 'western' ? '#334155' : '#541c0b';
      const roofBedRim = team === 'western' ? '#475569' : '#782a12';

      // 1. Geometrie & Höhenkoten (V4: 40% höheres Fundament = 62px)
      const groundY = y + 26;
      const dockH = DOCK_BLOCK_H; // 62px (44px * 1.40 = 61.6 -> 62px)
      const dockY = groundY - dockH; // y - 36px (Oberkante des erhöhten Fundaments)
      const dockX = dir === 1 ? x - 40 : x + 40 - DOCK_BLOCK_W;
      const dockFlankX = dir === 1 ? dockX + DOCK_BLOCK_W : dockX;

      // -------------------------------------------------------------
      // 2. FUNDAMENT-KÖRPER (DOCK-BLOCK — 62px hoch, massiv & direkt auf Boden)
      // -------------------------------------------------------------

      // A. Seitenwand in Fluchtperspektive mit Tiefenshading (Feature 6)
      const flankGrad = this._cachedGradient('bunkerFlank:' + team, () => {
        const g = ctx.createLinearGradient(dockFlankX, dockY, dockFlankX, groundY);
        if (team === 'western') {
          g.addColorStop(0, '#536375');
          g.addColorStop(1, '#3e4a58');
        } else {
          g.addColorStop(0, '#662816');
          g.addColorStop(1, '#47180b');
        }
        return g;
      });
      ctx.fillStyle = flankGrad;
      ctx.beginPath();
      ctx.moveTo(dockFlankX, dockY);
      ctx.lineTo(dockFlankX + dir * skewX, dockY - topDepth);
      ctx.lineTo(dockFlankX + dir * skewX, groundY - topDepth);
      ctx.lineTo(dockFlankX, groundY);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = outline;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Oberer 45°-Abschrägungsstreifen an der Seitenwand (Beveled Top Chamfer)
      ctx.fillStyle = highlight;
      ctx.beginPath();
      ctx.moveTo(dockFlankX, dockY);
      ctx.lineTo(dockFlankX + dir * skewX, dockY - topDepth);
      ctx.lineTo(dockFlankX + dir * skewX, dockY - topDepth + 3);
      ctx.lineTo(dockFlankX, dockY + 3);
      ctx.closePath();
      ctx.fill();

      // -------------------------------------------------------------
      // GEPANZERTES BEOBACHTUNGSFENSTER (Armored Bunker Viewport an der Flanke)
      // -------------------------------------------------------------
      // Flanken-Einfassung in perspektivischer Flucht (ersetzt die alten Nieten/Metallplatte):
      const winD1 = 0.16;
      const winD2 = 0.82;
      const winX1 = dockFlankX + dir * (skewX * winD1);
      const winX2 = dockFlankX + dir * (skewX * winD2);

      // Fensterhöhe im oberen Bereich der Flanke (Kommandobrücke / Schießscharte)
      const winTopRatio = 0.20;
      const winBotRatio = 0.54;
      const wY_top1 = (dockY - topDepth * winD1) + dockH * winTopRatio;
      const wY_top2 = (dockY - topDepth * winD2) + dockH * winTopRatio;
      const wY_bot1 = (dockY - topDepth * winD1) + dockH * winBotRatio;
      const wY_bot2 = (dockY - topDepth * winD2) + dockH * winBotRatio;

      // 1. Schwerer äußerer Panzerrahmen (Abschrägung / Panzerschürze)
      ctx.fillStyle = '#060a12';
      ctx.beginPath();
      ctx.moveTo(winX1 - dir * 1.5, wY_top1 - 2);
      ctx.lineTo(winX2 + dir * 1.5, wY_top2 - 2);
      ctx.lineTo(winX2 + dir * 1.5, wY_bot2 + 2);
      ctx.lineTo(winX1 - dir * 1.5, wY_bot1 + 2);
      ctx.closePath();
      ctx.fill();

      // 2. Facettierter Panzerstahl-Rahmen
      const frameSteel = team === 'western' ? '#1e293b' : '#3d1206';
      const frameRim = team === 'western' ? '#475569' : '#852d11';
      ctx.fillStyle = frameSteel;
      ctx.beginPath();
      ctx.moveTo(winX1, wY_top1);
      ctx.lineTo(winX2, wY_top2);
      ctx.lineTo(winX2, wY_bot2);
      ctx.lineTo(winX1, wY_bot1);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = frameRim;
      ctx.lineWidth = 1;
      ctx.stroke();

      // Obere Glanzkante des Rahmens
      ctx.strokeStyle = specLine;
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(winX1, wY_top1);
      ctx.lineTo(winX2, wY_top2);
      ctx.stroke();

      // 4 Sechskant-Schwerlastschrauben an den Ecken des Rahmens
      [
        { x: winX1 + dir * 1.8, y: wY_top1 + 2 },
        { x: winX2 - dir * 1.8, y: wY_top2 + 2 },
        { x: winX1 + dir * 1.8, y: wY_bot1 - 2 },
        { x: winX2 - dir * 1.8, y: wY_bot2 - 2 }
      ].forEach(b => {
        ctx.fillStyle = '#0a0f18';
        ctx.fillRect(b.x - 1, b.y - 1, 2, 2);
        ctx.fillStyle = '#cbd5e1';
        ctx.fillRect(b.x - 0.5, b.y - 0.5, 1, 1);
      });

      // 3. Tief eingelegtes Panzerglas (Ballistisches Mehrschichtglas)
      const gD1 = 0.22;
      const gD2 = 0.76;
      const gx1 = dockFlankX + dir * (skewX * gD1);
      const gx2 = dockFlankX + dir * (skewX * gD2);
      const gy_top1 = (dockY - topDepth * gD1) + dockH * (winTopRatio + 0.05);
      const gy_top2 = (dockY - topDepth * gD2) + dockH * (winTopRatio + 0.05);
      const gy_bot1 = (dockY - topDepth * gD1) + dockH * (winBotRatio - 0.05);
      const gy_bot2 = (dockY - topDepth * gD2) + dockH * (winBotRatio - 0.05);

      // Dunkle Hohlkehle / Dichtungsfalz
      ctx.fillStyle = '#02060d';
      ctx.beginPath();
      ctx.moveTo(gx1, gy_top1);
      ctx.lineTo(gx2, gy_top2);
      ctx.lineTo(gx2, gy_bot2);
      ctx.lineTo(gx1, gy_bot1);
      ctx.closePath();
      ctx.fill();

      // Panzerglas-Scheibe mit bläulicher / feuriger Tiefe
      const glassGrad = this._cachedGradient('bunkerGlass:' + team, () => {
        const g = ctx.createLinearGradient(gx1, gy_top1, gx2, gy_bot2);
        if (team === 'western') {
          g.addColorStop(0, '#0c2d48');
          g.addColorStop(0.5, '#083344');
          g.addColorStop(1, '#021824');
        } else {
          g.addColorStop(0, '#451a03');
          g.addColorStop(0.5, '#290e02');
          g.addColorStop(1, '#1c0701');
        }
        return g;
      });
      ctx.fillStyle = glassGrad;
      ctx.beginPath();
      ctx.moveTo(gx1 + 0.5, gy_top1 + 0.5);
      ctx.lineTo(gx2 - 0.5, gy_top2 + 0.5);
      ctx.lineTo(gx2 - 0.5, gy_bot2 - 0.5);
      ctx.lineTo(gx1 + 0.5, gy_bot1 - 0.5);
      ctx.closePath();
      ctx.fill();

      // Innenraum-Glow / Gefechtsbeleuchtung
      if (enableGlow) {
        ctx.fillStyle = team === 'western' ? 'rgba(0, 240, 255, 0.28)' : 'rgba(255, 80, 20, 0.28)';
        ctx.beginPath();
        ctx.moveTo(gx1 + 1, gy_top1 + 1);
        ctx.lineTo(gx2 - 1, gy_top2 + 1);
        ctx.lineTo(gx2 - 1, gy_bot2 - 1);
        ctx.lineTo(gx1 + 1, gy_bot1 - 1);
        ctx.closePath();
        ctx.fill();
      }

      // Horizontale Panzer-Scharte / Sehschlitz-Gitterlinie
      const my1 = (gy_top1 + gy_bot1) / 2;
      const my2 = (gy_top2 + gy_bot2) / 2;
      ctx.strokeStyle = '#050a14';
      ctx.lineWidth = 1.8;
      ctx.beginPath();
      ctx.moveTo(gx1, my1);
      ctx.lineTo(gx2, my2);
      ctx.stroke();

      ctx.strokeStyle = team === 'western' ? '#38bdf8' : '#fb923c';
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.moveTo(gx1 + 1, my1);
      ctx.lineTo(gx2 - 1, my2);
      ctx.stroke();

      // Realismus 5: Mehrschicht-Panzerglas-Reflexionsband & Glanzkante
      ctx.fillStyle = 'rgba(255, 255, 255, 0.14)';
      ctx.beginPath();
      ctx.moveTo(gx1 + dir * (gx2 - gx1) * 0.16, gy_top1 + 1);
      ctx.lineTo(gx1 + dir * (gx2 - gx1) * 0.38, gy_top1 + 1);
      ctx.lineTo(gx1 + dir * (gx2 - gx1) * 0.56, gy_bot1 - 1);
      ctx.lineTo(gx1 + dir * (gx2 - gx1) * 0.34, gy_bot1 - 1);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
      ctx.lineWidth = 1.0;
      ctx.beginPath();
      ctx.moveTo(gx1 + dir * (gx2 - gx1) * 0.22, gy_top1 + 1);
      ctx.lineTo(gx1 + dir * (gx2 - gx1) * 0.50, gy_bot1 - 1);
      ctx.stroke();

      // Vertikale Versorgungsleitung an der Seitenflanke (verstärkt)
      const pipeSideX = dockFlankX + dir * (skewX * 0.90);
      const pipeSideY1 = dockY - topDepth * 0.90 + 5;
      const pipeSideY2 = groundY - topDepth * 0.90;
      ctx.strokeStyle = '#0a0f18';
      ctx.lineWidth = 3.5;
      ctx.beginPath();
      ctx.moveTo(pipeSideX, pipeSideY1);
      ctx.lineTo(pipeSideX, pipeSideY2);
      ctx.stroke();
      ctx.strokeStyle = '#64748b';
      ctx.lineWidth = 2;
      ctx.stroke();

      // B. DACH-DOCKINGAUFNAHME (Eindeutige Aufnahme-Plattform für Module!)
      // Nutzt die gewonnene Höhe: Hellgraue Oberfläche, L-Markierungen für quadratische Mitte, Führungspyramiden & Bus
      const roofX = dockX + (DOCK_BLOCK_W - MOD_W) / 2;
      const roofY = dockY;

      // 1. Erhöhte Docking-Plattform & umlaufender Montage-Kragen
      ctx.fillStyle = frontMid; // Kragen in Fundament-Hellgrau (#728294)
      ctx.beginPath();
      ctx.moveTo(roofX, roofY);
      ctx.lineTo(roofX + MOD_W, roofY);
      ctx.lineTo(roofX + MOD_W + dir * skewX, roofY - topDepth);
      ctx.lineTo(roofX + dir * skewX, roofY - topDepth);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = outline;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Tief eingefasste Modul-Aufnahmebettung (Docking Socket Bed)
      ctx.fillStyle = '#060910';
      ctx.beginPath();
      ctx.moveTo(roofX + 2, roofY - 1);
      ctx.lineTo(roofX + MOD_W - 2, roofY - 1);
      ctx.lineTo(roofX + MOD_W - 2 + dir * (skewX * 0.90), roofY - topDepth + 2);
      ctx.lineTo(roofX + 2 + dir * (skewX * 0.90), roofY - topDepth + 2);
      ctx.closePath();
      ctx.fill();

      // Dunkles Stahlgrau der Plattform-Bettung (ehemals hellgrau, jetzt dunkel #334155)
      ctx.fillStyle = roofBedColor;
      ctx.beginPath();
      ctx.moveTo(roofX + 3.5, roofY - 2);
      ctx.lineTo(roofX + MOD_W - 3.5, roofY - 2);
      ctx.lineTo(roofX + MOD_W - 3.5 + dir * (skewX * 0.86), roofY - topDepth + 3);
      ctx.lineTo(roofX + 3.5 + dir * (skewX * 0.86), roofY - topDepth + 3);
      ctx.closePath();
      ctx.fill();

      // Feine Glanzkante an der Aufnahme-Stufe
      ctx.strokeStyle = roofBedRim;
      ctx.lineWidth = 1.0;
      ctx.beginPath();
      ctx.moveTo(roofX + 3.5, roofY - 2);
      ctx.lineTo(roofX + MOD_W - 3.5, roofY - 2);
      ctx.stroke();

      // Gelb-schwarze Gefahren-Schraffur an den Plattform-Außenkanten (Docking Perimeter)
      for (let s = 8; s < MOD_W - 10; s += 12) {
        ctx.fillStyle = '#ca8a04';
        ctx.beginPath();
        ctx.moveTo(roofX + s, roofY - 0.5);
        ctx.lineTo(roofX + s + 4, roofY - 0.5);
        ctx.lineTo(roofX + s + 6 + dir * (skewX * 0.08), roofY - topDepth * 0.08);
        ctx.lineTo(roofX + s + 2 + dir * (skewX * 0.08), roofY - topDepth * 0.08);
        ctx.closePath();
        ctx.fill();
      }

      // 2. QUADRATISCHE ZENTRAL-MARKIERUNG (4 L-Markierungen zeichnen die Mitte exakt quadratisch an)
      const midX = roofX + MOD_W / 2;
      const sqW = 32; // Breite des quadratischen Zentrums in Pixeln
      const dFront = 0.28; // Tiefenlinie vorn
      const dBack = 0.72;  // Tiefenlinie hinten
      const armX = 6;      // Schenkellänge der L-Markierung in X
      const armD = 0.11;   // Schenkellänge in die Tiefe

      const pFL_x = midX - sqW / 2 + dir * (skewX * dFront);
      const pFL_y = roofY - topDepth * dFront;

      const pFR_x = midX + sqW / 2 + dir * (skewX * dFront);
      const pFR_y = roofY - topDepth * dFront;

      const pBL_x = midX - sqW / 2 + dir * (skewX * dBack);
      const pBL_y = roofY - topDepth * dBack;

      const pBR_x = midX + sqW / 2 + dir * (skewX * dBack);
      const pBR_y = roofY - topDepth * dBack;

      // Dezente quadratische Verbindungs-Strichellinie (Führungslinie)
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.45)';
      ctx.lineWidth = 0.8;
      ctx.setLineDash([2.5, 2.5]);
      ctx.beginPath();
      ctx.moveTo(pFL_x, pFL_y);
      ctx.lineTo(pFR_x, pFR_y);
      ctx.lineTo(pBR_x, pBR_y);
      ctx.lineTo(pBL_x, pBL_y);
      ctx.closePath();
      ctx.stroke();
      ctx.setLineDash([]);

      // Funktion zum Zeichnen einer präzisen L-Markierung mit dunkler Kante & gelbem Kern
      function drawSquareL(x, y, dxArm, dDepth) {
        // Dunkle Schattierung
        ctx.strokeStyle = '#0a0f18';
        ctx.lineWidth = 2.4;
        ctx.beginPath();
        ctx.moveTo(x + dxArm, y);
        ctx.lineTo(x, y);
        ctx.lineTo(x + dir * (skewX * dDepth), y - topDepth * dDepth);
        ctx.stroke();

        // Leuchtend gelbe Passermarke
        ctx.strokeStyle = '#facc15';
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(x + dxArm, y);
        ctx.lineTo(x, y);
        ctx.lineTo(x + dir * (skewX * dDepth), y - topDepth * dDepth);
        ctx.stroke();
      }

      // 4 L-Markierungen an den 4 Ecken des quadratischen Zentrums:
      drawSquareL(pFL_x, pFL_y, armX, armD);   // Vorne-Links: Schenkel nach rechts & hinten
      drawSquareL(pFR_x, pFR_y, -armX, armD);  // Vorne-Rechts: Schenkel nach links & hinten
      drawSquareL(pBL_x, pBL_y, armX, -armD);  // Hinten-Links: Schenkel nach rechts & vorn
      drawSquareL(pBR_x, pBR_y, -armX, -armD); // Hinten-Rechts: Schenkel nach links & vorn

      // 3. ZENTRALE HOCHLEISTUNGS-KOPPLUNGEN (Power- & Coolant-Ports im quadratischen Zentrum)
      const socketMidX = midX + dir * (skewX * 0.50);
      const socketMidY = roofY - topDepth * 0.50;

      // Kupfer-Gold Hochstrom-Kontaktschienen
      ctx.fillStyle = '#0a0f18';
      ctx.fillRect(socketMidX - 13, socketMidY - 3, 26, 6);
      ctx.fillStyle = '#d97706';
      ctx.fillRect(socketMidX - 11, socketMidY - 2, 22, 4);
      ctx.fillStyle = '#fde047';
      ctx.fillRect(socketMidX - 9, socketMidY - 1.5, 18, 1.2);

      // Hydraulik- / Kühlmittel-Schnellverschluss-Buchsen
      [-6, 6].forEach(ox => {
        ctx.fillStyle = '#0a0f18';
        ctx.beginPath();
        ctx.arc(socketMidX + ox, socketMidY, 2.8, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#64748b';
        ctx.beginPath();
        ctx.arc(socketMidX + ox, socketMidY, 1.6, 0, Math.PI * 2);
        ctx.fill();
      });

      // 3. 4 MASSIVE ECK-ZENTRIERKEILE (Heavy Alignment Guide Wedges)
      // Visualisiert sofort: Hier senkt sich das Modul hinein und rastet exakt ein!
      const cornerLugs = [
        { u: 2, d: 0.06 }, { u: MOD_W - 9, d: 0.06 },
        { u: 2, d: 0.88 }, { u: MOD_W - 9, d: 0.88 }
      ];
      cornerLugs.forEach(cl => {
        const lx = roofX + cl.u + dir * (skewX * cl.d);
        const ly = roofY - topDepth * cl.d;

        // Keilsockel
        ctx.fillStyle = '#0a0f18';
        ctx.fillRect(lx - 1, ly - 7, 8, 8);
        ctx.fillStyle = frontMid;
        ctx.fillRect(lx, ly - 6, 6, 6);

        // Schräge Zentrier-Führungsflanke
        ctx.fillStyle = '#cbd5e1';
        ctx.beginPath();
        ctx.moveTo(lx, ly - 6);
        ctx.lineTo(lx + 6, ly - 6);
        ctx.lineTo(lx + 4, ly - 1);
        ctx.lineTo(lx + 1, ly - 1);
        ctx.closePath();
        ctx.fill();

        // Leuchtende gelbe Zentrier-Spitze / Laser-Passermarke
        ctx.fillStyle = '#facc15';
        ctx.fillRect(lx + 1.5, ly - 8.5, 3, 2.5);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(lx + 2, ly - 8, 2, 1);
      });

      // 4. HYDRAULISCHE VERRIEGELUNGS-KLEMMEN (Locking Jaws an Vorder- und Hinterkante)
      [-14, 14].forEach(cxOffset => {
        const jx = roofX + MOD_W / 2 + cxOffset;
        const jy = roofY;
        ctx.fillStyle = '#0a0f18';
        ctx.fillRect(jx - 3, jy - 5, 6, 6);
        ctx.fillStyle = '#475569';
        ctx.fillRect(jx - 2, jy - 4, 4, 4);
        ctx.fillStyle = '#f8fafc'; // Chrom-Zylinder der Klemme
        ctx.fillRect(jx - 1, jy - 6, 2, 2.5);
      });

      // 5. L-förmige Industrie-Passermarken an den 4 Ecken des Modul-Docks
      const dockPassers = [
        { x: roofX + 1, y: roofY - 1, dirX: 1, dirY: -1 },
        { x: roofX + MOD_W - 1, y: roofY - 1, dirX: -1, dirY: -1 },
        { x: roofX + dir * skewX + 1, y: roofY - topDepth + 1, dirX: 1, dirY: 1 },
        { x: roofX + MOD_W + dir * skewX - 1, y: roofY - topDepth + 1, dirX: -1, dirY: 1 }
      ];
      ctx.strokeStyle = '#facc15';
      ctx.lineWidth = 1.2;
      dockPassers.forEach(dp => {
        ctx.beginPath();
        ctx.moveTo(dp.x, dp.y + dp.dirY * 5);
        ctx.lineTo(dp.x, dp.y);
        ctx.lineTo(dp.x + dp.dirX * 6, dp.y);
        ctx.stroke();
      });

      // C. FRONTWAND DES DOCK-BLOCKS (65px HOCH MIT BUNKER-TOR & SINUS-MONITOR)
      const wallH = dockH; // 65px

      // 1. Wand-Hauptkörper mit subtilem vertikalem Tiefenshading (Feature 6)
      const wallGrad = this._cachedGradient('bunkerWall:' + team, () => {
        const g = ctx.createLinearGradient(dockX, dockY, dockX, dockY + wallH);
        if (team === 'western') {
          g.addColorStop(0, '#7c8e9f');   // Oberes Himmelslicht / Reflektion
          g.addColorStop(0.40, '#728294'); // Mittlerer Basiston
          g.addColorStop(1, '#566677');   // Bodenverschattung / Ambient Falloff
        } else {
          g.addColorStop(0, '#b86348');
          g.addColorStop(0.40, '#a05238');
          g.addColorStop(1, '#783520');
        }
        return g;
      });
      ctx.fillStyle = wallGrad;
      ctx.fillRect(dockX, dockY, DOCK_BLOCK_W, wallH);
      ctx.strokeStyle = outline;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(dockX, dockY, DOCK_BLOCK_W, wallH);

      // Feature 4: Horizontale Panzerplatten-Fugen (Geschweißte Panzerplatten-Schichtung)
      [dockY + 16, dockY + 38].forEach((seamY, sIdx) => {
        // Dunkle Fuge / Schweißnaht-Schatten
        ctx.fillStyle = team === 'western' ? '#263342' : '#3d1206';
        ctx.fillRect(dockX + 0.5, seamY, DOCK_BLOCK_W - 1.0, 1.0);
        // Feine Lichtkante an der unteren Plattenkante
        ctx.fillStyle = team === 'western' ? '#94a3b8' : '#c27d65';
        ctx.fillRect(dockX + 0.5, seamY + 1.0, DOCK_BLOCK_W - 1.0, 0.6);

        // Realismus 2: Punktuelle Schweißnaht-Oxidation / Anlauffarben
        const rustX1 = dockX + (sIdx === 0 ? 24 : 58);
        const rustGrad = this._cachedGradient('bunkerRust:' + team + ':' + sIdx, () => {
          const g = ctx.createLinearGradient(rustX1, seamY, rustX1 + 8, seamY);
          g.addColorStop(0, 'rgba(180, 83, 9, 0.0)');
          g.addColorStop(0.5, team === 'western' ? 'rgba(180, 83, 9, 0.22)' : 'rgba(234, 88, 12, 0.22)');
          g.addColorStop(1, 'rgba(180, 83, 9, 0.0)');
          return g;
        });
        ctx.fillStyle = rustGrad;
        ctx.fillRect(rustX1, seamY - 0.5, 8, 2.0);
      });

      // Realismus 2: Regenwasser- & Schmutzablauf-Schlieren unter dem Gesims (Rain Runoff)
      [dockX + 16, dockX + 46, dockX + 74].forEach((gx, idx) => {
        const grimeLen = 8 + (idx % 2) * 5;
        const grime = this._cachedGradient('bunkerGrime:' + team + ':' + idx, () => {
          const g = ctx.createLinearGradient(gx, dockY + 2.5, gx, dockY + 2.5 + grimeLen);
          g.addColorStop(0, 'rgba(8, 12, 20, 0.26)');
          g.addColorStop(0.7, 'rgba(8, 12, 20, 0.10)');
          g.addColorStop(1, 'rgba(8, 12, 20, 0.0)');
          return g;
        });
        ctx.fillStyle = grime;
        ctx.fillRect(gx, dockY + 2.5, 1.2, grimeLen);
      });

      // Realismus 1: Militärische Stencil-Sektorkennung ("SEC-04")
      const stencilX = dir === 1 ? dockX + 8 : dockX + DOCK_BLOCK_W - 32;
      const stencilY = dockY + 11.5;
      ctx.save();
      ctx.font = 'bold 7px "Share Tech Mono", monospace';
      ctx.fillStyle = 'rgba(241, 245, 249, 0.42)'; // Verwitterter Off-White Anstrich
      ctx.fillText('SEC-04', stencilX, stencilY);
      // Stencil-Stege (typische Schablonen-Unterbrechungen in den Ziffern)
      ctx.fillStyle = team === 'western' ? '#728294' : '#a05238';
      ctx.fillRect(stencilX + 3.4, stencilY - 5.5, 0.6, 6);
      ctx.fillRect(stencilX + 14.4, stencilY - 5.5, 0.6, 6);
      ctx.restore();

      // Realismus 3: Starre Wand-Leitung (Rigid Steel Conduit) mit Omega-Schellen
      const conduitY = dockY + 7.5;
      const condX1 = dir === 1 ? dockX + 48 : dockX + 8;
      const condX2 = dir === 1 ? dockX + DOCK_BLOCK_W - 7 : dockX + 40;
      const condW = condX2 - condX1;
      // Rohrschatten & verzinkter Stahlkörper
      ctx.fillStyle = '#080c14';
      ctx.fillRect(condX1, conduitY + 1.2, condW, 0.8);
      ctx.fillStyle = team === 'western' ? '#334155' : '#4d190c';
      ctx.fillRect(condX1, conduitY, condW, 1.4);
      ctx.fillStyle = team === 'western' ? '#94a3b8' : '#fed7aa';
      ctx.fillRect(condX1, conduitY, condW, 0.5);

      // Omega-Befestigungsschellen mit Verschraubung
      [condX1 + 6, condX1 + condW - 7].forEach(cx => {
        ctx.fillStyle = '#080c14';
        ctx.fillRect(cx - 1, conduitY - 0.8, 3, 3);
        ctx.fillStyle = team === 'western' ? '#64748b' : '#9a3412';
        ctx.fillRect(cx - 0.5, conduitY - 0.4, 2, 2.2);
        ctx.fillStyle = '#f8fafc';
        ctx.fillRect(cx, conduitY + 0.2, 1, 1);
      });

      // Kleine gusseiserne Verteilerdose (Junction Box) am Leitungsende
      const jBoxX = dir === 1 ? condX2 - 0.5 : condX1 - 4.5;
      ctx.fillStyle = '#080c14';
      ctx.fillRect(jBoxX - 0.5, conduitY - 1.5, 5, 5);
      ctx.fillStyle = team === 'western' ? '#1e293b' : '#3d1206';
      ctx.fillRect(jBoxX, conduitY - 1.0, 4, 4);
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(jBoxX + 1.5, conduitY + 0.5, 1, 1);

      // Feature 2: Auskragendes Dachgesims mit Tropfkante & Schlagschatten (Cornice Overhang)
      const corniceH = 3.5;
      const corniceOverhang = 1.5;
      const corniceX = dockX - corniceOverhang;
      const corniceW = DOCK_BLOCK_W + corniceOverhang * 2;
      const corniceY = dockY - 1.5;

      // Gesims-Grundkörper (Harter massiver Stahlstreifen)
      ctx.fillStyle = '#080c14';
      ctx.fillRect(corniceX - 0.5, corniceY - 0.5, corniceW + 1.0, corniceH + 1.0);
      ctx.fillStyle = team === 'western' ? '#334155' : '#4d190c';
      ctx.fillRect(corniceX, corniceY, corniceW, corniceH);

      // Obere helle Lichtkante des Gesimses
      ctx.fillStyle = team === 'western' ? '#cbd5e1' : '#fdba74';
      ctx.fillRect(corniceX, corniceY, corniceW, 0.9);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(corniceX + 1, corniceY, corniceW - 2, 0.4);

      // Dunkle Tropfkante (Drip Edge) an der Gesims-Unterseite
      ctx.fillStyle = '#060a12';
      ctx.fillRect(corniceX, corniceY + corniceH - 0.8, corniceW, 0.8);

      // Weicher Schlagschatten des Gesimses auf die Frontwand
      const corniceShadow = this._cachedGradient('bunkerCornice:' + team, () => {
        const g = ctx.createLinearGradient(dockX, dockY + 2.0, dockX, dockY + 6.5);
        g.addColorStop(0, 'rgba(2, 6, 23, 0.55)');
        g.addColorStop(0.5, 'rgba(2, 6, 23, 0.25)');
        g.addColorStop(1, 'rgba(2, 6, 23, 0.0)');
        return g;
      });
      ctx.fillStyle = corniceShadow;
      ctx.fillRect(dockX, dockY + 2.0, DOCK_BLOCK_W, 4.5);

      // Feature 5: Abgeschrägte Sockel-Fase an der Fußleiste (Beveled Chamfer Skirting)
      const trimH = 6.5;
      const chamferH = 2.4;
      const skirtY = groundY - trimH;

      // Äußere Kontur der Fußleiste
      ctx.fillStyle = '#080c14';
      ctx.fillRect(dockX - 1, skirtY - 0.8, DOCK_BLOCK_W + 2, trimH + 1.6);

      // 45°-Böschungs-Fase (obere Schräge) fängt direktes Licht ein
      ctx.fillStyle = team === 'western' ? '#94a3b8' : '#bd654c';
      ctx.fillRect(dockX, skirtY, DOCK_BLOCK_W, chamferH);
      // Oberer Glanzfalz auf der Fase
      ctx.fillStyle = team === 'western' ? '#e2e8f0' : '#fed7aa';
      ctx.fillRect(dockX, skirtY, DOCK_BLOCK_W, 0.8);

      // Vertikaler Sockel-Hauptteil darunter (dunklerer Strukturstahl)
      ctx.fillStyle = frontDark;
      ctx.fillRect(dockX, skirtY + chamferH, DOCK_BLOCK_W, trimH - chamferH);

      // Gehrungs-Umschwung an der rechten Ecke (3D Miter Joint zur Seitenflanke)
      if (dir === 1) {
        ctx.fillStyle = sideDark;
        ctx.beginPath();
        ctx.moveTo(dockFlankX, skirtY);
        ctx.lineTo(dockFlankX + skewX * 0.12, skirtY - topDepth * 0.12);
        ctx.lineTo(dockFlankX + skewX * 0.12, groundY - topDepth * 0.12);
        ctx.lineTo(dockFlankX, groundY);
        ctx.closePath();
        ctx.fill();

        // Diagonale Gehrungskante
        ctx.strokeStyle = '#080c14';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(dockFlankX, skirtY);
        ctx.lineTo(dockFlankX, groundY);
        ctx.stroke();
      }

      // Realismus 4: Teer-/Bitumen-Dehnungsfuge & organischer Staubschleier am Boden
      // 1. Dichte Bitumen-Dehnungsfuge (Mastic Expansion Joint)
      ctx.fillStyle = '#05070c';
      ctx.fillRect(dockX - 1.5, groundY - 0.4, DOCK_BLOCK_W + 3, 1.2);

      // 2. Erdig-staubiger Bodenschleier (Ground Dust Occlusion)
      const dustGrad = this._cachedGradient('bunkerDust:' + team, () => {
        const g = ctx.createLinearGradient(dockX, groundY - 5.5, dockX, groundY);
        g.addColorStop(0, 'rgba(45, 35, 25, 0.0)');
        g.addColorStop(1, 'rgba(45, 35, 25, 0.18)');
        return g;
      });
      ctx.fillStyle = dustGrad;
      ctx.fillRect(dockX - 1, groundY - 5.5, DOCK_BLOCK_W + 2, 5.5);

      // 3. Ganz feine hellgraue unterste Kante am Sockelfuß (wie bei der Kanone!)
      ctx.fillStyle = team === 'western' ? '#94a3b8' : '#bd654c';
      ctx.fillRect(dockX, groundY - 1.0, DOCK_BLOCK_W, 1.0);
      ctx.fillStyle = team === 'western' ? '#e2e8f0' : '#fed7aa';
      ctx.fillRect(dockX, groundY - 1.0, DOCK_BLOCK_W, 0.8);

      // Gehrungs-Umschwung der feinen untersten Kante um die Ecke auf die Flanke:
      ctx.fillStyle = team === 'western' ? '#94a3b8' : '#bd654c';
      ctx.beginPath();
      ctx.moveTo(dockFlankX, groundY - 1.0);
      ctx.lineTo(dockFlankX + dir * skewX * 0.12, groundY - 1.0 - topDepth * 0.12);
      ctx.lineTo(dockFlankX + dir * skewX * 0.12, groundY - topDepth * 0.12);
      ctx.lineTo(dockFlankX, groundY);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = team === 'western' ? '#e2e8f0' : '#fed7aa';
      ctx.beginPath();
      ctx.moveTo(dockFlankX, groundY - 1.0);
      ctx.lineTo(dockFlankX + dir * skewX * 0.12, groundY - 1.0 - topDepth * 0.12);
      ctx.lineTo(dockFlankX + dir * skewX * 0.12, groundY - 0.8 - topDepth * 0.12);
      ctx.lineTo(dockFlankX, groundY - 0.8);
      ctx.closePath();
      ctx.fill();

      // Realismus 2: Kantenabrieb an der Fußleiste (Micro Edge Wear)
      [dockX + 18, dockX + 54].forEach(wearX => {
        ctx.fillStyle = '#060a12';
        ctx.fillRect(wearX, skirtY + 0.5, 2.2, 1.2);
        ctx.fillStyle = '#e2e8f0';
        ctx.fillRect(wearX + 0.4, skirtY + 0.2, 1.2, 0.6);
      });

      // Facettierte Sechskant-Schraubenbolzen mit 3D-Lichtpunkt
      for (let fx = 6; fx < DOCK_BLOCK_W - 3; fx += 13) {
        const bx = dockX + fx;
        const by = groundY - 3.6;
        // Senkloch / Schattensockel
        ctx.fillStyle = '#060a12';
        ctx.fillRect(bx - 0.5, by - 0.5, 2.6, 2.6);
        // Bolzenkopf
        ctx.fillStyle = team === 'western' ? '#cbd5e1' : '#fcd34d';
        ctx.fillRect(bx, by, 1.8, 1.8);
        // Specular Glint
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(bx, by, 0.8, 0.8);
      }

      // =============================================================
      // 4. PANZERTOR (Blast Door / Schweres Bunkertor)
      // =============================================================
      // Sitzt auf der dem Spielfeld/Hinterland zugewandten Seite der Front
      const gateW = 41;
      const gateH = 46; // Nutzt die volle Bunkertor-Höhe bis zur Fußleiste
      const gateX = dir === 1 ? dockX + 5 : dockX + DOCK_BLOCK_W - gateW - 5;
      const gateY = groundY - trimH - gateH;

      // Realismus 1: Boden-Gefahrenzone vor dem Panzertor (Keep Clear Hazard Line)
      ctx.strokeStyle = 'rgba(202, 138, 4, 0.42)';
      ctx.lineWidth = 1.0;
      ctx.setLineDash([3, 2.5]);
      ctx.beginPath();
      ctx.moveTo(gateX - 2, groundY + 0.5);
      ctx.lineTo(gateX + gateW + 2, groundY + 0.5);
      ctx.stroke();
      ctx.setLineDash([]);

      // A. Massiver Torrahmen & Sturzbalken
      ctx.fillStyle = '#080c14';
      ctx.fillRect(gateX - 2, gateY - 5, gateW + 4, gateH + 5);
      ctx.fillStyle = '#141a24';
      ctx.fillRect(gateX - 1.5, gateY - 4.5, gateW + 3, gateH + 4.5);

      // Gelb-schwarzer Gefahren-Warnbalken über dem Torsturz
      const stripeH = 3.5;
      const stripeY = gateY - 4.5;
      ctx.fillStyle = '#0a0f18';
      ctx.fillRect(gateX, stripeY, gateW, stripeH);
      for (let sx = 0; sx < gateW; sx += 6) {
        ctx.fillStyle = '#ca8a04';
        ctx.beginPath();
        ctx.moveTo(gateX + sx, stripeY + stripeH);
        ctx.lineTo(gateX + sx + 3, stripeY);
        ctx.lineTo(gateX + sx + 5.5, stripeY);
        ctx.lineTo(gateX + sx + 2.5, stripeY + stripeH);
        ctx.closePath();
        ctx.fill();
      }

      // B. Vertiefte Toröffnung mit 3D-Laibungen & echter Wandstärke (Feature 1)
      ctx.fillStyle = '#04070d';
      ctx.fillRect(gateX, gateY, gateW, gateH);

      // Maße der 3D-Laibungen (Mauer-Ausschnittstiefe)
      const revealTop = 2.6;
      const revealLeft = 2.4;
      const revealRight = 1.8;

      // 1. Obere Sturz-Laibung (unter dem Sturzbalken, horizontal abgeschrägt)
      // Tiefes Dunkelgrau, da von oben kein Licht hinfällt
      ctx.fillStyle = '#0c131d';
      ctx.beginPath();
      ctx.moveTo(gateX, gateY);
      ctx.lineTo(gateX + gateW, gateY);
      ctx.lineTo(gateX + gateW - revealRight, gateY + revealTop);
      ctx.lineTo(gateX + revealLeft, gateY + revealTop);
      ctx.closePath();
      ctx.fill();

      // 2. Linke Wangen-Laibung: Dunkler Kernschatten (Licht von links oben wird abgefangen)
      ctx.fillStyle = '#070b12';
      ctx.beginPath();
      ctx.moveTo(gateX, gateY);
      ctx.lineTo(gateX + revealLeft, gateY + revealTop);
      ctx.lineTo(gateX + revealLeft, gateY + gateH);
      ctx.lineTo(gateX, gateY + gateH);
      ctx.closePath();
      ctx.fill();

      // 3. Rechte Wangen-Laibung: Feine Aufhellung durch Umgebungs-Reflexion
      ctx.fillStyle = '#1a2636';
      ctx.beginPath();
      ctx.moveTo(gateX + gateW, gateY);
      ctx.lineTo(gateX + gateW, gateY + gateH);
      ctx.lineTo(gateX + gateW - revealRight, gateY + gateH);
      ctx.lineTo(gateX + gateW - revealRight, gateY + revealTop);
      ctx.closePath();
      ctx.fill();
      // Lichtkante an der rechten äußeren Mauerecke
      ctx.fillStyle = '#64748b';
      ctx.fillRect(gateX + gateW - 0.6, gateY, 0.6, gateH);

      // C. 4 Horizontale Panzertor-Segmente (Sitzen vertieft in der Laibung)
      const doorInX = gateX + revealLeft;
      const doorInY = gateY + revealTop;
      const doorInW = gateW - revealLeft - revealRight;
      const doorInH = gateH - revealTop;
      const slatH = doorInH / 4;

      for (let s = 0; s < 4; s++) {
        const sy = doorInY + s * slatH;
        // Segment-Körper in dunklem Stahlgrau (#334155)
        ctx.fillStyle = doorSlatBase;
        ctx.fillRect(doorInX, sy + 0.4, doorInW, slatH - 0.8);
        // Obere feine Glanzkante jedes Segments
        ctx.fillStyle = doorSlatLight;
        ctx.fillRect(doorInX + 0.5, sy + 0.6, doorInW - 1, 0.7);
        // Untere Schattenfuge
        ctx.fillStyle = doorSlatDark;
        ctx.fillRect(doorInX, sy + slatH - 0.8, doorInW, 0.8);

        // Nieten an den Torflügeln
        [4, doorInW - 5].forEach(nx => {
          ctx.fillStyle = '#0a0f18';
          ctx.fillRect(doorInX + nx, sy + slatH / 2 - 0.75, 1.5, 1.5);
          ctx.fillStyle = '#64748b';
          ctx.fillRect(doorInX + nx + 0.4, sy + slatH / 2 - 0.5, 0.8, 0.8);
        });
      }

      // Schlagschatten des Sturzes oben auf die oberste Torlamelle
      const lintelShadow = this._cachedGradient('bunkerLintel:' + team, () => {
        const g = ctx.createLinearGradient(doorInX, doorInY, doorInX, doorInY + 3.2);
        g.addColorStop(0, 'rgba(0, 0, 0, 0.65)');
        g.addColorStop(1, 'rgba(0, 0, 0, 0.0)');
        return g;
      });
      ctx.fillStyle = lintelShadow;
      ctx.fillRect(doorInX, doorInY, doorInW, 3.2);

      // D. Zentraler vertikaler Dichtungsfalz & Tor-Verriegelung
      const midGateX = doorInX + doorInW / 2;
      ctx.fillStyle = '#060910';
      ctx.fillRect(midGateX - 1.5, doorInY, 3, doorInH);
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(midGateX - 0.8, doorInY, 1.6, doorInH);

      // Zentrales mechanisches Verriegelungsrad / Schwerlast-Tresorverschluss
      const lockY = doorInY + doorInH * 0.52;
      ctx.fillStyle = '#0a0f18';
      ctx.beginPath();
      ctx.arc(midGateX, lockY, 4.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#334155';
      ctx.beginPath();
      ctx.arc(midGateX, lockY, 3.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#64748b';
      ctx.beginPath();
      ctx.arc(midGateX, lockY, 1.8, 0, Math.PI * 2);
      ctx.fill();
      // Verriegelungs-Kreuz
      ctx.strokeStyle = '#0a0f18';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(midGateX - 3.5, lockY); ctx.lineTo(midGateX + 3.5, lockY);
      ctx.moveTo(midGateX, lockY - 3.5); ctx.lineTo(midGateX, lockY + 3.5);
      ctx.stroke();

      // Realismus 6: Schwerlast-Führungsbacken am Torrahmen (Verriegelungs-Schienen)
      [gateY + 12, gateY + 32].forEach(guideY => {
        [gateX - 2.5, gateX + gateW].forEach(gx => {
          ctx.fillStyle = '#060910';
          ctx.fillRect(gx, guideY, 2.5, 4.0);
          ctx.fillStyle = '#1e293b';
          ctx.fillRect(gx + 0.4, guideY + 0.4, 1.7, 3.2);
          ctx.fillStyle = '#cbd5e1';
          ctx.fillRect(gx + 0.7, guideY + 1.2, 1.1, 1.1);
        });
      });

      // Realismus 6: Roter Notentriegelungs-Handhebel mit gelbem Schutzbügel
      const leverX = dir === 1 ? gateX + gateW + 1.6 : gateX - 4.2;
      const leverY = gateY + gateH - 12;
      ctx.fillStyle = '#ca8a04';
      ctx.fillRect(leverX - 0.5, leverY - 1, 3.5, 7);
      ctx.fillStyle = '#0a0f18';
      ctx.fillRect(leverX, leverY, 2.5, 5);
      ctx.fillStyle = '#dc2626';
      ctx.fillRect(leverX + 0.5, leverY + 1, 1.5, 3);
      ctx.fillStyle = '#f87171';
      ctx.fillRect(leverX + 0.7, leverY + 1, 1.1, 1);

      // E. Tor-Status-LED über dem Sturz mit weichem Lichtschein (Realismus 5)
      const gateLedX = dir === 1 ? gateX + 6 : gateX + gateW - 6;
      const ledGlow = this._cachedGradient('bunkerLed:' + team, () => {
        const g = ctx.createRadialGradient(gateLedX, gateY - 2.2, 0.5, gateLedX, gateY - 2.2, 5.5);
        g.addColorStop(0, 'rgba(34, 197, 94, 0.45)');
        g.addColorStop(0.5, 'rgba(34, 197, 94, 0.18)');
        g.addColorStop(1, 'rgba(34, 197, 94, 0.0)');
        return g;
      });
      ctx.fillStyle = ledGlow;
      ctx.beginPath();
      ctx.arc(gateLedX, gateY - 2.2, 5.5, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#22c55e'; // Grün = Bunker betriebsbereit
      ctx.beginPath();
      ctx.arc(gateLedX, gateY - 2.2, 1.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(gateLedX - 0.4, gateY - 2.6, 0.8, 0.8);

      // =============================================================
      // 5. ZENTRALER PFEILER & TRENNRIPPE (Zwischen Tor & Terminal)
      // =============================================================
      const ribW = 4.5;
      const ribX = dir === 1 ? gateX + gateW + 1 : gateX - ribW - 1;

      // Feature 3: Weicher Schlagschatten der Rippe nach rechts auf die Wand
      const ribShadow = this._cachedGradient('bunkerRib:' + team, () => {
        const g = ctx.createLinearGradient(ribX + ribW, dockY + 2, ribX + ribW + 3.0, dockY + 2);
        g.addColorStop(0, 'rgba(2, 6, 23, 0.45)');
        g.addColorStop(0.6, 'rgba(2, 6, 23, 0.20)');
        g.addColorStop(1, 'rgba(2, 6, 23, 0.0)');
        return g;
      });
      ctx.fillStyle = ribShadow;
      ctx.fillRect(ribX + ribW, dockY + 2, 3.0, wallH - trimH - 2);

      // Rippen-Körper
      ctx.fillStyle = '#0a0f18';
      ctx.fillRect(ribX - 0.5, dockY + 1, ribW + 1, wallH - trimH);
      ctx.fillStyle = doorSlatBase; // Dunkle Verstärkungsrippe auf hellgrauer Wand
      ctx.fillRect(ribX, dockY + 2, ribW, wallH - trimH - 2);
      // Linke helle Lichtkante (fängt direktes Licht ein)
      ctx.fillStyle = specLine;
      ctx.fillRect(ribX, dockY + 2, 1, wallH - trimH - 2);
      // Obere Glanzkante
      ctx.fillStyle = highlight;
      ctx.fillRect(ribX, dockY + 2, ribW, 0.8);

      // Realismus 1: Technische Typenschild-Plakette (Data Plate)
      const plateY = dockY + 8;
      ctx.fillStyle = '#060a10';
      ctx.fillRect(ribX + 0.6, plateY - 0.4, ribW - 1.2, 3.6);
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(ribX + 0.9, plateY, ribW - 1.8, 2.8);
      ctx.fillStyle = '#0a0f18';
      ctx.fillRect(ribX + 1.2, plateY + 0.9, ribW - 2.4, 0.9);

      // Nieten auf der Mittelrippe mit 3D-Tiefe
      [0.18, 0.38, 0.58, 0.78].forEach(ratio => {
        const ny = dockY + 2 + (wallH - trimH - 4) * ratio;
        ctx.fillStyle = '#060a12';
        ctx.fillRect(ribX + 1.2, ny - 0.3, 2.0, 2.0);
        ctx.fillStyle = specLine;
        ctx.fillRect(ribX + 1.5, ny, 1.4, 1.4);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(ribX + 1.5, ny, 0.7, 0.7);
      });

      // =============================================================
      // 6. TERMINAL-BUCHT MIT VERKLEINERTEM SINUS-MONITOR
      // =============================================================
      const cornerW = 5.5;
      const rightColX = dockX + DOCK_BLOCK_W - cornerW;
      const termX = dir === 1 ? ribX + ribW + 1.5 : dockX + 4;
      const termW = dir === 1 ? (rightColX - 1.5 - termX) : (DOCK_BLOCK_W - gateW - ribW - 9); // ca. 28px
      const termBayH = wallH - trimH - 6;

      // Vertiefte Kassetten-Nische für das Kontroll-Terminal mit 3D-Laibung (Feature 1)
      ctx.fillStyle = '#080c14';
      ctx.fillRect(termX - 1, dockY + 2.8, termW + 2, termBayH + 2.2);

      // Nischen-Rückwand mit Tiefenverlauf
      const nicheGrad = this._cachedGradient('bunkerNiche:' + team, () => {
        const g = ctx.createLinearGradient(termX, dockY + 4, termX, dockY + 4 + termBayH);
        g.addColorStop(0, '#131b26');
        g.addColorStop(1, '#1e293b');
        return g;
      });
      ctx.fillStyle = nicheGrad;
      ctx.fillRect(termX, dockY + 4, termW, termBayH);

      // Obere Nischen-Sturzlaibung (Schatten)
      ctx.fillStyle = '#070b12';
      ctx.fillRect(termX, dockY + 3.8, termW, 2.0);

      // Linke Nischen-Laibung (Schatten)
      ctx.fillStyle = '#090e17';
      ctx.fillRect(termX, dockY + 4, 1.5, termBayH);

      // Rechte Nischen-Laibung (Aufhellung)
      ctx.fillStyle = '#263342';
      ctx.fillRect(termX + termW - 1.2, dockY + 4, 1.2, termBayH);

      // A. HERAUSSTEHENDES VERKLEINERTES TERMINAL-GEHÄUSE (Kompakt & Schlank)
      const dispBoxW = 20; // verkleinert auf 20px für perfekten Sitz im 28px-Schacht
      const dispBoxH = 13; // verkleinert von 17 auf 13px
      const dispBaseX = termX + (termW - dispBoxW) / 2;
      const dispBaseY = dockY + 6;
      const protrudeY = 4;
      const protrudeX = dir * 1.0;

      const fX = dispBaseX + protrudeX;
      const fY = dispBaseY + protrudeY;

      // Schlagschatten des herausstehenden Monitors
      ctx.fillStyle = 'rgba(2, 6, 23, 0.85)';
      ctx.beginPath();
      ctx.moveTo(dispBaseX + 1, dispBaseY + dispBoxH);
      ctx.lineTo(fX + dispBoxW + 2, fY + dispBoxH);
      ctx.lineTo(fX + dispBoxW + 2, fY + dispBoxH + 3);
      ctx.lineTo(dispBaseX + 1, dispBaseY + dispBoxH + 3);
      ctx.closePath();
      ctx.fill();

      // Obere Dachschräge des Gehäuses
      ctx.fillStyle = topLight;
      ctx.beginPath();
      ctx.moveTo(dispBaseX, dispBaseY);
      ctx.lineTo(dispBaseX + dispBoxW, dispBaseY);
      ctx.lineTo(fX + dispBoxW, fY);
      ctx.lineTo(fX, fY);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = outline;
      ctx.lineWidth = 1;
      ctx.stroke();

      // Glanzkante oben
      ctx.fillStyle = specLine;
      ctx.beginPath();
      ctx.moveTo(fX, fY);
      ctx.lineTo(fX + dispBoxW, fY);
      ctx.lineTo(fX + dispBoxW, fY + 1);
      ctx.lineTo(fX, fY + 1);
      ctx.closePath();
      ctx.fill();

      // Seitenflanke des herausstehenden Gehäuses
      const flankX1 = dir === 1 ? dispBaseX : dispBaseX + dispBoxW;
      const flankX2 = dir === 1 ? fX : fX + dispBoxW;
      ctx.fillStyle = '#141c2b';
      ctx.beginPath();
      ctx.moveTo(flankX1, dispBaseY);
      ctx.lineTo(flankX2, fY);
      ctx.lineTo(flankX2, fY + dispBoxH);
      ctx.lineTo(flankX1, dispBaseY + dispBoxH);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = outline;
      ctx.lineWidth = 1;
      ctx.stroke();

      // Frontseite des Gehäuses
      ctx.fillStyle = '#0a0f18';
      ctx.fillRect(fX - 1, fY - 1, dispBoxW + 2, dispBoxH + 2);
      ctx.fillStyle = frontDark;
      ctx.fillRect(fX, fY, dispBoxW, dispBoxH);
      ctx.strokeStyle = '#475569';
      ctx.lineWidth = 1;
      ctx.strokeRect(fX, fY, dispBoxW, dispBoxH);

      // B. MONITOR-BILDSCHIRM (Kompakter Sinus-Wellen-Screen)
      const scrW = 12; // 12px Breite für zentrierten Sitz
      const scrH = 7.5; // von 10 auf 7.5px
      const scrX = fX + 2.2;
      const scrY = fY + 2.5;

      const isShotAlarm = (recoilTimer || 0) > 0.05;
      const alertFactor = Math.min(1.0, (recoilTimer || 0) * 1.5);

      ctx.fillStyle = '#0a0f18';
      ctx.fillRect(scrX - 1, scrY - 1, scrW + 2, scrH + 2);
      // Bildschirmhintergrund (im Schussmoment alarm-rot getönt)
      ctx.fillStyle = isShotAlarm ? '#1f0404' : (team === 'western' ? '#021824' : '#1c0701');
      ctx.fillRect(scrX, scrY, scrW, scrH);

      if (enableGlow) {
        // Realismus 5: Weicher Phosphorglow auf die Kassetten-Nische
        this._crtGlowLUTs = this._crtGlowLUTs || new Map();
        const crtKey = isShotAlarm ? 'alarm' : (team === 'western' ? 'western' : 'eastern');
        let crtCanvas = this._crtGlowLUTs.get(crtKey);
        if (!crtCanvas) {
          crtCanvas = document.createElement('canvas');
          crtCanvas.width = 32;
          crtCanvas.height = 32;
          const cctx = crtCanvas.getContext('2d');
          const g = cctx.createRadialGradient(16, 16, 2, 16, 16, 15);
          if (crtKey === 'alarm') {
            g.addColorStop(0, 'rgba(239, 68, 68, 0.35)');
            g.addColorStop(0.6, 'rgba(239, 68, 68, 0.12)');
          } else if (crtKey === 'western') {
            g.addColorStop(0, 'rgba(0, 240, 255, 0.22)');
            g.addColorStop(0.6, 'rgba(0, 240, 255, 0.08)');
          } else {
            g.addColorStop(0, 'rgba(255, 0, 85, 0.22)');
            g.addColorStop(0.6, 'rgba(255, 0, 85, 0.08)');
          }
          g.addColorStop(1, 'rgba(0, 0, 0, 0.0)');
          cctx.fillStyle = g;
          cctx.fillRect(0, 0, 32, 32);
          this._crtGlowLUTs.set(crtKey, crtCanvas);
        }
        ctx.save();
        ctx.beginPath();
        ctx.rect(termX, dockY + 4, termW, termBayH);
        ctx.clip();
        if (isShotAlarm) {
          ctx.globalAlpha = alertFactor;
        }
        ctx.drawImage(crtCanvas, scrX + scrW / 2 - 16, scrY + scrH / 2 - 16, 32, 32);
        ctx.restore();

        // Screen-Glow (im Schussmoment alarm-rot)
        ctx.fillStyle = isShotAlarm
          ? `rgba(239, 68, 68, ${Math.round((0.20 + alertFactor * 0.25) * 100) / 100})`
          : (team === 'western' ? 'rgba(0, 240, 255, 0.25)' : 'rgba(255, 0, 85, 0.25)');
        ctx.fillRect(scrX, scrY, scrW, scrH);
      }

      // Sinus-Welle (auch auf Low aktiv! Im Schussmoment grell rot)
      ctx.strokeStyle = isShotAlarm ? '#ef4444' : teamGlow;
      ctx.lineWidth = isShotAlarm ? 1.1 : 0.9;
      ctx.beginPath();
      const spike = (recoilTimer || 0) * 2.8;
      const waveFreq = (recoilTimer > 0) ? 14 : 9;
      for (let px = 0; px < scrW; px += 1.5) {
        const noise = spike > 0 ? ((px % 3 === 0 ? 1.0 : -0.7) * spike) : 0;
        const py = Math.max(scrY + 0.8, Math.min(scrY + scrH - 0.8, scrY + scrH / 2 + Math.sin(t * waveFreq + px * 1.1) * (1.8 + spike * 0.3) + noise));
        if (px === 0) ctx.moveTo(scrX + px, py);
        else ctx.lineTo(scrX + px, py);
      }
      ctx.stroke();

      if (enableGlow) {
        // Feine weiße Kernlinie auf der Sinuswelle bei starkem Schussimpuls
        if (isShotAlarm && alertFactor > 0.4) {
          ctx.strokeStyle = '#fecaca';
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          for (let px = 0; px < scrW; px += 1.5) {
            const noise = spike > 0 ? ((px % 3 === 0 ? 1.0 : -0.7) * spike) : 0;
            const py = Math.max(scrY + 0.8, Math.min(scrY + scrH - 0.8, scrY + scrH / 2 + Math.sin(t * waveFreq + px * 1.1) * (1.8 + spike * 0.3) + noise));
            if (px === 0) ctx.moveTo(scrX + px, py);
            else ctx.lineTo(scrX + px, py);
          }
          ctx.stroke();
        }

        const scanY = scrY + (Math.sin(t * 4) * 0.5 + 0.5) * (scrH - 1);
        ctx.fillStyle = isShotAlarm ? 'rgba(254, 202, 202, 0.55)' : 'rgba(255, 255, 255, 0.45)';
        ctx.fillRect(scrX, scanY, scrW, 1);
      }

      // Status-LEDs am kompakten Monitor (obere LED schlägt bei Schuss auf Rot um)
      const ledX = fX + dispBoxW - 3.5;
      ctx.fillStyle = isShotAlarm ? '#ef4444' : '#22c55e';
      ctx.beginPath();
      ctx.arc(ledX, fY + 3.5, 1.2, 0, Math.PI * 2);
      ctx.fill();

      const blinkRed = Math.floor(t * 3) % 2 === 0;
      ctx.fillStyle = blinkRed ? '#ef4444' : '#7f1d1d';
      ctx.beginPath();
      ctx.arc(ledX, fY + 8, 1.2, 0, Math.PI * 2);
      ctx.fill();

      // Geriffelte Daten-Schläuche (Wellrohr) vom Monitor zur Wand
      function drawCorrugatedHose(hx1, hy1, hx2, hy2, color, rimColor) {
        ctx.strokeStyle = '#0a0f18';
        ctx.lineWidth = 3.2;
        ctx.beginPath();
        ctx.moveTo(hx1, hy1);
        ctx.bezierCurveTo(hx1, hy1 - 6, hx2 - dir * 3, hy2 - 3, hx2, hy2);
        ctx.stroke();

        ctx.strokeStyle = color;
        ctx.lineWidth = 1.8;
        ctx.stroke();

        ctx.strokeStyle = rimColor;
        ctx.lineWidth = 0.8;
        ctx.setLineDash([1.2, 1.2]);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      drawCorrugatedHose(fX + 3, fY, dispBaseX + 5, dockY + 3, '#d97706', '#fef08a');
      drawCorrugatedHose(fX + 8, fY, dispBaseX + 13, dockY + 3, '#475569', '#cbd5e1');

      // C. SERVICE-EINHEIT / KEYPAD & LÜFTERGITTER UNTER DEM MONITOR
      const subPanelY = fY + dispBoxH + 4;
      const subPanelH = (dockY + termBayH + 4) - subPanelY - 2;

      if (subPanelH > 8) {
        // Tastenfeld / Intercom-Panel
        const subW = termW - 4;
        const subX = termX + 2;
        ctx.fillStyle = '#0a0f18';
        ctx.fillRect(subX - 0.5, subPanelY - 0.5, subW + 1, subPanelH + 1);
        ctx.fillStyle = doorSlatBase;
        ctx.fillRect(subX, subPanelY, subW, subPanelH);
        ctx.fillStyle = doorSlatLight;
        ctx.fillRect(subX, subPanelY, subW, 0.8);

        // Belüftungsschlitze der Serviceeinheit
        for (let l = 0; l < 3; l++) {
          const ly = subPanelY + 3 + l * 2.8;
          ctx.fillStyle = '#0a0f18';
          ctx.fillRect(subX + 3, ly, subW - 6, 1.4);
          ctx.fillStyle = '#334155';
          ctx.fillRect(subX + 3, ly + 0.4, subW - 6, 0.6);
        }

        // Mini-Wartungsgriff / gelber Riegel
        const barY = subPanelY + subPanelH - 4.5;
        ctx.fillStyle = '#ca8a04';
        ctx.fillRect(subX + subW / 2 - 4, barY, 8, 2);
        ctx.fillStyle = '#fde047';
        ctx.fillRect(subX + subW / 2 - 3, barY, 6, 0.7);
      }
      // =============================================================
      // 7. RECHTE ECKVERSTÄRKUNG IN HELLEN METALLFARBEN MIT VERNIETUNG
      // =============================================================
      // Schließt die Lücke an der rechten Kante: Massiver 3D-Eckpfeiler unter der Säule des Moduls
      const cornerColY = dockY;
      const cornerColH = wallH - trimH;

      // A. 3D-Seitenlasche (Umgreift die 90°-Ecke auf die Seitenflanke)
      if (dir === 1) {
        const wrapD = 0.08;
        const wrapX1 = dockFlankX;
        const wrapX2 = dockFlankX + skewX * wrapD;
        const wrapY_top1 = dockY;
        const wrapY_top2 = dockY - topDepth * wrapD;
        const wrapY_bot1 = groundY - trimH;
        const wrapY_bot2 = groundY - trimH - topDepth * wrapD;

        // Dunkle Schattierungsfuge
        ctx.fillStyle = '#060a12';
        ctx.beginPath();
        ctx.moveTo(wrapX1, wrapY_top1 - 0.5);
        ctx.lineTo(wrapX2 + 0.5, wrapY_top2 - 0.5);
        ctx.lineTo(wrapX2 + 0.5, wrapY_bot2 + 0.5);
        ctx.lineTo(wrapX1, wrapY_bot1 + 0.5);
        ctx.closePath();
        ctx.fill();

        // Schattierte Flankenplatte der Eckverstärkung
        ctx.fillStyle = team === 'western' ? '#4a586a' : '#5c2414';
        ctx.beginPath();
        ctx.moveTo(wrapX1, wrapY_top1);
        ctx.lineTo(wrapX2, wrapY_top2);
        ctx.lineTo(wrapX2, wrapY_bot2);
        ctx.lineTo(wrapX1, wrapY_bot1);
        ctx.closePath();
        ctx.fill();

        // Oberer Glanzfalz der Seitenlasche
        ctx.strokeStyle = '#8a9ab0';
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(wrapX1, wrapY_top1);
        ctx.lineTo(wrapX2, wrapY_top2);
        ctx.stroke();
      }

      // Feature 3: Relief-Schlagschatten / Ambient-Occlusion-Fuge links der Eckverstärkung
      const cornerShadow = this._cachedGradient('bunkerCorner:' + team, () => {
        const g = ctx.createLinearGradient(rightColX - 2.8, cornerColY, rightColX, cornerColY);
        g.addColorStop(0, 'rgba(2, 6, 23, 0.0)');
        g.addColorStop(0.5, 'rgba(2, 6, 23, 0.22)');
        g.addColorStop(1, 'rgba(2, 6, 23, 0.48)');
        return g;
      });
      ctx.fillStyle = cornerShadow;
      ctx.fillRect(rightColX - 2.8, cornerColY, 2.8, cornerColH);

      // B. Vordere Hauptplatte der Eckverstärkung (Front Face)
      // Äußere dunkle Kontur
      ctx.fillStyle = '#080c14';
      ctx.fillRect(rightColX - 0.5, cornerColY - 0.5, cornerW + 1.0, cornerColH + 1.0);

      // Grundkörper im hellen Industriestahl (#728294 / #8292a4)
      ctx.fillStyle = team === 'western' ? '#728294' : '#a05238';
      ctx.fillRect(rightColX, cornerColY, cornerW, cornerColH);

      // Vertiefter Mittelkanal / Verstärkungssicke
      ctx.fillStyle = team === 'western' ? '#556578' : '#782d18';
      ctx.fillRect(rightColX + 1.2, cornerColY + 3, cornerW - 2.4, cornerColH - 6);

      // Linke Lichtkante (Übergang zur Wand)
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(rightColX + 0.4, cornerColY, 0.8, cornerColH);

      // Rechte 3D-Eckkante (Gratlinie zum Flankenumschwung — stark ausgeleuchtet!)
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(rightColX + cornerW - 1.2, cornerColY, 1.2, cornerColH);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(rightColX + cornerW - 0.6, cornerColY, 0.6, cornerColH);

      // Montage-Knotenbleche oben und unten
      [cornerColY, groundY - trimH - 3.5].forEach(gy => {
        ctx.fillStyle = '#0a0f18';
        ctx.fillRect(rightColX - 0.5, gy, cornerW + 1, 3.5);
        ctx.fillStyle = '#94a3b8';
        ctx.fillRect(rightColX, gy + 0.5, cornerW, 2.5);
        ctx.fillStyle = '#f8fafc';
        ctx.fillRect(rightColX + 0.5, gy + 0.8, cornerW - 1, 0.8);
      });

      // C. VERNIETUNG (5 Nieten vertikal auf der Eckverstärkung)
      const rivetCount = 5;
      for (let r = 0; r < rivetCount; r++) {
        const ry = cornerColY + 7 + r * ((cornerColH - 14) / (rivetCount - 1));
        const rx = rightColX + cornerW / 2;

        // Dunkle Nieteinfassung / Senkloch
        ctx.fillStyle = '#060a10';
        ctx.fillRect(rx - 1.2, ry - 1.2, 2.4, 2.4);

        // Heller Nietenbolzen
        ctx.fillStyle = '#cbd5e1';
        ctx.fillRect(rx - 0.8, ry - 0.8, 1.6, 1.6);

        // Weißer Glanzpunkt auf der Niete
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(rx - 0.4, ry - 0.4, 0.8, 0.8);

        // Passende Niete auf der Flankenlasche (seitliche Vernietung)
        if (dir === 1) {
          const wrapD = 0.08;
          const sRivetX = (rightColX + cornerW) + skewX * wrapD * 0.55;
          const sRivetY = ry - topDepth * wrapD * 0.55;
          ctx.fillStyle = '#0a0f18';
          ctx.fillRect(sRivetX - 0.7, sRivetY - 0.7, 1.4, 1.4);
          ctx.fillStyle = '#94a3b8';
          ctx.fillRect(sRivetX - 0.35, sRivetY - 0.35, 0.7, 0.7);
        }
      }

      return {
        dockPlane: { x: roofX, y: roofY }
      };
  }

  _drawCannonMountAndCables(ctx, team, flankX, mountX, mountY, dir, sFlankX, sSkewX, sTopDepth, gY) {
      const topLight = team === 'western' ? '#64748b' : '#9a3412';
      const frontDark = team === 'western' ? '#1e293b' : '#3d1206';
      const frontMid = team === 'western' ? '#334155' : '#6b250e';
      const highlight = team === 'western' ? '#cbd5e1' : '#fdba74';
      const specLine = '#f8fafc';
      const darkSteel = '#0a0f18';
      const gunmetal = '#475569';
      const brass = '#d97706';
      const brassLight = '#fde047';

      // Fundament- und Wandgeometrie der Bunkerflanke
      const scaledFlankX = sFlankX !== undefined ? sFlankX : (flankX - dir * (18 * 1.06 * 0.5));
      const scaledSkewX = sSkewX !== undefined ? sSkewX : (18 * 1.06);
      const scaledTopDepth = sTopDepth !== undefined ? sTopDepth : (24 * 1.06);
      const groundY = gY !== undefined ? gY : (mountY + scaledTopDepth * 0.5 + 3);

      // =======================================================================
      // A. REALISTISCHER WANDANSCHLUSS-TERMINAL (Bunkerflanke -> Kanonensockel)
      // =======================================================================
      // Sitzt bündig auf der geneigten Seitenflanke des Bunkers (exakte 2.5D-Perspektive)
      const uStart = 0.20;
      const uEnd = 0.46;
      const boxH = 14.0;
      const boxThick = 3.2; // 3D-Auskragung von der Wand

      // Wandkontakt-Punkte (folgen exakt der Neigung der Flankenwand)
      const xWall1 = scaledFlankX + dir * (scaledSkewX * uStart);
      const yWallBase1 = groundY - scaledTopDepth * uStart;
      const yWallTop1 = yWallBase1 - boxH;

      const xWall2 = scaledFlankX + dir * (scaledSkewX * uEnd);
      const yWallBase2 = groundY - scaledTopDepth * uEnd;
      const yWallTop2 = yWallBase2 - boxH;

      // 3D-Frontpunkte (nach außen auskragend in Richtung dir)
      const xFace1 = xWall1 + dir * boxThick;
      const xFace2 = xWall2 + dir * boxThick;

      // Kabelabgangs-Punkte (Muffen sitzen an der Stirnseite facing cannon)
      const glandLen = 3.0;
      const portX = xFace2 + dir * glandLen;
      const port1Y = mountY - 2.5;
      const port2Y = mountY + 2.5;

      const sockX = mountX - dir * 17.5;
      const sock1Y = mountY - 2.5;
      const sock2Y = mountY + 2.5;

      // 1. Zuleitungs-Panzerrohr von der Bunkerwand (vertikal von unter der Scharte ins Gehäuse)
      const pipeU = (uStart + uEnd) * 0.5;
      const pipeX = scaledFlankX + dir * (scaledSkewX * pipeU);
      const pipeBaseY = (yWallTop1 + yWallTop2) * 0.5;
      const pipeTopY = pipeBaseY - 14.0;

      ctx.strokeStyle = darkSteel;
      ctx.lineWidth = 3.2;
      ctx.beginPath();
      ctx.moveTo(pipeX, pipeTopY);
      ctx.lineTo(pipeX, pipeBaseY);
      ctx.stroke();

      ctx.strokeStyle = gunmetal;
      ctx.lineWidth = 2.0;
      ctx.beginPath();
      ctx.moveTo(pipeX, pipeTopY);
      ctx.lineTo(pipeX, pipeBaseY);
      ctx.stroke();

      ctx.strokeStyle = highlight;
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(pipeX - dir * 0.4, pipeTopY);
      ctx.lineTo(pipeX - dir * 0.4, pipeBaseY);
      ctx.stroke();

      // Omega-Wandschelle am Zulaufrohr
      const clampY = pipeTopY + 6.0;
      ctx.fillStyle = darkSteel;
      ctx.fillRect(pipeX - 2.4, clampY - 1.2, 4.8, 2.4);
      ctx.fillStyle = highlight;
      ctx.fillRect(pipeX - 1.8, clampY - 0.8, 3.6, 1.6);
      ctx.fillStyle = darkSteel;
      ctx.fillRect(pipeX - 0.5, clampY - 0.8, 1.0, 1.6);

      // 2. Weicher Bodenschatten des Wandterminals & der Muffen
      ctx.fillStyle = 'rgba(2, 6, 23, 0.55)';
      ctx.beginPath();
      ctx.moveTo(xWall1 - dir * 1.0, yWallBase1 + 0.5);
      ctx.lineTo(portX + dir * 1.0, yWallBase2 + 1.2);
      ctx.lineTo(xFace1 - dir * 0.5, yWallBase1 + 2.0);
      ctx.closePath();
      ctx.fill();

      // 3. Montage-Grundplatte an der Wand (Wandflansch im dunklen Panzerstahl)
      ctx.fillStyle = darkSteel;
      ctx.beginPath();
      ctx.moveTo(xWall1 - dir * 0.6, yWallTop1 - 0.8);
      ctx.lineTo(xFace2 + dir * 0.6, yWallTop2 - 0.8);
      ctx.lineTo(xFace2 + dir * 0.6, yWallBase2 + 0.8);
      ctx.lineTo(xWall1 - dir * 0.6, yWallBase1 + 0.8);
      ctx.closePath();
      ctx.fill();

      // 4. Hauptgehäuse des Panzerschott-Terminals (in Fluchtperspektive)
      ctx.fillStyle = frontDark;
      ctx.beginPath();
      ctx.moveTo(xFace1, yWallTop1);
      ctx.lineTo(xFace2, yWallTop2);
      ctx.lineTo(xFace2, yWallBase2);
      ctx.lineTo(xFace1, yWallBase1);
      ctx.closePath();
      ctx.fill();

      // Inset-Deckelplatte
      ctx.fillStyle = team === 'western' ? '#263342' : '#4d1708';
      ctx.beginPath();
      ctx.moveTo(xFace1 + dir * 0.6, yWallTop1 + 1.2);
      ctx.lineTo(xFace2 - dir * 0.6, yWallTop2 + 1.2);
      ctx.lineTo(xFace2 - dir * 0.6, yWallBase2 - 1.2);
      ctx.lineTo(xFace1 + dir * 0.6, yWallBase1 - 1.2);
      ctx.closePath();
      ctx.fill();

      // Obere bemaßte 3D-Lichtfase (Dachfase)
      ctx.fillStyle = highlight;
      ctx.beginPath();
      ctx.moveTo(xWall1, yWallTop1);
      ctx.lineTo(xWall2, yWallTop2);
      ctx.lineTo(xFace2, yWallTop2);
      ctx.lineTo(xFace1, yWallTop1);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = specLine;
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(xWall1, yWallTop1);
      ctx.lineTo(xWall2, yWallTop2);
      ctx.stroke();

      // Stirnseite in Richtung Kanone (Austrittsebene)
      ctx.fillStyle = frontMid;
      ctx.beginPath();
      ctx.moveTo(xWall2, yWallTop2);
      ctx.lineTo(xFace2, yWallTop2);
      ctx.lineTo(xFace2, yWallBase2);
      ctx.lineTo(xWall2, yWallBase2);
      ctx.closePath();
      ctx.fill();

      // 4 Sechskant-Befestigungsbolzen an den Ecken
      [
        { x: xWall1 + dir * 0.8, y: yWallTop1 + 2.0 },
        { x: xWall2 - dir * 0.8, y: yWallTop2 + 2.0 },
        { x: xWall1 + dir * 0.8, y: yWallBase1 - 2.0 },
        { x: xWall2 - dir * 0.8, y: yWallBase2 - 2.0 }
      ].forEach(b => {
        ctx.fillStyle = darkSteel;
        ctx.fillRect(b.x - 0.8, b.y - 0.8, 1.6, 1.6);
        ctx.fillStyle = highlight;
        ctx.fillRect(b.x - 0.4, b.y - 0.4, 0.8, 0.8);
      });

      // Gelbe Gefahren-Warnmarkierung / Hochspannungs-Badge
      const badgeX = (xFace1 + xFace2) * 0.5;
      const badgeY = (yWallTop1 + yWallBase1) * 0.5 - 1.0;
      ctx.fillStyle = darkSteel;
      ctx.fillRect(badgeX - 1.8, badgeY - 1.5, 3.6, 3.0);
      ctx.fillStyle = '#ca8a04';
      ctx.fillRect(badgeX - 1.2, badgeY - 1.0, 2.4, 2.0);
      ctx.fillStyle = '#fde047';
      ctx.fillRect(badgeX - 0.6, badgeY - 0.6, 1.2, 1.2);

      // 5. Zwei massive Industrie-Kabelverschraubungen / Schottmuffen (Austritt parallel zum Boden)
      [port1Y, port2Y].forEach(py => {
        const gBaseX = Math.min(xFace2, portX);
        const gW = Math.abs(portX - xFace2);

        // Dunkle Einfassung
        ctx.fillStyle = darkSteel;
        ctx.fillRect(gBaseX - (dir === 1 ? 0 : 0.6), py - 2.0, gW + 0.6, 4.0);

        // Sechskant-Muffenkörper
        ctx.fillStyle = '#64748b';
        ctx.fillRect(gBaseX + (dir === 1 ? 0.4 : 0.6), py - 1.5, gW - 0.8, 3.0);

        // Messing-Dichtungsring
        ctx.fillStyle = brass;
        ctx.fillRect(dir === 1 ? gBaseX + 0.4 : gBaseX + gW - 1.0, py - 1.5, 0.8, 3.0);
        ctx.fillStyle = brassLight;
        ctx.fillRect(dir === 1 ? gBaseX + 0.4 : gBaseX + gW - 1.0, py - 1.5, 0.8, 0.8);

        // Knickschutztülle (Gummimanschette)
        ctx.fillStyle = darkSteel;
        ctx.fillRect(dir === 1 ? portX - 0.8 : portX, py - 1.2, 1.0, 2.4);
      });

      // 6. Bodenschatten beider Panzerschläuche (exakt parallel zum Boden, dy = 0)
      ctx.strokeStyle = 'rgba(2, 6, 23, 0.55)';
      ctx.lineWidth = 3.6;
      ctx.beginPath();
      ctx.moveTo(portX, port1Y + 1.8);
      ctx.lineTo(sockX, sock1Y + 1.8);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(portX, port2Y + 1.8);
      ctx.lineTo(sockX, sock2Y + 1.8);
      ctx.stroke();

      // 7. KABEL 1: ROBUSTER PANZERSCHLAUCH (PARALLEL ZUM BODEN, dy = 0)
      ctx.strokeStyle = '#060910';
      ctx.lineWidth = 4.2;
      ctx.beginPath();
      ctx.moveTo(portX, port1Y);
      ctx.lineTo(sockX, sock1Y);
      ctx.stroke();

      ctx.strokeStyle = '#334155';
      ctx.lineWidth = 2.6;
      ctx.beginPath();
      ctx.moveTo(portX, port1Y);
      ctx.lineTo(sockX, sock1Y);
      ctx.stroke();

      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth = 1.2;
      ctx.setLineDash([2.5, 2.5]);
      ctx.beginPath();
      ctx.moveTo(portX, port1Y);
      ctx.lineTo(sockX, sock1Y);
      ctx.stroke();
      ctx.setLineDash([]);

      // 8. KABEL 2: GEPANZERTER DATEN- & HYDRAULIKSCHLAUCH (PARALLEL ZUM BODEN, dy = 0)
      ctx.strokeStyle = '#0a0f18';
      ctx.lineWidth = 3.8;
      ctx.beginPath();
      ctx.moveTo(portX, port2Y);
      ctx.lineTo(sockX, sock2Y);
      ctx.stroke();

      ctx.strokeStyle = '#475569';
      ctx.lineWidth = 2.4;
      ctx.beginPath();
      ctx.moveTo(portX, port2Y);
      ctx.lineTo(sockX, sock2Y);
      ctx.stroke();

      ctx.strokeStyle = '#cbd5e1';
      ctx.lineWidth = 1.0;
      ctx.setLineDash([2, 2]);
      ctx.beginPath();
      ctx.moveTo(portX, port2Y);
      ctx.lineTo(sockX, sock2Y);
      ctx.stroke();
      ctx.setLineDash([]);

      // 9. Robuste Bodenkabel-Brückenklemme (Schwerer Metall-Sattel mittig auf beiden Kabeln)
      const clipX = (portX + sockX) * 0.5;
      const clipY = mountY;
      ctx.fillStyle = darkSteel;
      ctx.fillRect(clipX - 2.5, clipY - 4.5, 5.0, 9.0);
      ctx.fillStyle = '#475569';
      ctx.fillRect(clipX - 1.8, clipY - 3.8, 3.6, 7.6);
      ctx.fillStyle = specLine;
      ctx.fillRect(clipX - 1.0, clipY - 3.0, 2.0, 1.2);
      ctx.fillRect(clipX - 1.0, clipY + 1.8, 2.0, 1.2);

      // 10. Massive Sockel-Einsteckflansche am runden Drehkranz (parallel zum Boden)
      [sock1Y, sock2Y].forEach(sy => {
        ctx.fillStyle = darkSteel;
        ctx.fillRect(dir === 1 ? sockX - 0.8 : sockX - 3.2, sy - 2.2, 4.0, 4.4);
        ctx.fillStyle = '#64748b';
        ctx.fillRect(dir === 1 ? sockX - 0.2 : sockX - 2.4, sy - 1.7, 2.6, 3.4);
        ctx.fillStyle = brass;
        ctx.fillRect(dir === 1 ? sockX - 0.8 : sockX + 0.2, sy - 1.5, 0.8, 3.0);
      });
      // B. FACETTIERTER 3D-BUNKER-PYLON (Vorschlag 1 & 5: Sockel mit Höhe, Fase, Bitumen & Warnring)
      ctx.save();
      ctx.translate(mountX, mountY);
      ctx.scale(0.88, 0.88);
      ctx.translate(-mountX, -mountY);

      // 1. Weicher Kontaktschatten des Pylons auf den Boden
      ctx.fillStyle = 'rgba(2, 6, 23, 0.70)';
      ctx.beginPath();
      ctx.ellipse(mountX + dir * 3, mountY + 4.5, 25, 11, 0, 0, Math.PI * 2);
      ctx.fill();

      // 2. Bitumen-/Teer-Dehnungsfuge am Bodenkontakt (analog zum Bunker)
      ctx.fillStyle = '#05070c';
      ctx.beginPath();
      ctx.ellipse(mountX, mountY + 3.5, 23.5, 10.2, 0, 0, Math.PI * 2);
      ctx.fill();

      // 3. Erdig-staubiger Bodenschleier
      const mountDust = ctx.createLinearGradient(mountX, mountY - 2, mountX, mountY + 6);
      mountDust.addColorStop(0, 'rgba(45, 35, 25, 0.0)');
      mountDust.addColorStop(1, 'rgba(45, 35, 25, 0.28)');
      ctx.fillStyle = mountDust;
      ctx.beginPath();
      ctx.ellipse(mountX, mountY + 3.5, 24, 10.5, 0, 0, Math.PI * 2);
      ctx.fill();

      // 4. 45°-Böschungsfase am Sockelfuß (wie die Fußleiste des Bunkers!)
      ctx.fillStyle = team === 'western' ? '#94a3b8' : '#bd654c';
      ctx.beginPath();
      ctx.ellipse(mountX, mountY + 3.0, 22.5, 9.8, 0, 0, Math.PI * 2);
      ctx.fill();
      // Glanzfalz auf der Fase
      ctx.fillStyle = team === 'western' ? '#e2e8f0' : '#fed7aa';
      ctx.beginPath();
      ctx.ellipse(mountX, mountY + 2.2, 21.8, 9.4, 0, 0, Math.PI * 2);
      ctx.fill();

      // 5. Vertikaler 3D-Zylinderkörper (Sockelwand mit Eigenschatten)
      const pylonH = 5.0;
      const pylonTopY = mountY - 2.5;
      const pylonBotY = mountY + 2.5;

      // Zylinder-Mantelfläche
      const pylonGrad = ctx.createLinearGradient(mountX - 21, mountY, mountX + 21, mountY);
      if (team === 'western') {
        pylonGrad.addColorStop(0, '#7c8e9f');   // Lichtseite links
        pylonGrad.addColorStop(0.45, '#728294'); // Mittlerer Basiston
        pylonGrad.addColorStop(1, '#566677');   // Schattenseite rechts
      } else {
        pylonGrad.addColorStop(0, '#b86348');
        pylonGrad.addColorStop(0.45, '#a05238');
        pylonGrad.addColorStop(1, '#783520');
      }
      ctx.fillStyle = pylonGrad;
      ctx.beginPath();
      ctx.moveTo(mountX - 21, pylonTopY);
      ctx.lineTo(mountX - 21, pylonBotY);
      ctx.ellipse(mountX, pylonBotY, 21, 9.0, 0, Math.PI, 0, true);
      ctx.lineTo(mountX + 21, pylonTopY);
      ctx.ellipse(mountX, pylonTopY, 21, 9.0, 0, 0, Math.PI, false);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#0a0f18';
      ctx.lineWidth = 1.2;
      ctx.stroke();

      // Auskragendes Deckgesims mit Tropfkante oben am Pylon
      ctx.fillStyle = '#080c14';
      ctx.beginPath();
      ctx.ellipse(mountX, pylonTopY + 0.8, 20.5, 8.8, 0, 0, Math.PI * 2);
      ctx.fill();

      // 6. Segmentierter Industrie-Warnring (Gelb-Schwarz)
      const ringOuterR = 18.5;
      const ringOuterRY = 7.8;
      ctx.fillStyle = '#141a24';
      ctx.beginPath();
      ctx.ellipse(mountX, pylonTopY, ringOuterR, ringOuterRY, 0, 0, Math.PI * 2);
      ctx.fill();

      // 8 Warn-Segmente entlang des Umfangs
      for (let w = 0; w < 8; w++) {
        if (w % 2 === 0) {
          const a1 = (w * Math.PI) / 4;
          const a2 = ((w + 1) * Math.PI) / 4;
          ctx.fillStyle = '#ca8a04';
          ctx.beginPath();
          ctx.moveTo(mountX, pylonTopY);
          ctx.lineTo(mountX + Math.cos(a1) * ringOuterR, pylonTopY + Math.sin(a1) * ringOuterRY);
          // Näherung entlang der Ellipse
          for (let step = 1; step <= 3; step++) {
            const a = a1 + (a2 - a1) * (step / 3);
            ctx.lineTo(mountX + Math.cos(a) * ringOuterR, pylonTopY + Math.sin(a) * ringOuterRY);
          }
          ctx.closePath();
          ctx.fill();
        }
      }

      // Eingefräste dunkle Innenfuge
      ctx.fillStyle = '#0a0f18';
      ctx.beginPath();
      ctx.ellipse(mountX, pylonTopY, 15.0, 6.2, 0, 0, Math.PI * 2);
      ctx.fill();

      // Vorschlag 7: Schmierfett-Rille & Zahnkranz-Laufspur am Drehteller
      // 1. Feucht-dunkle Fettfuge / Kugellaufbahn auf dem Drehkranz
      ctx.strokeStyle = '#05070c';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.ellipse(mountX, pylonTopY, 14.5, 5.9, 0, 0, Math.PI * 2);
      ctx.stroke();

      // 2. Schmierfilm & zäher Fettglanz
      ctx.strokeStyle = 'rgba(15, 23, 42, 0.85)';
      ctx.lineWidth = 0.8;
      ctx.stroke();

      // 3. Metallischer Abrieb & Glanzpunkte der Kugellager auf der Lauffläche
      ctx.strokeStyle = 'rgba(203, 213, 225, 0.45)';
      ctx.lineWidth = 0.6;
      ctx.setLineDash([2.5, 4.5]);
      ctx.beginPath();
      ctx.ellipse(mountX, pylonTopY, 14.5, 5.9, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);

      // 7. Zentraler Drehteller im dunklen Panzerstahl (#334155 / #1e293b)
      ctx.fillStyle = team === 'western' ? '#334155' : '#4d190c';
      ctx.beginPath();
      ctx.ellipse(mountX, pylonTopY, 13.8, 5.6, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = team === 'western' ? '#94a3b8' : '#fed7aa';
      ctx.lineWidth = 0.9;
      ctx.stroke();

      // 6 Facettierte Sechskant-Bodenanker mit 3D-Senkloch
      for (let a = 0; a < 6; a++) {
        const ang = (a * Math.PI) / 3;
        const bx = mountX + Math.cos(ang) * 9.5;
        const by = pylonTopY + Math.sin(ang) * 3.8;
        // Senkloch
        ctx.fillStyle = '#060a12';
        ctx.fillRect(bx - 1.2, by - 1.0, 2.4, 2.0);
        // Bolzenkopf
        ctx.fillStyle = team === 'western' ? '#cbd5e1' : '#fcd34d';
        ctx.fillRect(bx - 0.8, by - 0.8, 1.6, 1.6);
        // Glanzpunkt
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(bx - 0.4, by - 0.8, 0.7, 0.7);
      }
      ctx.restore();

      return { x: mountX, y: mountY - 4 * 0.88, groundY: mountY };
  }

  _drawTerraceTurret(ctx, base, mount, dir, aimDir, t, isLow) {
    const team = base.team;
    const recoilTimer = base.recoilTimer || 0;
    const gunHeat = base._gunHeat || 0;
    const muzzleFlash = base.recoilTimer > 0.15 ? (base.recoilTimer / 0.28) : 0;
    let spentShellCasings = base._spentShellCasings;
    if (!spentShellCasings) spentShellCasings = base._spentShellCasings = [];
    let casingImpactSparks = base._casingImpactSparks;
    if (!casingImpactSparks) casingImpactSparks = base._casingImpactSparks = [];
    let gunSmokeParticles = base._gunSmokeParticles;
    if (!gunSmokeParticles) gunSmokeParticles = base._gunSmokeParticles = [];
    let casingsToEject = base._casingsToEject || 0;
    base._casingsToEject = 0;
    let smokeCooldown = base._smokeCooldown || 0;
    if (smokeCooldown > 0) base._smokeCooldown = smokeCooldown - 1;
      const sx = mount.x;
      const sy = mount.y;
      const gy = mount.groundY !== undefined ? mount.groundY : (sy + 3.5);

      ctx.save();
      ctx.translate(sx, gy);
      ctx.scale(0.88, 0.88);
      ctx.translate(-sx, -gy);



      // Farbschema des Robo-Arms (Dunkelgrau wie das Panzertor & die Inlets)
      const ARM_DARK = '#0a0f18';
      const ARM_BASE = team === 'western' ? '#334155' : '#541c0b'; // Sattes Dunkelgrau
      const ARM_LIGHT = team === 'western' ? '#475569' : '#782a12'; // Lichtkante / Facette
      const ARM_SHADE = team === 'western' ? '#1e293b' : '#3d1206'; // Tiefenschatten
      const ARM_STEEL = team === 'western' ? '#141a24' : '#280b03'; // Dunkler Strukturstahl für Gelenke & Inlets
      const ARM_SPEC = team === 'western' ? '#94a3b8' : '#fdba74'; // Metallischer Glanz
      const COPPER_RING = '#d97706';
      const specLine = ARM_SPEC;

      // KINEMATIK DES ROBO-ARMS:
      // Basis sitzt auf mount (sx, sy).
      // 1. Zylindrischer Sockel / Azimuth-Drehteller
      const baseH = 12;
      const baseW = 20;

      // Motorpack / Power Box an der Seite der Basis mit Wartungsplakette (Vorschlag 5)
      const pBoxW = 12;
      const pBoxH = 10;
      const pBoxX = sx - dir * 14;
      const pBoxY = sy - baseH + 3;

      ctx.fillStyle = ARM_DARK;
      ctx.fillRect(pBoxX - 1, pBoxY - 1, pBoxW + 2, pBoxH + 2);
      ctx.fillStyle = ARM_STEEL;
      ctx.fillRect(pBoxX, pBoxY, pBoxW, pBoxH);
      ctx.fillStyle = ARM_BASE;
      ctx.fillRect(pBoxX + (dir === 1 ? 1 : pBoxW - 4), pBoxY + 1, 3, pBoxH - 2);

      // Typenschild / Wartungs-Plakette an der Motorbox
      ctx.fillStyle = '#060a10';
      ctx.fillRect(pBoxX + 2, pBoxY + 2, 5.5, 3.2);
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(pBoxX + 2.5, pBoxY + 2.5, 4.5, 2.2);
      ctx.fillStyle = '#0a0f18';
      ctx.fillRect(pBoxX + 3.0, pBoxY + 3.2, 3.5, 0.7);

      // Roter Netzschalter & Kabel am Powerpack
      ctx.fillStyle = '#ef4444';
      ctx.fillRect(pBoxX + (dir === 1 ? 2 : pBoxW - 5), pBoxY + pBoxH - 4, 3, 2);
      ctx.fillStyle = '#0a0f18';
      ctx.fillRect(pBoxX + (dir === 1 ? 1 : pBoxW - 3), pBoxY + pBoxH - 3, 1, 1);

      // Zylindrischer Drehsockel (Drehkranz-Säule) im hellgrauen Bunker-Stahl
      ctx.fillStyle = ARM_DARK;
      ctx.beginPath();
      ctx.ellipse(sx, sy - 2, baseW / 2 + 1, 5, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = ARM_BASE;
      ctx.fillRect(sx - baseW / 2, sy - baseH, baseW, baseH);
      ctx.strokeStyle = ARM_DARK;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(sx - baseW / 2, sy - baseH, baseW, baseH);

      // Lichtkante an der Basis
      ctx.fillStyle = ARM_LIGHT;
      ctx.fillRect(sx - baseW / 2 + 1, sy - baseH, 2.5, baseH);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(sx - baseW / 2 + 1, sy - baseH, 0.8, baseH);

      // Kupfer / Bronze Azimuth-Dichtungsring
      ctx.fillStyle = COPPER_RING;
      ctx.fillRect(sx - baseW / 2 - 1, sy - baseH - 1.5, baseW + 2, 2.5);
      ctx.fillStyle = '#fef08a';
      ctx.fillRect(sx - baseW / 2, sy - baseH - 1.5, baseW, 0.8);

      // Sechskantschrauben am Sockelflansch mit 3D-Lichtpunkt
      [-6, 0, 6].forEach(bx => {
        ctx.fillStyle = '#060a12';
        ctx.fillRect(sx + bx - 1.0, sy - 3.2, 2.0, 2.0);
        ctx.fillStyle = '#cbd5e1';
        ctx.fillRect(sx + bx - 0.75, sy - 3, 1.5, 1.5);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(sx + bx - 0.75, sy - 3, 0.6, 0.6);
      });

      // 2. SCHULTER-DREHGEHÄUSE (Shoulder Joint im dunklen Panzerstahl)
      const shoulderX = sx;
      const shoulderY = sy - baseH - 6;

      ctx.fillStyle = ARM_DARK;
      ctx.beginPath();
      ctx.arc(shoulderX, shoulderY, 8.5, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = ARM_STEEL;
      ctx.beginPath();
      ctx.arc(shoulderX, shoulderY, 7.5, 0, Math.PI * 2);
      ctx.fill();

      // Nabe im hellgrauen Bunker-Stahl
      ctx.fillStyle = ARM_BASE;
      ctx.beginPath();
      ctx.arc(shoulderX - dir * 1.5, shoulderY - 1.5, 5, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = ARM_SPEC;
      ctx.beginPath();
      ctx.arc(shoulderX - dir * 2, shoulderY - 2, 2.5, 0, Math.PI * 2);
      ctx.fill();

      // 3. KINEMATIK: KUKA-STIL MIT AKTIVEM HEBEN/SENKEN, RUHEATMUNG & FEDER-DÄMPFER-RÜCKSTOSS
      const armLen1 = 27; // Steil aufrechter Hauptarm (mit 18° KUKA-Rückneigung)
      const armLen2 = 28; // Horizontal nach vorne reichender Vorderarm (ausbalancierte Reichweite)

      // Symmetrische Ziel-Elevation (>0 = nach oben, <0 = nach unten):
      // Winkel-Differenz muss auf [-PI, PI] gewrapped werden (kürzester Rotationspfad).
      // Für die Eastern Base (dir = -1, Vorwärtsrichtung = Math.PI) erzeugt das Zielen
      // nach oben einen atan2-Winkel nahe -PI (z.B. -3.0 rad). Ohne Wrap ergab
      // (aimDir - Math.PI) ~ -6.14 rad statt +0.14 rad. Dadurch kippte der Roboterarm
      // um über 200° nach hinten und faltete sich unnatürlich gegen den Bunker.
      let relAim = aimDir - (dir === 1 ? 0 : Math.PI);
      relAim = ((relAim + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
      const clampedRelAim = Math.max(-0.85, Math.min(0.85, relAim));
      const elevAngle = -clampedRelAim * dir;

      // Subtile Ruheatmung
      const idleTime = performance.now() * 0.0018;
      const idleBreathe = Math.sin(idleTime) * 0.022 + Math.cos(idleTime * 2.3) * 0.008;

      // Feder-Dämpfer-Rückstoß (recoilTimer: 0.28s -> 0.0s, normalisiert auf 1.0 -> 0.0)
      const recoilFraction = Math.max(0, Math.min(1.0, (recoilTimer || 0) / 0.28));
      const tau = 1.0 - recoilFraction;
      const recoilSpring = (recoilTimer > 0) ? (Math.exp(-4.2 * tau) * Math.cos(Math.PI * 3.0 * tau)) : 0;

      // Vorschlag 1: Kinematisches Einfedern des gesamten Roboterarms beim Schuss
      // Kraftvoller Rückstoß-Schockimpuls entlang der Schussachse
      const shockBack = recoilSpring * 4.8;
      const shockDrop = Math.max(0, recoilSpring) * 2.2;

      // Koordiniertes Heben & Senken beim Zielen samt Schock-Nachgeben des Hauptarms
      const boomBase = -Math.PI / 2 - dir * 0.31;
      const boomAngle = boomBase + dir * (elevAngle * 0.28 + idleBreathe) - dir * (recoilSpring * 0.18);
      const elbowX = shoulderX + Math.cos(boomAngle) * armLen1;
      const elbowY = shoulderY + Math.sin(boomAngle) * armLen1 + shockDrop;

      const forearmBase = (dir === 1 ? 0.08 : Math.PI - 0.08);
      const forearmAngle = forearmBase - dir * (elevAngle * 0.62 + idleBreathe * 0.8) + dir * (recoilSpring * 0.28);
      const wristX = elbowX + Math.cos(forearmAngle) * armLen2 - Math.cos(aimDir) * shockBack;
      const wristY = elbowY + Math.sin(forearmAngle) * armLen2 - Math.sin(aimDir) * shockBack;

      // PARALLELER VERCHROMTER HYDRAULIK-HUBZYLINDER (mit dynamischem Kolben-Eintauchen bei Schuss!)
      const cylBaseX = shoulderX - dir * 3.5;
      const cylBaseY = shoulderY + 2.0;
      const cylTargetX = shoulderX + Math.cos(boomAngle) * (armLen1 * 0.58) - dir * 3.5;
      const cylTargetY = shoulderY + Math.sin(boomAngle) * (armLen1 * 0.58) + shockDrop * 0.6;

      // Dynamische Zylinderteilung: Kolbenstange taucht beim Rückstoß sichtbar tiefer in das Rohr ein!
      const pistonPlunge = (recoilSpring > 0 ? recoilSpring : 0) * 0.24;
      const splitRatio = Math.min(0.70, 0.50 + pistonPlunge);
      const cylMidX = cylBaseX + (cylTargetX - cylBaseX) * splitRatio;
      const cylMidY = cylBaseY + (cylTargetY - cylBaseY) * splitRatio;

      // Zylinder-Auge am Schulterblock
      ctx.fillStyle = '#0a0f18';
      ctx.beginPath();
      ctx.arc(cylBaseX, cylBaseY, 2.5, 0, Math.PI * 2);
      ctx.fill();

      // Zylinderrohr (Barrel im dunklen brünierten Stahl)
      ctx.strokeStyle = '#080c14';
      ctx.lineWidth = 4.2;
      ctx.beginPath();
      ctx.moveTo(cylBaseX, cylBaseY);
      ctx.lineTo(cylMidX, cylMidY);
      ctx.stroke();

      ctx.strokeStyle = '#141c28';
      ctx.lineWidth = 2.8;
      ctx.beginPath();
      ctx.moveTo(cylBaseX, cylBaseY);
      ctx.lineTo(cylMidX, cylMidY);
      ctx.stroke();

      // Glänzende verchromte Kolbenstange (Piston Rod)
      ctx.strokeStyle = '#cbd5e1';
      ctx.lineWidth = 1.8;
      ctx.beginPath();
      ctx.moveTo(cylMidX, cylMidY);
      ctx.lineTo(cylTargetX, cylTargetY);
      ctx.stroke();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 0.8;
      ctx.stroke();

      // A. HAUPTARM (Im edlen hellgrauen Industriestahl des Bunkers!)
      ctx.save();
      ctx.strokeStyle = ARM_DARK;
      ctx.lineWidth = 11;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(shoulderX, shoulderY);
      ctx.lineTo(elbowX, elbowY);
      ctx.stroke();

      ctx.strokeStyle = ARM_BASE; // Hellgrauer Industriestahl
      ctx.lineWidth = 8.5;
      ctx.stroke();

      ctx.strokeStyle = ARM_LIGHT; // Obere Lichtkante
      ctx.lineWidth = 4.5;
      ctx.stroke();

      ctx.strokeStyle = ARM_SPEC; // Weißer Glanzstreifen
      ctx.lineWidth = 1.2;
      ctx.stroke();
      ctx.restore();

      // Schraubenleiste am Hauptarm
      const midBoomX = (shoulderX + elbowX) / 2;
      const midBoomY = (shoulderY + elbowY) / 2;
      ctx.fillStyle = '#0a0f18';
      ctx.beginPath();
      ctx.arc(midBoomX, midBoomY, 2.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(midBoomX - 0.75, midBoomY - 0.75, 1.5, 1.5);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(midBoomX - 0.75, midBoomY - 0.75, 0.7, 0.7);

      // B. ELLBOGEN-GELENK (Dunkler Strukturstahl mit silbernem Ring)
      const elbowR = 8.5;
      ctx.fillStyle = ARM_DARK;
      ctx.beginPath();
      ctx.arc(elbowX, elbowY, elbowR, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = ARM_STEEL;
      ctx.beginPath();
      ctx.arc(elbowX, elbowY, elbowR - 1, 0, Math.PI * 2);
      ctx.fill();

      // Runde Abdeckung im hellen Bunker-Stahl
      ctx.fillStyle = ARM_BASE;
      ctx.beginPath();
      ctx.arc(elbowX, elbowY, elbowR - 3.2, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = ARM_SPEC;
      ctx.beginPath();
      ctx.arc(elbowX - dir * 1, elbowY - 1, 2.2, 0, Math.PI * 2);
      ctx.fill();

      // C. HORIZONTALER VORDERARM (Im hellgrauen Bunker-Stahl)
      ctx.save();
      ctx.strokeStyle = ARM_DARK;
      ctx.lineWidth = 9.5;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(elbowX, elbowY);
      ctx.lineTo(wristX, wristY);
      ctx.stroke();

      ctx.strokeStyle = ARM_BASE;
      ctx.lineWidth = 7;
      ctx.stroke();

      ctx.strokeStyle = ARM_LIGHT;
      ctx.lineWidth = 3.5;
      ctx.stroke();

      ctx.strokeStyle = ARM_SPEC;
      ctx.lineWidth = 1.1;
      ctx.stroke();
      ctx.restore();

      // Vorschlag 4: GEPANZERTE MUNITIONSZUFÜHRUNG / AMMO FEED CHUTE (Gliedergurt)
      const cableTau = Math.max(0, tau - 0.04);
      const cableWhip = (recoilTimer > 0) ? (Math.sin(cableTau * Math.PI * 3.5) * Math.exp(-3.8 * cableTau) * 14.0) : 0;
      const cableIdle = Math.sin(idleTime + 0.8) * 1.5;

      const cp1X = shoulderX - dir * 8;
      const cp1Y = shoulderY + 4 + cableWhip * 0.3 + cableIdle * 0.4;
      const cp2X = elbowX - dir * (7 + cableWhip * 0.5);
      const cp2Y = elbowY + 7 + cableWhip + cableIdle;

      // Dunkler Führungskern
      ctx.strokeStyle = '#0a0f18';
      ctx.lineWidth = 3.2;
      ctx.beginPath();
      ctx.moveTo(pBoxX + (dir === 1 ? pBoxW : 0), pBoxY + 3);
      ctx.bezierCurveTo(cp1X, cp1Y, cp2X, cp2Y, wristX, wristY + 4);
      ctx.stroke();

      // Gelenkiges Stahlflex-Segmentband
      ctx.strokeStyle = '#475569';
      ctx.lineWidth = 1.8;
      ctx.stroke();
      ctx.strokeStyle = '#cbd5e1';
      ctx.lineWidth = 0.9;
      ctx.setLineDash([2.0, 2.0]);
      ctx.stroke();
      ctx.setLineDash([]);

      // D. HANDGELENK (Flansch & Tool Hub)
      ctx.fillStyle = ARM_DARK;
      ctx.beginPath();
      ctx.arc(wristX, wristY, 6, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = ARM_STEEL;
      ctx.beginPath();
      ctx.arc(wristX, wristY, 5, 0, Math.PI * 2);
      ctx.fill();

      // =============================================================
      // 4. DIE KANONE AM ROBO-ARM (Präzise gerichtet nach aimDir)
      // =============================================================
      const recoilDist = recoilSpring * 13.5;
      const barrelLen = 38;

      ctx.save();
      ctx.translate(wristX, wristY);
      ctx.rotate(aimDir);
      ctx.scale(0.75, 0.75); // 75% Kanonenkopf-Skalierung

      const startX = -recoilDist;

      // A. ZWILLINGS-ROHRE MIT GESCHLITZTEN MÜNDUNGSBREMSEN & SCHMAUCH (Vorschlag 4)
      const bSpacing = 6;
      const bW = 5;
      [-bSpacing, bSpacing].forEach(offY => {
        // Dunkle Rohrkontur
        ctx.fillStyle = '#0a0f18';
        ctx.fillRect(startX, offY - bW / 2 - 0.75, barrelLen, bW + 1.5);

        // Rohrkörper im brünierten Gunmetal-Stahl
        ctx.fillStyle = team === 'western' ? '#475569' : '#8c3822';
        ctx.fillRect(startX, offY - bW / 2, barrelLen, bW);

        // Obere Glanzlicht-Kante
        ctx.fillStyle = '#cbd5e1';
        ctx.fillRect(startX, offY - bW / 2, barrelLen, 1.1);

        // Mündungsfeuerdämpfer / Kompensator-Gehäuse
        ctx.fillStyle = '#0a0f18';
        ctx.fillRect(startX + barrelLen - 7, offY - bW / 2 - 1.5, 7, bW + 3);
        ctx.fillStyle = '#334155';
        ctx.fillRect(startX + barrelLen - 6, offY - bW / 2 - 1.0, 5, bW + 2);

        // Seitliche Kompensator-Entlastungsschlitze
        [startX + barrelLen - 5, startX + barrelLen - 3].forEach(slotX => {
          ctx.fillStyle = '#060a10';
          ctx.fillRect(slotX, offY - bW / 2 - 1.2, 1.2, bW + 2.4);
        });

        // Schmauchspuren (Gunpowder Soot an der Mündung)
        const sootGrad = ctx.createLinearGradient(startX + barrelLen - 8, offY, startX + barrelLen, offY);
        sootGrad.addColorStop(0, 'rgba(10, 15, 25, 0.0)');
        sootGrad.addColorStop(1, 'rgba(10, 15, 25, 0.55)');
        ctx.fillStyle = sootGrad;
        ctx.fillRect(startX + barrelLen - 8, offY - bW / 2 - 1.5, 8, bW + 3);
      });

      // Farbschema des Geschützkopfs (Hellgrauer Industriestahl analog zum Bunkerfundament)
      const HEAD_BASE = team === 'western' ? '#728294' : '#a05238'; // Heller Basiston
      const HEAD_LIGHT = team === 'western' ? '#8a9ab0' : '#bd654c'; // Obere Lichtkante
      const HEAD_SHADE = team === 'western' ? '#566677' : '#783520'; // Tiefenschatten

      // B. KASTENFÖRMIGES PANZERGEHÄUSE (Box Mantlet im hellgrauen Bunker-Stahl!)
      const mX = startX - 14;
      const mW = 20;
      const mH = 22;

      ctx.fillStyle = '#0a0f18';
      ctx.fillRect(mX - 1, -mH / 2 - 1, mW + 2, mH + 2);

      // Hauptkörper im hellgrauen Industriestahl (#728294)
      ctx.fillStyle = HEAD_BASE;
      ctx.fillRect(mX, -mH / 2, mW, mH);

      // Obere Dachschräge & Glanzkante
      ctx.fillStyle = HEAD_LIGHT;
      ctx.fillRect(mX, -mH / 2, mW, 3.5);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(mX, -mH / 2, mW, 1.0);

      // Hülsen-Auswurffenster (Ejection Port oben auf der Kastenblende)
      ctx.fillStyle = '#060910';
      ctx.fillRect(mX + 3, -mH / 2 + 1, 7, 3.2);
      ctx.fillStyle = '#263342';
      ctx.fillRect(mX + 3.5, -mH / 2 + 1.5, 6, 2.2);
      // Messingglanz im Inneren (nächste Patrone im Zuführschacht)
      ctx.fillStyle = '#b45309';
      ctx.fillRect(mX + 4.5, -mH / 2 + 2.0, 4, 1.2);
      ctx.fillStyle = '#fde047';
      ctx.fillRect(mX + 5.5, -mH / 2 + 2.0, 2, 0.6);

      // Vorschlag 4: Schmauch- & Brandspuren durch austretende Pulvergase um das Auswurffenster
      if (gunHeat > 0.05) {
        const sootA = Math.min(0.65, gunHeat * 0.70).toFixed(3);
        const portSoot = ctx.createRadialGradient(mX + 5, -mH / 2 + 2.5, 1, mX + 5, -mH / 2 + 2.5, 8.5);
        portSoot.addColorStop(0, `rgba(10, 15, 25, ${sootA})`);
        portSoot.addColorStop(0.5, `rgba(45, 25, 15, ${(sootA * 0.45).toFixed(3)})`);
        portSoot.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = portSoot;
        ctx.fillRect(mX - 2, -mH / 2 - 2, 16, 9);
      }

      // Vorschlag 5: STENCIL-AUFDRUCK AUF DER BLENDE ("CAL-75")
      ctx.save();
      ctx.font = 'bold 5.5px "Share Tech Mono", monospace';
      ctx.fillStyle = team === 'western' ? 'rgba(15, 23, 42, 0.65)' : 'rgba(45, 14, 6, 0.65)'; // Dunkler Stencil-Schriftzug auf hellem Stahl
      ctx.fillText('CAL-75', mX + 2.0, -mH / 2 + 8.5);
      ctx.restore();

      // Vorschlag 3: TAKTISCHER SPLITTERSCHUTZ / PANZERSCHILD (Gun Shield im hellgrauen Stahl)
      const shieldX = mX - 1;
      const shieldW = 14;
      const shieldWingH = 5;

      // Oberer angewinkelter Schildflügel
      ctx.fillStyle = '#080c14';
      ctx.beginPath();
      ctx.moveTo(shieldX, -mH / 2);
      ctx.lineTo(shieldX - 4, -mH / 2 - shieldWingH);
      ctx.lineTo(shieldX + shieldW - 3, -mH / 2 - shieldWingH);
      ctx.lineTo(shieldX + shieldW, -mH / 2);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = HEAD_BASE;
      ctx.beginPath();
      ctx.moveTo(shieldX + 0.5, -mH / 2);
      ctx.lineTo(shieldX - 3.2, -mH / 2 - shieldWingH + 0.5);
      ctx.lineTo(shieldX + shieldW - 3.5, -mH / 2 - shieldWingH + 0.5);
      ctx.lineTo(shieldX + shieldW - 0.5, -mH / 2);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(shieldX - 3, -mH / 2 - shieldWingH + 0.5, shieldW, 0.8);

      // Unterer angewinkelter Schildflügel
      ctx.fillStyle = '#080c14';
      ctx.beginPath();
      ctx.moveTo(shieldX, mH / 2);
      ctx.lineTo(shieldX - 4, mH / 2 + shieldWingH);
      ctx.lineTo(shieldX + shieldW - 3, mH / 2 + shieldWingH);
      ctx.lineTo(shieldX + shieldW, mH / 2);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = HEAD_SHADE;
      ctx.beginPath();
      ctx.moveTo(shieldX + 0.5, mH / 2);
      ctx.lineTo(shieldX - 3.2, mH / 2 + shieldWingH - 0.5);
      ctx.lineTo(shieldX + shieldW - 3.5, mH / 2 + shieldWingH - 0.5);
      ctx.lineTo(shieldX + shieldW - 0.5, mH / 2);
      ctx.closePath();
      ctx.fill();

      // Nieten am Panzerschild
      [shieldX - 1.5, shieldX + shieldW - 4].forEach(nx => {
        ctx.fillStyle = '#060a10';
        ctx.fillRect(nx, -mH / 2 - 3.5, 1.6, 1.6);
        ctx.fillStyle = '#cbd5e1';
        ctx.fillRect(nx + 0.3, -mH / 2 - 3.2, 1.0, 1.0);
      });

      // Optik-Sensorwürfel im Zentrum (taktisches Blinken)
      ctx.fillStyle = '#0a0f18';
      ctx.fillRect(mX + mW - 7, -3.5, 7, 7);
      const blink = (Math.sin(performance.now() * 0.006) + 1) * 0.5;
      if (team === 'western') {
        ctx.fillStyle = blink > 0.4 ? '#00f0ff' : '#0369a1';
        ctx.fillRect(mX + mW - 6, -2.5, 5, 5);
        ctx.fillStyle = blink > 0.6 ? '#ffffff' : '#38bdf8';
        ctx.fillRect(mX + mW - 4, -1.5, 2, 2);
      } else {
        ctx.fillStyle = blink > 0.4 ? '#ff0055' : '#881337';
        ctx.fillRect(mX + mW - 6, -2.5, 5, 5);
        ctx.fillStyle = blink > 0.6 ? '#ffffff' : '#fb7185';
        ctx.fillRect(mX + mW - 4, -1.5, 2, 2);
      }

      // Hintere Munitionskassette mit Kühlschlitzen
      ctx.fillStyle = ARM_STEEL;
      ctx.fillRect(mX - 4, -mH / 2 + 2, 4, mH - 4);
      ctx.fillStyle = '#0a0f18';
      ctx.fillRect(mX - 3, -4, 2, 2);
      ctx.fillRect(mX - 3, 2, 2, 2);

      // Vorschlag 5: Thermisches Hitzeflimmern über den Rohren (Heat Distortion Shimmer ab gunHeat > 0.35)
      if (gunHeat > 0.35) {
        const heatIntensity = Math.min(1.0, (gunHeat - 0.35) / 0.50);
        const waveT = t * 16;
        [-bSpacing, bSpacing].forEach(offY => {
          ctx.save();
          // Wabernde optische Heißluft-Schlieren direkt über dem Waffenrohr
          for (let hLayer = 1; hLayer <= 3; hLayer++) {
            const hDist = hLayer * 2.2;
            const hAlpha = (0.14 * heatIntensity * (1 - hLayer * 0.25)).toFixed(3);
            ctx.strokeStyle = `rgba(255, 255, 255, ${hAlpha})`;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            for (let bx = startX + 12; bx <= startX + barrelLen + 4; bx += 3) {
              const waveY = offY - hDist + Math.sin(waveT + bx * 0.38 + hLayer) * 0.9;
              if (bx === startX + 12) ctx.moveTo(bx, waveY);
              else ctx.lineTo(bx, waveY);
            }
            ctx.stroke();

            // Warmer Hitzeschleier
            ctx.strokeStyle = `rgba(254, 215, 170, ${(hAlpha * 0.55).toFixed(3)})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            for (let bx = startX + 14; bx <= startX + barrelLen + 2; bx += 3) {
              const waveY = offY - hDist - 0.4 + Math.cos(waveT * 1.2 + bx * 0.42) * 0.7;
              if (bx === startX + 14) ctx.moveTo(bx, waveY);
              else ctx.lineTo(bx, waveY);
            }
            ctx.stroke();
          }
          ctx.restore();
        });
      }

      // Thermisches Hitzeglühen der Rohre (erst nach min. 10 schnellen Schüssen)
      if (gunHeat > 0.65) {
        const glow = (gunHeat - 0.65) / 0.35;
        [-bSpacing, bSpacing].forEach(offY => {
          ctx.fillStyle = `rgba(239, 68, 68, ${(glow * 0.75).toFixed(2)})`;
          ctx.fillRect(startX + barrelLen - 6, offY - bW / 2, 5, bW);
          ctx.fillStyle = `rgba(251, 146, 60, ${(glow * 0.85).toFixed(2)})`;
          ctx.fillRect(startX + barrelLen - 4, offY - bW / 2 + 0.5, 3, bW - 1);
        });
      }

      // Mündungsfeuer
      if (muzzleFlash > 0) {
        [-bSpacing, bSpacing].forEach(offY => {
          ctx.fillStyle = '#fef08a';
          ctx.beginPath();
          ctx.arc(startX + barrelLen + 6, offY, 8 * muzzleFlash, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(startX + barrelLen + 3, offY, 4 * muzzleFlash, 0, Math.PI * 2);
          ctx.fill();
        });
      }

      // Patronenhülsen-Auswurf aus der Kanone (2 Hülsen für die Zwillingsrohre)
      if (casingsToEject > 0) {
        casingsToEject = 0;
        const cosA = Math.cos(aimDir);
        const sinA = Math.sin(aimDir);

        [-4.5, 4.5].forEach((ejectOffY, bIdx) => {
          const localX = (startX - 8) * 0.75;
          const localY = ejectOffY * 0.75;
          const wx = wristX + (localX * cosA - localY * sinA);
          const wy = wristY + (localX * sinA + localY * cosA);
          const spawnX = sx + (wx - sx) * 0.88;
          const spawnY = gy + (wy - gy) * 0.88;

          // Auswurf-Impuls: rückwärts & bogenförmig nach oben
          const vBack = 0.55 + Math.random() * 0.35;
          const vUp = 2.2 + Math.random() * 0.7 + (bIdx === 0 ? 0.35 : 0);
          const vLat = (dir * 0.3) + (Math.random() - 0.5) * 0.25;

          const vx = -cosA * vBack + vLat;
          const vy = -sinA * vBack - vUp;

          spentShellCasings.push({
            x: spawnX,
            y: spawnY,
            vx: vx,
            vy: vy,
            angle: aimDir + Math.PI / 2 + (Math.random() - 0.5) * 0.6,
            spin: (dir * -0.28) + (Math.random() - 0.5) * 0.35,
            groundY: gy + 2.0 + (Math.random() - 0.5) * 5.0,
            bounces: 0,
            maxBounces: 2,
            isResting: false,
            restAge: 0,
            maxRestAge: 220 + Math.random() * 60,
            length: 5.2,
            width: 2.2,
            smokeCooldown: 8
          });
        });
      }

      // 3. Zarter Mündungsrauch (subtiles Ausdampfen aus den heißen Rohren nach dem Schuss)
      if (smokeCooldown > 0) {
        smokeCooldown--;
        const cosA = Math.cos(aimDir);
        const sinA = Math.sin(aimDir);
        [-bSpacing, bSpacing].forEach(offY => {
          const lx = (startX + barrelLen + 5) * 0.75;
          const ly = offY * 0.75;
          const wx = wristX + (lx * cosA - ly * sinA);
          const wy = wristY + (lx * sinA + ly * cosA);
          const finalWx = sx + (wx - sx) * 0.88;
          const finalWy = gy + (wy - gy) * 0.88;
          gunSmokeParticles.push({
            x: finalWx,
            y: finalWy,
            vx: cosA * (0.75 + Math.random() * 0.4) + (Math.random() - 0.5) * 0.25,
            vy: sinA * (0.75 + Math.random() * 0.4) - 0.35 - Math.random() * 0.3,
            age: 0,
            maxLife: 20 + Math.random() * 6,
            size: 1.1 + Math.random() * 0.4
          });
        });
      }

      ctx.restore();

      // 1. Mündungsfeuer-Lichtschein (erhellt flüchtig Roboterarm, Deck & Modulfront)
      if (muzzleFlash > 0) {
        const cosA = Math.cos(aimDir);
        const sinA = Math.sin(aimDir);
        const lx = (startX + barrelLen + 6) * 0.75;
        const flashX = wristX + lx * cosA;
        const flashY = wristY + lx * sinA;

        ctx.save();
        const flashGrad = ctx.createRadialGradient(flashX, flashY, 2, flashX, flashY, 72);
        flashGrad.addColorStop(0, `rgba(254, 240, 138, ${(0.32 * muzzleFlash).toFixed(3)})`);
        flashGrad.addColorStop(0.35, `rgba(245, 158, 11, ${(0.13 * muzzleFlash).toFixed(3)})`);
        flashGrad.addColorStop(1, 'rgba(245, 158, 11, 0)');
        ctx.fillStyle = flashGrad;
        ctx.beginPath();
        ctx.arc(flashX, flashY, 72, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      ctx.restore(); // Stellt Skalierung 0.88 wieder her

    return { wristX, wristY };
  }

  _renderBaseShellCasings(ctx, base) {
    if (this.quality === 'low') return;

    // 1. Spent Casings
    if (base._spentShellCasings) {
      const casings = base._spentShellCasings;
      const sparks = base._casingImpactSparks || (base._casingImpactSparks = []);
      const smoke = base._gunSmokeParticles || (base._gunSmokeParticles = []);

      for (let i = casings.length - 1; i >= 0; i--) {
        const c = casings[i];
        if (!c.isResting) {
          c.x += c.vx;
          c.y += c.vy;
          c.vy += 0.36;
          c.vx *= 0.975;
          c.angle += c.spin;

          if (c.smokeCooldown > 0 && c.smokeCooldown % 3 === 0) {
            c.smokeCooldown--;
            const p = _casingSmokePool.pop() || {};
            p.x = c.x;
            p.y = c.y;
            p.vx = (Math.random() - 0.5) * 0.15;
            p.vy = -0.25 - Math.random() * 0.15;
            p.age = 0;
            p.maxLife = 10 + Math.random() * 5;
            p.size = 0.7;
            smoke.push(p);
          }

          if (c.y >= c.groundY) {
            c.y = c.groundY;
            if (Math.abs(c.vy) > 0.6 && c.bounces < c.maxBounces) {
              c.bounces++;
              if (c.bounces === 1) {
                for (let s = 0; s < 3; s++) {
                  const sp = _casingSparkPool.pop() || {};
                  sp.x = c.x + (Math.random() - 0.5) * 2.0;
                  sp.y = c.y - 0.5;
                  sp.vx = (Math.random() - 0.5) * 1.6 + c.vx * 0.3;
                  sp.vy = -1.2 - Math.random() * 1.0;
                  sp.age = 0;
                  sp.maxLife = 5 + Math.floor(Math.random() * 4);
                  sparks.push(sp);
                }
              }
              c.vy = -c.vy * (0.32 + Math.random() * 0.12);
              c.vx *= 0.58;
              c.spin = (Math.random() - 0.5) * 0.22;
            } else {
              c.isResting = true;
              c.vy = 0;
              c.vx = 0;
              c.spin = 0;
              c.angle = (c.angle % Math.PI);
              if (Math.abs(c.angle) > Math.PI / 4) {
                c.angle = Math.PI / 2 + (Math.random() - 0.5) * 0.25;
              } else {
                c.angle = (Math.random() - 0.5) * 0.25;
              }
            }
          }
        } else {
          c.restAge = (c.restAge || 0) + 1;
          if (c.restAge >= (c.maxRestAge || 220)) {
            casings.splice(i, 1);
            continue;
          }
        }

        ctx.save();
        const rem = (c.maxRestAge || 220) - (c.restAge || 0);
        ctx.globalAlpha = rem < 40 ? (rem / 40) : 1.0;

        const sDist = Math.max(0, c.groundY - c.y);
        const sAlpha = Math.max(0.12, 0.45 - sDist * 0.025);
        ctx.fillStyle = `rgba(5, 8, 16, ${Math.round(sAlpha * 100) / 100})`;
        ctx.beginPath();
        ctx.ellipse(c.x, c.groundY, 3.2 + sDist * 0.08, 1.4, 0, 0, Math.PI * 2);
        ctx.fill();

        ctx.translate(c.x, c.y);
        ctx.rotate(c.angle);
        const halfL = c.length / 2;
        const halfW = c.width / 2;

        ctx.fillStyle = '#b45309';
        ctx.fillRect(-halfL, -halfW, 1.1, c.width);
        ctx.fillStyle = '#d97706';
        ctx.fillRect(-halfL + 1.1, -halfW, c.length - 1.1, c.width);
        ctx.fillStyle = '#fef08a';
        ctx.fillRect(-halfL + 0.8, -halfW + 0.3, c.length - 1.6, 0.6);
        ctx.fillStyle = '#78350f';
        ctx.fillRect(-halfL + 0.9, -halfW, 0.4, c.width);
        ctx.fillStyle = '#050810';
        ctx.fillRect(halfL - 0.6, -halfW + 0.2, 0.6, c.width - 0.4);
        ctx.restore();
      }
      if (casings.length > 30) casings.splice(0, casings.length - 30);
    }

    // 2. Impact Sparks
    if (base._casingImpactSparks) {
      const sparks = base._casingImpactSparks;
      for (let i = sparks.length - 1; i >= 0; i--) {
        const sp = sparks[i];
        sp.age++;
        sp.x += sp.vx;
        sp.y += sp.vy;
        sp.vy += 0.28;
        if (sp.age >= sp.maxLife) {
          const dead = sparks.splice(i, 1)[0];
          if (_casingSparkPool.length < 50) _casingSparkPool.push(dead);
        } else {
          ctx.fillStyle = sp.age <= 2 ? '#ffffff' : (sp.age <= 4 ? '#fef08a' : '#f59e0b');
          ctx.fillRect(Math.round(sp.x), Math.round(sp.y), 1.0, 1.0);
        }
      }
      while (sparks.length > 20) {
        const dead = sparks.shift();
        if (_casingSparkPool.length < 50) _casingSparkPool.push(dead);
      }
    }

    // 3. Smoke Puffs
    if (base._gunSmokeParticles) {
      const smoke = base._gunSmokeParticles;
      for (let i = smoke.length - 1; i >= 0; i--) {
        const p = smoke[i];
        p.age++;
        p.x += p.vx + Math.sin(p.age * 0.18) * 0.15;
        p.y += p.vy;
        p.vx *= 0.93;
        p.vy -= 0.04;
        p.size += 0.045;
        const alpha = Math.max(0, (1 - p.age / p.maxLife) * 0.20);
        if (p.age >= p.maxLife) {
          const dead = smoke.splice(i, 1)[0];
          if (_casingSmokePool.length < 50) _casingSmokePool.push(dead);
        } else {
          ctx.fillStyle = `rgba(195, 208, 222, ${Math.round(alpha * 1000) / 1000})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      while (smoke.length > 25) {
        const dead = smoke.shift();
        if (_casingSmokePool.length < 50) _casingSmokePool.push(dead);
      }
    }
  }

  _drawBunkerMonitorDynamic(ctx, base, x, y, dir, topDepth = MODULE_TOP_D, skewX = MODULE_SKEW, mainColor, glowColor, t, isLow) {
    const team = base.team;
    const recoilTimer = base.recoilTimer || 0;
    const enableGlow = !isLow;
    const teamGlow = team === 'western' ? '#00f0ff' : '#ff0055';
    const DOCK_BLOCK_W = 88;
    const DOCK_BLOCK_H = 65;
    const groundY = y + 26;
    const dockH = DOCK_BLOCK_H;
    const dockY = groundY - dockH;
    const dockX = dir === 1 ? x - 40 : x + 40 - DOCK_BLOCK_W;
    const wallH = dockH;
    const trimH = 6.5;
    const gateW = 41;
    const gateX = dir === 1 ? dockX + 5 : dockX + DOCK_BLOCK_W - gateW - 5;
    const ribW = 4.5;
    const ribX = dir === 1 ? gateX + gateW + 1 : gateX - ribW - 1;
    const cornerW = 5.5;
    const rightColX = dockX + DOCK_BLOCK_W - cornerW;
    const termX = dir === 1 ? ribX + ribW + 1.5 : dockX + 4;
    const termW = dir === 1 ? (rightColX - 1.5 - termX) : (DOCK_BLOCK_W - gateW - ribW - 9);
    const termBayH = wallH - trimH - 6;
    const dispBoxW = 20;
    const dispBoxH = 13;
    const dispBaseX = termX + (termW - dispBoxW) / 2;
    const dispBaseY = dockY + 6;
    const protrudeY = 4;
    const protrudeX = dir * 1.0;
    const fX = dispBaseX + protrudeX;
    const fY = dispBaseY + protrudeY;
    const scrW = 12;
    const scrH = 7.5;
    const scrX = fX + 2.2;
    const scrY = fY + 2.5;

    const isShotAlarm = (recoilTimer || 0) > 0.05;
    const alertFactor = Math.min(1.0, (recoilTimer || 0) * 1.5);

    ctx.fillStyle = '#0a0f18';
    ctx.fillRect(scrX - 1, scrY - 1, scrW + 2, scrH + 2);
    ctx.fillStyle = isShotAlarm ? '#1f0404' : (team === 'western' ? '#021824' : '#1c0701');
    ctx.fillRect(scrX, scrY, scrW, scrH);

    if (enableGlow) {
      this._crtGlowLUTs = this._crtGlowLUTs || new Map();
      const crtKey = isShotAlarm ? 'alarm' : (team === 'western' ? 'western' : 'eastern');
      let crtCanvas = this._crtGlowLUTs.get(crtKey);
      if (!crtCanvas) {
        if (typeof document !== 'undefined') {
          crtCanvas = document.createElement('canvas');
          crtCanvas.width = 32;
          crtCanvas.height = 32;
          const cctx = crtCanvas.getContext('2d');
          const g = cctx.createRadialGradient(16, 16, 2, 16, 16, 15);
          if (crtKey === 'alarm') {
            g.addColorStop(0, 'rgba(239, 68, 68, 0.35)');
            g.addColorStop(0.6, 'rgba(239, 68, 68, 0.12)');
          } else if (crtKey === 'western') {
            g.addColorStop(0, 'rgba(0, 240, 255, 0.22)');
            g.addColorStop(0.6, 'rgba(0, 240, 255, 0.08)');
          } else {
            g.addColorStop(0, 'rgba(255, 0, 85, 0.22)');
            g.addColorStop(0.6, 'rgba(255, 0, 85, 0.08)');
          }
          g.addColorStop(1, 'rgba(0, 0, 0, 0.0)');
          cctx.fillStyle = g;
          cctx.fillRect(0, 0, 32, 32);
          this._crtGlowLUTs.set(crtKey, crtCanvas);
        }
      }
      if (crtCanvas) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(termX, dockY + 4, termW, termBayH);
        ctx.clip();
        if (isShotAlarm) {
          ctx.globalAlpha = alertFactor;
        }
        ctx.drawImage(crtCanvas, scrX + scrW / 2 - 16, scrY + scrH / 2 - 16, 32, 32);
        ctx.restore();
      }

      ctx.fillStyle = isShotAlarm
        ? `rgba(239, 68, 68, ${Math.round((0.20 + alertFactor * 0.25) * 100) / 100})`
        : (team === 'western' ? 'rgba(0, 240, 255, 0.25)' : 'rgba(255, 0, 85, 0.25)');
      ctx.fillRect(scrX, scrY, scrW, scrH);
    }

    ctx.strokeStyle = isShotAlarm ? '#ef4444' : teamGlow;
    ctx.lineWidth = isShotAlarm ? 1.1 : 0.9;
    ctx.beginPath();
    const spike = (recoilTimer || 0) * 2.8;
    const waveFreq = (recoilTimer > 0) ? 14 : 9;
    for (let px = 0; px < scrW; px += 1.5) {
      const noise = spike > 0 ? ((px % 3 === 0 ? 1.0 : -0.7) * spike) : 0;
      const py = Math.max(scrY + 0.8, Math.min(scrY + scrH - 0.8, scrY + scrH / 2 + Math.sin(t * waveFreq + px * 1.1) * (1.8 + spike * 0.3) + noise));
      if (px === 0) ctx.moveTo(scrX + px, py);
      else ctx.lineTo(scrX + px, py);
    }
    ctx.stroke();

    if (enableGlow) {
      if (isShotAlarm && alertFactor > 0.4) {
        ctx.strokeStyle = '#fecaca';
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        for (let px = 0; px < scrW; px += 1.5) {
          const noise = spike > 0 ? ((px % 3 === 0 ? 1.0 : -0.7) * spike) : 0;
          const py = Math.max(scrY + 0.8, Math.min(scrY + scrH - 0.8, scrY + scrH / 2 + Math.sin(t * waveFreq + px * 1.1) * (1.8 + spike * 0.3) + noise));
          if (px === 0) ctx.moveTo(scrX + px, py);
          else ctx.lineTo(scrX + px, py);
        }
        ctx.stroke();
      }

      const scanY = scrY + (Math.sin(t * 4) * 0.5 + 0.5) * (scrH - 1);
      ctx.fillStyle = isShotAlarm ? 'rgba(254, 202, 202, 0.55)' : 'rgba(255, 255, 255, 0.45)';
      ctx.fillRect(scrX, scanY, scrW, 1);
    }

    const ledX = fX + dispBoxW - 3.5;
    ctx.fillStyle = isShotAlarm ? '#ef4444' : '#22c55e';
    ctx.beginPath();
    ctx.arc(ledX, fY + 3.5, 1.2, 0, Math.PI * 2);
    ctx.fill();

    const blinkRed = Math.floor(t * 3) % 2 === 0;
    ctx.fillStyle = blinkRed ? '#ef4444' : '#7f1d1d';
    ctx.beginPath();
    ctx.arc(ledX, fY + 8, 1.2, 0, Math.PI * 2);
    ctx.fill();
  }

  renderBase(ctx, base, mainColor, glowColor) {
    if (base?.race === 'Sul') {
      this.renderSulBase(ctx, base, mainColor, glowColor);
      return;
    }
    ctx.save();
    const x = base.x;
    const y = base.y;
    const BASE_SCALE = 0.864; // +8% proportional hochskaliert (0.8 * 1.08)
    ctx.translate(x, y);
    ctx.scale(BASE_SCALE, BASE_SCALE);
    ctx.translate(-x, -y);
    const isWestern = base.team === 'western';
    const dir = isWestern ? 1 : -1;
    const t = performance.now() * 0.001;
    const isLow = this.quality === 'low';

    const topDepth = MODULE_TOP_D;
    const skewX = MODULE_SKEW;
    const DOCK_BLOCK_W = 88;
    const groundY = y + 26;
    const dockX = dir === 1 ? x - 40 : x + 40 - DOCK_BLOCK_W;
    const bAnchorX = dockX + DOCK_BLOCK_W / 2;
    const bAnchorY = groundY;
    const BLDG_SCALE = 1.06;

    if (!this._staticBasePrimed) {
      // Uncached fallback if called without ensureStaticBases
      ctx.save();
      ctx.translate(bAnchorX, bAnchorY);
      ctx.scale(BLDG_SCALE, BLDG_SCALE);
      ctx.translate(-bAnchorX, -bAnchorY);
      this._drawBuildingGroundShadow(ctx, x, y, dir, topDepth, skewX);
      ctx.restore();

      ctx.save();
      ctx.translate(bAnchorX, bAnchorY);
      ctx.scale(BLDG_SCALE, BLDG_SCALE);
      ctx.translate(-bAnchorX, -bAnchorY);
      this._drawBunkerPlatform(ctx, base, x, y, dir, topDepth, skewX, mainColor, glowColor, t, isLow);
      ctx.restore();
    } else {
      // Pre-baked path: foundation and ground shadow are already blitted via _staticBaseCanvas.
      // Only render the animated CRT monitor screen and blinking status LEDs.
      ctx.save();
      ctx.translate(bAnchorX, bAnchorY);
      ctx.scale(BLDG_SCALE, BLDG_SCALE);
      ctx.translate(-bAnchorX, -bAnchorY);
      this._drawBunkerMonitorDynamic(ctx, base, x, y, dir, topDepth, skewX, mainColor, glowColor, t, isLow);
      ctx.restore();
    }

    ctx.restore();
  }

  // The cannon mount (wall terminal, pylon, cables, turntable bolts) is ~57
  // fill/stroke calls and two gradient objects that read no time, no base
  // state and no randomness - yet it was re-issued twice per frame, every
  // frame, on every quality tier. It is baked once per base (2x, tight bounds
  // measured from the actual pixels so no geometry can be clipped) and blitted
  // at the exact same z-position inside the same transform. Falls back to the
  // direct vector draw wherever offscreen canvases are unavailable.
  _drawCannonMountCached(ctx, base, flankGroundX, mountX, mountY, dir, scaledFlankX, scaledSkewX, scaledTopDepth, groundY) {
    const team = base.team;
    // Kept on the renderer, not on `base`: the base object is simulation state that
    // snapshots/clones walk, and must never carry a canvas.
    const mountCaches = this._mountCaches || (this._mountCaches = {});
    let c = mountCaches[team];
    if (!c || c.team !== team || c.mountX !== mountX || c.mountY !== mountY || c.flankX !== flankGroundX ||
        c.dir !== dir || c.skew !== scaledSkewX || c.depth !== scaledTopDepth || c.groundY !== groundY) {
      c = this._bakeCannonMount(team, flankGroundX, mountX, mountY, dir, scaledFlankX, scaledSkewX, scaledTopDepth, groundY);
      mountCaches[team] = c;
    }
    if (c.canvas) {
      ctx.drawImage(c.canvas, c.ox, c.oy, c.w, c.h);
    } else {
      this._drawCannonMountAndCables(ctx, team, flankGroundX, mountX, mountY, dir, scaledFlankX, scaledSkewX, scaledTopDepth, groundY);
    }
    return c.turretMount;
  }

  _bakeCannonMount(team, flankGroundX, mountX, mountY, dir, scaledFlankX, scaledSkewX, scaledTopDepth, groundY) {
    const entry = {
      team, mountX, mountY, flankX: flankGroundX, dir, skew: scaledSkewX, depth: scaledTopDepth, groundY,
      canvas: null, ox: 0, oy: 0, w: 0, h: 0,
      turretMount: { x: mountX, y: mountY - 4 * 0.88, groundY: mountY }
    };
    if (typeof document === 'undefined') return entry;
    try {
      const SCALE = 2;
      const margin = 90;
      const minX = Math.min(mountX, flankGroundX, scaledFlankX) - margin;
      const maxX = Math.max(mountX, flankGroundX, scaledFlankX) + margin;
      const minY = mountY - margin - 40;
      const maxY = mountY + margin;
      const W = Math.ceil((maxX - minX) * SCALE);
      const H = Math.ceil((maxY - minY) * SCALE);
      const full = document.createElement('canvas');
      full.width = W;
      full.height = H;
      const fctx = full.getContext('2d', { willReadFrequently: true });
      if (!fctx || typeof fctx.getImageData !== 'function') return entry;
      fctx.setTransform(SCALE, 0, 0, SCALE, -minX * SCALE, -minY * SCALE);
      entry.turretMount = this._drawCannonMountAndCables(fctx, team, flankGroundX, mountX, mountY, dir, scaledFlankX, scaledSkewX, scaledTopDepth, groundY) || entry.turretMount;
      // Tight bounds from the real pixels (alpha > 0), padded by 2 px.
      const data = fctx.getImageData(0, 0, W, H).data;
      let x0 = W, y0 = H, x1 = -1, y1 = -1;
      for (let y = 0; y < H; y++) {
        const row = y * W * 4;
        for (let x = 0; x < W; x++) {
          if (data[row + x * 4 + 3] !== 0) {
            if (x < x0) x0 = x;
            if (x > x1) x1 = x;
            if (y < y0) y0 = y;
            if (y > y1) y1 = y;
          }
        }
      }
      if (x1 < 0) return entry; // nothing drawn - keep the direct path (also a safe default)
      // An edge touching the bake rectangle means the margin clipped real geometry: don't bake.
      if (x0 === 0 || y0 === 0 || x1 === W - 1 || y1 === H - 1) return entry;
      x0 = Math.max(0, x0 - 2); y0 = Math.max(0, y0 - 2);
      x1 = Math.min(W - 1, x1 + 2); y1 = Math.min(H - 1, y1 + 2);
      const cw = x1 - x0 + 1;
      const ch = y1 - y0 + 1;
      const out = document.createElement('canvas');
      out.width = cw;
      out.height = ch;
      out.getContext('2d').drawImage(full, x0, y0, cw, ch, 0, 0, cw, ch);
      entry.canvas = out;
      entry.ox = minX + x0 / SCALE;
      entry.oy = minY + y0 / SCALE;
      entry.w = cw / SCALE;
      entry.h = ch / SCALE;
      full.width = 1; full.height = 1; // release the scratch backing store (iOS canvas memory)
    } catch (_) {
      entry.canvas = null;
    }
    return entry;
  }

  renderBaseTurret(ctx, battlefield, base, mainColor, glowColor) {
    if (base?.race === 'Sul') {
      this.renderSulBaseTurret(ctx, battlefield, base, mainColor, glowColor);
      return;
    }
    ctx.save();
    const isWestern = base.team === 'western';
    const dir = isWestern ? 1 : -1;
    const t = performance.now() * 0.001;
    const isLow = this.quality === 'low';

    const x = base.x;
    const y = base.y;
    const BASE_SCALE = 0.864; // +8% proportional hochskaliert (0.8 * 1.08)
    ctx.translate(x, y);
    ctx.scale(BASE_SCALE, BASE_SCALE);
    ctx.translate(-x, -y);

    const topDepth = MODULE_TOP_D;
    const skewX = MODULE_SKEW;
    const DOCK_BLOCK_W = 88;
    const groundY = y + 26;
    const dockX = dir === 1 ? x - 40 : x + 40 - DOCK_BLOCK_W;
    const bAnchorX = dockX + DOCK_BLOCK_W / 2;
    const bAnchorY = groundY;
    const BLDG_SCALE = 1.06;

    const scaledFlankX = bAnchorX + dir * (DOCK_BLOCK_W / 2 * BLDG_SCALE);
    const scaledTopDepth = topDepth * BLDG_SCALE;
    const scaledSkewX = skewX * BLDG_SCALE;
    const mountY = groundY - scaledTopDepth * 0.5 - 3;
    const flankGroundX = scaledFlankX + dir * (scaledSkewX * 0.5);
    const mountX = flankGroundX + dir * 38;

    // Sentry Surveillance AI vs Enemy Tracking
    const tx = mountX;
    const ty = mountY;
    let aimDir;
    if (base.targetEnemy) {
      const enemyAngle = Math.atan2(base.targetEnemy.y - ty, base.targetEnemy.x - tx);
      base._gunTargetAngle = enemyAngle;
      if (base._gunCurrentAngle === undefined) base._gunCurrentAngle = enemyAngle;
      let diff = enemyAngle - base._gunCurrentAngle;
      diff = ((diff + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
      base._gunCurrentAngle += diff * 0.15;
      base._gunCurrentAngle = ((base._gunCurrentAngle + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
      if (base._sentry) {
        base._sentry.currentAngle = base._gunCurrentAngle;
      }
      aimDir = base._gunCurrentAngle;
    } else {
      if (!base._sentry) {
        base._sentry = {
          targetAngle: dir === 1 ? 0 : Math.PI,
          holdUntil: 0,
          currentAngle: base._gunCurrentAngle !== undefined ? base._gunCurrentAngle : (dir === 1 ? 0 : Math.PI)
        };
      }
      const s = base._sentry;
      if (base._gunCurrentAngle !== undefined && Math.abs(base._gunCurrentAngle - s.currentAngle) > 0.01) {
        let syncDiff = base._gunCurrentAngle - s.currentAngle;
        syncDiff = ((syncDiff + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
        if (Math.abs(syncDiff) > 0.05) {
          s.currentAngle = base._gunCurrentAngle;
        }
      }
      const now = performance.now();
      if (now >= s.holdUntil) {
        const targetOffset = (Math.random() * 0.60 - 0.30);
        let tAng = (dir === 1 ? 0 : Math.PI) - targetOffset;
        s.targetAngle = ((tAng + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
        s.holdUntil = now + 1500 + Math.random() * 1500;
      }
      const microDrift = Math.sin(now * 0.0018) * 0.012;
      const desiredAngle = s.targetAngle + microDrift;
      let sentryDiff = desiredAngle - s.currentAngle;
      sentryDiff = ((sentryDiff + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
      s.currentAngle += sentryDiff * 0.055;
      s.currentAngle = ((s.currentAngle + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
      base._gunCurrentAngle = s.currentAngle;
      aimDir = s.currentAngle;
    }

    // Thermal gun heat tracking & ejection trigger
    if (base._lastRecoilTimer === undefined) base._lastRecoilTimer = 0;
    if (base.recoilTimer > 0.18 && base._lastRecoilTimer <= 0.18) {
      base._gunHeat = Math.min(1.0, (base._gunHeat || 0) + 0.12);
      base._casingsToEject = 2;
      base._smokeCooldown = 5;
    }
    base._gunHeat = Math.max(0, (base._gunHeat || 0) - 0.0025);
    base._lastRecoilTimer = base.recoilTimer;

    // 1. Turntable Ground Shadow
    this._drawCannonTurntableShadow(ctx, mountX, mountY, dir);

    // 2. Cannon Mount, Pylon & Cables
    const turretMount = this._drawCannonMountCached(ctx, base, flankGroundX, mountX, mountY, dir, scaledFlankX, scaledSkewX, scaledTopDepth, groundY);

    // 3. Cannon Turret & Kinematic Arm
    this._drawTerraceTurret(ctx, base, turretMount, dir, aimDir, t, isLow);

    // 4. Shell Casings & Impact Sparks & Smoke
    this._renderBaseShellCasings(ctx, base);

    ctx.restore();
  }


  renderTargetingCrosshair(ctx) {
    ctx.save();
    const x = this.targetX;
    const y = this.targetY;
    const color = this.reticleColor || '#ff0033';
    const isSul = color === '#a855f7' || color === '#d946ef';

    ctx.strokeStyle = color;
    ctx.fillStyle = color;

    if (isSul) {
      // Outer Amethyst dashed targeting circle
      ctx.lineWidth = 2.5;
      ctx.setLineDash([8, 6]);
      ctx.beginPath();
      ctx.arc(x, y, 90, 0, Math.PI * 2);
      ctx.stroke();

      // Outer concentric bio-resonator ring
      ctx.lineWidth = 1.0;
      ctx.setLineDash([3, 5]);
      ctx.beginPath();
      ctx.arc(x, y, 102, 0, Math.PI * 2);
      ctx.stroke();

      // Diamond reticle crosshairs
      ctx.setLineDash([]);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x - 36, y); ctx.lineTo(x - 14, y);
      ctx.moveTo(x + 14, y); ctx.lineTo(x + 36, y);
      ctx.moveTo(x, y - 36); ctx.lineTo(x, y - 14);
      ctx.moveTo(x, y + 14); ctx.lineTo(x, y + 36);
      ctx.stroke();

      // Central diamond focus
      ctx.beginPath();
      ctx.moveTo(x, y - 7);
      ctx.lineTo(x + 7, y);
      ctx.lineTo(x, y + 7);
      ctx.lineTo(x - 7, y);
      ctx.closePath();
      ctx.stroke();

      // Corner tick brackets
      const tick = 10;
      const dist = 60;
      ctx.beginPath();
      ctx.moveTo(x - dist, y - dist + tick); ctx.lineTo(x - dist, y - dist); ctx.lineTo(x - dist + tick, y - dist);
      ctx.moveTo(x + dist - tick, y - dist); ctx.lineTo(x + dist, y - dist); ctx.lineTo(x + dist, y - dist + tick);
      ctx.moveTo(x - dist, y + dist - tick); ctx.lineTo(x - dist, y + dist); ctx.lineTo(x - dist + tick, y + dist);
      ctx.moveTo(x + dist - tick, y + dist); ctx.lineTo(x + dist, y + dist); ctx.lineTo(x + dist, y + dist - tick);
      ctx.stroke();

      ctx.font = 'bold 12px monospace';
      ctx.textAlign = 'center';
      if (this.quality !== 'low') {
        ctx.save();
        ctx.strokeStyle = '#d946ef';
        ctx.lineWidth = 3;
        ctx.strokeText("TARGET PLASMA STRIKE", x, y - 44);
        ctx.restore();
      }
      ctx.fillText("TARGET PLASMA STRIKE", x, y - 44);
    } else {
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 6]);

      // Outer targeting circle
      ctx.beginPath();
      ctx.arc(x, y, 90, 0, Math.PI * 2);
      ctx.stroke();

      // Crosshair lines
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(x - 30, y); ctx.lineTo(x + 30, y);
      ctx.moveTo(x, y - 30); ctx.lineTo(x, y + 30);
      ctx.stroke();

      ctx.font = 'bold 12px monospace';
      ctx.textAlign = 'center';
      if (this.quality !== 'low') {
        ctx.save();
        ctx.strokeStyle = '#ff0033';
        ctx.lineWidth = 3;
        ctx.strokeText("TARGET NUCLEAR STRIKE", x, y - 40);
        ctx.restore();
      }
      ctx.fillText("TARGET NUCLEAR STRIKE", x, y - 40);
    }

    ctx.restore();
  }

  renderDeployBeacons(ctx, battlefield) {
    // Spawnpunkt vor der Basis getilgt: Einheiten spawnen verteilt auf der Lane
    // und besitzen ihre eigene Spawnanimation in render/units.js.
  }

  renderMetamorphosisFogEvent(ctx, battlefield, nowSec) {
    const timer = battlefield.metamorphosisFogTimer || 0;
    const totalDuration = 4.5;
    const progress = Math.max(0, Math.min(1.0, 1.0 - (timer / totalDuration)));

    let alpha = 0;
    if (progress < 0.33) {
      alpha = (progress / 0.33) * 0.45;
    } else if (progress < 0.55) {
      alpha = 0.45;
    } else {
      alpha = (1.0 - ((progress - 0.55) / 0.45)) * 0.45;
    }

    if (alpha <= 0.005) return;

    ctx.save();
    const laneY = battlefield.laneY || 120;
    const laneH = battlefield.laneHeight || 320;
    const w = this.width || 1600;

    // 1. Dual-Edge Rolling Fog Gradients across lane (Paradigma 3: Reusable Pre-Baked Strip)
    const fogStrip = getMetaFogStripCanvas();
    if (fogStrip) {
      ctx.globalAlpha = alpha;
      ctx.drawImage(fogStrip, 0, laneY - 20, w, laneH + 40);
    }

    // 2. Rolling Cloud Banks & Spores
    if (this.quality !== 'low') {
      const billowCount = 6;
      const billowCanvas = getMetaBillowCanvas();
      ctx.globalAlpha = alpha * 0.6;
      for (let i = 0; i < billowCount; i++) {
        const bx = ((nowSec * 45 + i * (w / billowCount)) % (w + 200)) - 100;
        const by = laneY + 40 + Math.sin(nowSec * 1.5 + i) * (laneH * 0.4);
        const br = 140 + Math.sin(i * 2.3) * 30;

        if (billowCanvas) {
          ctx.drawImage(billowCanvas, bx - br, by - br, br * 2, br * 2);
        }
      }

      // 3. Floating Bioluminescent Green Spores
      ctx.globalAlpha = alpha * 0.9;
      ctx.fillStyle = '#bef264';
      for (let s = 0; s < 12; s++) {
        const sx = ((s * 137 + nowSec * 25) % w);
        const sy = laneY + 20 + ((s * 73 + nowSec * 15) % (laneH - 40));
        const pulse = 1.0 + Math.sin(nowSec * 4 + s) * 0.5;
        ctx.fillRect(sx - pulse, sy - pulse, pulse * 2, pulse * 2);
      }
    }

    ctx.restore();
  }

  renderPrecisionStrikes(ctx, battlefield, nowSec) {
    if (!battlefield?.activePrecisionStrikes || battlefield.activePrecisionStrikes.length === 0) return;

    for (let i = 0; i < battlefield.activePrecisionStrikes.length; i++) {
      const strike = battlefield.activePrecisionStrikes[i];
      const sx = strike.shadowX;
      const sy = strike.shadowY || (battlefield.laneY + battlefield.laneHeight * 0.5);

      // 1. Volumetric Overflight Shadow
      if (sx >= -300 && sx <= (this.width || 1600) + 300) {
        // A. Ambient Sun-Blotting Vignette ("The sun stops shining for half a second. Maybe forever.")
        if (this.quality !== 'low') {
          const dimCanvas = getPrecisionStrikeSunDimCanvas();
          if (dimCanvas) {
            ctx.drawImage(dimCanvas, sx - 260, sy - 260, 520, 520);
          } else {
            ctx.save();
            const sunDimGrad = ctx.createRadialGradient(sx, sy, 40, sx, sy, 260);
            sunDimGrad.addColorStop(0, 'rgba(12, 6, 22, 0.36)');
            sunDimGrad.addColorStop(0.5, 'rgba(10, 5, 20, 0.18)');
            sunDimGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
            ctx.fillStyle = sunDimGrad;
            ctx.beginPath();
            ctx.arc(sx, sy, 260, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
          }
        }

        // B. Giant Winged Bomber Creature Shadow (Multi-Jointed Organic Flap Dynamics)
        ctx.save();
        ctx.translate(sx, sy);
        if (strike.shadowDir < 0) ctx.scale(-1, 1);
        ctx.scale(1.25, 0.65); // Isometric floor perspective projection

        // Organic harmonic wing beat cycle (phased root -> elbow -> wingtip flexion)
        const tFlap = nowSec * 5.2;
        const rootBeat = Math.sin(tFlap);
        const midBeat  = Math.sin(tFlap - 0.45);
        const tipBeat  = Math.sin(tFlap - 0.90);
        const tailWave1 = Math.sin(tFlap - 1.2) * 5;
        const tailWave2 = Math.sin(tFlap - 1.8) * 9;

        // Head / crest lunging forward with stroke
        const headX = 96 + rootBeat * 4;
        const headY = rootBeat * -1.5;

        // Dynamic Left Wing joints (y < 0)
        const lElbowX = 8 + midBeat * 6;
        const lElbowY = -72 + midBeat * 20;

        const lTipX   = -26 + tipBeat * 14;
        const lTipY   = -148 + tipBeat * 36;

        const lFin1X  = -42 + tipBeat * 8;
        const lFin1Y  = -116 + midBeat * 24;

        const lFin2X  = -52 + midBeat * 6;
        const lFin2Y  = -82 + midBeat * 16;

        const lFin3X  = -50 + rootBeat * 4;
        const lFin3Y  = -48 + rootBeat * 10;

        const lRootX  = -36;
        const lRootY  = -22;

        // Symmetrical Right Wing joints (y > 0)
        const rElbowX = lElbowX;
        const rElbowY = -lElbowY;

        const rTipX   = lTipX;
        const rTipY   = -lTipY;

        const rFin1X  = lFin1X;
        const rFin1Y  = -lFin1Y;

        const rFin2X  = lFin2X;
        const rFin2Y  = -lFin2Y;

        const rFin3X  = lFin3X;
        const rFin3Y  = -lFin3Y;

        const rRootX  = lRootX;
        const rRootY  = -lRootY;

        // Undulating segmented tail
        const tailMidX = -75;
        const tailMidY = tailWave1;
        const tailTipX = -112 + Math.cos(tFlap) * 3;
        const tailTipY = tailWave2;

        ctx.fillStyle = 'rgba(10, 6, 20, 0.48)'; // Volumetric dark shadow over ground and units

        // Outer Wing Membrane Silhouette
        ctx.beginPath();
        ctx.moveTo(headX, headY);
        ctx.quadraticCurveTo(65, -14, 28, -18);
        ctx.quadraticCurveTo(lElbowX + 15, lElbowY * 0.75, lElbowX, lElbowY);
        ctx.quadraticCurveTo((lElbowX + lTipX) * 0.5 + 8, (lElbowY + lTipY) * 0.5, lTipX, lTipY);

        // Left Wing trailing edge scallops (fluttering membrane)
        ctx.quadraticCurveTo(lTipX - 8, (lTipY + lFin1Y) * 0.5, lFin1X, lFin1Y);
        ctx.quadraticCurveTo((lFin1X + lFin2X) * 0.5 + 4, (lFin1Y + lFin2Y) * 0.5, lFin2X, lFin2Y);
        ctx.quadraticCurveTo((lFin2X + lFin3X) * 0.5 + 4, (lFin2Y + lFin3Y) * 0.5, lFin3X, lFin3Y);
        ctx.quadraticCurveTo(lFin3X + 6, (lFin3Y + lRootY) * 0.5, lRootX, lRootY);

        // Segmented Chitin Tail
        ctx.quadraticCurveTo(-60, lRootY * 0.5 + tailMidY * 0.5, tailMidX, tailMidY);
        ctx.lineTo(tailTipX, tailTipY);
        ctx.lineTo(tailMidX, tailMidY * 0.5);
        ctx.quadraticCurveTo(-60, rRootY * 0.5 + tailMidY * 0.5, rRootX, rRootY);

        // Right Wing trailing edge scallops
        ctx.quadraticCurveTo(rFin3X + 6, (rFin3Y + rRootY) * 0.5, rFin3X, rFin3Y);
        ctx.quadraticCurveTo((rFin2X + rFin3X) * 0.5 + 4, (rFin2Y + rFin3Y) * 0.5, rFin2X, rFin2Y);
        ctx.quadraticCurveTo((rFin1X + rFin2X) * 0.5 + 4, (rFin1Y + rFin2Y) * 0.5, rFin1X, rFin1Y);
        ctx.quadraticCurveTo(rTipX - 8, (rTipY + rFin1Y) * 0.5, rTipX, rTipY);

        // Right Wing leading edge
        ctx.quadraticCurveTo((rElbowX + rTipX) * 0.5 + 8, (rElbowY + rTipY) * 0.5, rElbowX, rElbowY);
        ctx.quadraticCurveTo(rElbowX + 15, rElbowY * 0.75, 28, 18);
        ctx.quadraticCurveTo(65, 14, headX, headY);
        ctx.closePath();
        ctx.fill();

        // Dense Internal Skeletal Spars & Chitin Ribs (Internal Depth & Motion Accent)
        if (this.quality !== 'low') {
          ctx.strokeStyle = 'rgba(4, 2, 8, 0.45)';
          ctx.lineWidth = 4.5;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';

          ctx.beginPath();
          // Left wing primary spar (spine -> elbow -> tip)
          ctx.moveTo(20, -14);
          ctx.lineTo(lElbowX, lElbowY);
          ctx.lineTo(lTipX + 4, lTipY + 4);
          // Left wing finger struts
          ctx.moveTo(lElbowX, lElbowY);
          ctx.lineTo(lFin1X + 4, lFin1Y + 3);
          ctx.moveTo(lElbowX, lElbowY);
          ctx.lineTo(lFin2X + 4, lFin2Y + 2);

          // Right wing primary spar
          ctx.moveTo(20, 14);
          ctx.lineTo(rElbowX, rElbowY);
          ctx.lineTo(rTipX + 4, rTipY - 4);
          // Right wing finger struts
          ctx.moveTo(rElbowX, rElbowY);
          ctx.lineTo(rFin1X + 4, rFin1Y - 3);
          ctx.moveTo(rElbowX, rElbowY);
          ctx.lineTo(rFin2X + 4, rFin2Y - 2);

          // Dorsal spine ridge
          ctx.moveTo(70, 0);
          ctx.lineTo(-70, tailMidY * 0.6);
          ctx.stroke();
        }

        ctx.restore();
      }

      // 2. Falling Glowing Organic Spore Bomb
      if (strike.state === 'BOMB_FALLING' && strike.bombAltitude > 0) {
        const bx = strike.targetX;
        const by = strike.targetY - strike.bombAltitude;

        // Ground contact shadow under bomb (grows as bomb nears ground)
        const altFrac = Math.max(0, Math.min(1, strike.bombAltitude / 380));
        const groundShadowScale = Math.max(0.35, 1.0 - altFrac * 0.65);
        ctx.save();
        ctx.fillStyle = `rgba(0, 0, 0, ${0.45 * groundShadowScale})`;
        ctx.beginPath();
        ctx.ellipse(strike.targetX, strike.targetY, 14 * groundShadowScale, 7 * groundShadowScale, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        // The airborne organic ball itself
        ctx.save();
        ctx.translate(bx, by);

        const pulse = 1.0 + 0.12 * Math.sin(nowSec * 18.0);
        const radius = 10 * pulse;

        // Bioluminescent green gradient core
        const bombCanvas = getPrecisionStrikeSporeBombCanvas();
        if (bombCanvas) {
          ctx.drawImage(bombCanvas, -radius, -radius, radius * 2, radius * 2);
        } else {
          const ballGrad = ctx.createRadialGradient(-2, -2, 2, 0, 0, radius);
          ballGrad.addColorStop(0, '#ffffff');
          ballGrad.addColorStop(0.35, '#bef264');
          ballGrad.addColorStop(0.75, '#84cc16');
          ballGrad.addColorStop(1, '#4d7c0f');
          ctx.fillStyle = ballGrad;
          ctx.beginPath();
          ctx.arc(0, 0, radius, 0, Math.PI * 2);
          ctx.fill();
        }

        // Veined organic nucleus details
        ctx.fillStyle = '#1e3a0f';
        ctx.beginPath();
        ctx.arc(-2.5, -1.5, 3.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(2.5, 2.0, 2.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
      }
    }
  }

  renderRetreatSignals(ctx, battlefield, nowSec) {
    if (!battlefield?.activeRetreatSignals || battlefield.activeRetreatSignals.length === 0) return;

    const laneY = battlefield.laneY || 180;
    const laneH = battlefield.laneHeight || 320;
    const isLow = this.quality === 'low';

    for (let i = 0; i < battlefield.activeRetreatSignals.length; i++) {
      const sig = battlefield.activeRetreatSignals[i];
      const wx = sig.currentWaveX;
      if (wx < -100 || wx > (this.width || 1600) + 100) continue;

      const alpha = Math.max(0, Math.min(1, (sig.duration - sig.timer) / 0.8));
      if (alpha <= 0) continue;

      const isWest = sig.team === 'western';
      const dir = isWest ? 1 : -1;
      const bowOffset = dir * 28;

      ctx.save();
      ctx.globalAlpha = alpha;

      // Quality Gate (Invariante 4): Gradient & multi-stroke bloom only on high/medium (Zero blur per Paradigm 8)
      if (!isLow) {
        // Soft alien aura / light corridor behind the wavefront
        const auraWidth = 60;
        const auraX = isWest ? wx - auraWidth : wx;
        const grad = ctx.createLinearGradient(wx, 0, wx - dir * auraWidth, 0);
        grad.addColorStop(0, 'rgba(163, 230, 53, 0.35)');
        grad.addColorStop(0.4, 'rgba(168, 85, 247, 0.15)');
        grad.addColorStop(1, 'rgba(168, 85, 247, 0)');

        ctx.fillStyle = grad;
        ctx.fillRect(auraX, laneY - 20, auraWidth, laneH + 40);

        // Soft outer glow stroke
        ctx.strokeStyle = 'rgba(0, 255, 136, 0.3)';
        ctx.lineWidth = 8.0;
        ctx.beginPath();
        ctx.moveTo(wx, laneY);
        ctx.quadraticCurveTo(wx + bowOffset, laneY + laneH * 0.5, wx, laneY + laneH);
        ctx.stroke();

        // Core bright wave pulse
        ctx.strokeStyle = '#a3e635';
        ctx.lineWidth = 3.0;
      } else {
        // Low quality: clean simple stroke, no gradient, single pass
        ctx.strokeStyle = '#a3e635';
        ctx.lineWidth = 2.0;
      }

      // Parabolic curved wave front line
      ctx.beginPath();
      ctx.moveTo(wx, laneY);
      ctx.quadraticCurveTo(wx + bowOffset, laneY + laneH * 0.5, wx, laneY + laneH);
      ctx.stroke();

      // Additional faint echoing harmonic wave (high/medium only)
      if (!isLow) {
        ctx.strokeStyle = 'rgba(168, 85, 247, 0.45)';
        ctx.lineWidth = 1.8;
        ctx.beginPath();
        const echoX = wx - dir * 22;
        ctx.moveTo(echoX, laneY + 15);
        ctx.quadraticCurveTo(echoX + bowOffset * 0.8, laneY + laneH * 0.5, echoX, laneY + laneH - 15);
        ctx.stroke();
      }

      ctx.restore();
    }
  }
}

// On-field unit rendering (js/engine/render/units.js), background terrain
// rendering (js/engine/render/background.js), post-process screen effects
// (js/engine/render/postProcessEffects.js), building-attachment rendering
// (js/engine/render/buildings.js), base module towers
// (js/engine/render/modules.js) and weather (js/engine/render/weather.js)
// all live outside the class body above and get mixed onto the prototype
// here instead - see each file's own header comment for why.
Object.assign(Renderer.prototype, unitMixin, backgroundMixin, sulBackgroundMixin, swampBackgroundMixin, voidglassBackgroundMixin, stratosBackgroundMixin, postProcessMixin, buildingsMixin, weatherMixin, modulesMixin);
